import { useOwnershipLabels } from "../../OwnershipLabels";
import { invalidOwnershipPercentageEdges, OWNERSHIP_PERCENTAGE_WARNING } from "../../../utils/ownershipPercentageValidation";
import KycEntityTypeChoice from "../client/KycEntityTypeChoice";
import { applyClientEntityType, clientEntityTypeOf, entityTypeComplete, typeChangeClearsDetails } from "../../clientEntityType";
import { appConfirm } from "../../../components/ui/appConfirm";
import { nameFields } from "../../../utils/individualName";
import { editLinkedPerson } from "../../../utils/linkedScreeningPerson";
import { companyShareholdersNotice, linkPrefilledSignatories } from "../../../utils/companyReadingPrefill";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination } from "swiper/modules";
import countryList from "react-select-country-list";
import { CircleFlag } from "react-circle-flags";
import { countryMatchesSearch, toIso3 } from "../../../utils/iso2to3";
import {
  normalizeSignatoryCapacity,
  SIGNATORY_CAPACITY_OPTIONS,
} from "../common/signatoryCapacity";
import { OwnershipTreeUaeReadOnly } from "../common/OwnershipTreeUae";
import { buildLiveReviewOwnershipSnapshot } from "../review/KycReviewOwnershipSection";
import { MOBILE_TEXT_ENTRY_PROPS } from "./mobileTextEntryProps";
import MobileIndividualNamePreview from "./MobileIndividualNamePreview";
import "./KycMobileClientSections.css";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import sharesLottieJson from "../../../images/shares-lottie.json";
import signatoryLottieJson from "../../../images/signatory-lottie.json";
import orgtreeLottieJson from "../../../images/orgtreeA.json";
import trustBadgeLottieJson from "../../../images/trustBadge.json";
import iconCertificate from "../../../images/certificate.svg";
import iconAvatar from "../../../images/avatar.svg";
import iconBeneficiary from "../../../images/beneficiary.svg";
import iconHandshake from "../../../images/handshake.svg";
import iconSettlor from "../../../images/settlor.svg";
import iconPassport from "../../../images/passport.svg";
import iconAddress from "../../../images/address.svg";
import iconEarthLocation from "../../../images/earth-location.svg";
import iconPercent from "../../../images/analytics.svg";
import iconBusinessTrade from "../../../images/businessTrade.svg";
import iconBriefcase from "../../../images/briefcase.svg";
import warningIcon from "../../../images/warning-b.svg";
import iconAllocation from "../../../images/allocation.svg";

const COUNTRY_OPTIONS = countryList().getData();
const BASIS_OF_CONTROL_OPTIONS = [
  { value: "Indirect ownership", label: "Indirect ownership" },
  { value: "Managing director", label: "Managing director" },
  { value: "Control via agreement", label: "Control via agreement" },
  { value: "Other", label: "Other" },
];

function makeId() {
  return Math.random().toString(36).slice(2, 10);
}

// Capitalize the first letter of each word (after spaces) as the user types.
function toTitleCase(str) {
  return str.replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

function normalizeHolderType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "entity") return "entity";
  if (raw === "legalarrangement" || raw === "legal_arrangement")
    return "legalArrangement";
  if (raw === "trust") return "trust";
  if (raw === "foundation") return "foundation";
  return "individual";
}

function formatHolderTypeLabel(value) {
  const type = normalizeHolderType(value);
  if (type === "entity") return "Entity";
  if (type === "legalArrangement") return "Legal arrangement";
  if (type === "trust") return "Trust";
  if (type === "foundation") return "Foundation";
  return "Individual";
}

function desktopProfileForPreliminaryMode(mode) {
  if (mode === "pic") return { entityType: "pic", holdingKind: "" };
  if (mode === "holdingActive") return { entityType: "holding", holdingKind: "active" };
  if (mode === "holdingPassive") return { entityType: "holding", holdingKind: "passive" };
  if (mode === "quoted") return { entityType: "quoted", holdingKind: "" };
  if (mode === "trust") return { entityType: "trust", holdingKind: "" };
  if (mode === "foundation") return { entityType: "foundation", holdingKind: "" };
  if (mode === "noneOfAbove") return { entityType: "operating", holdingKind: "" };
  return { entityType: "", holdingKind: "" };
}

function sanitizePercent(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits === "10000") return "10000";
  return digits.slice(0, 4);
}

function normalizePercentForSave(value) {
  const cleaned = sanitizePercent(value);
  if (!cleaned) return "";
  if (cleaned === "10000") return "100.00";
  const padded = cleaned.padEnd(4, "0");
  const whole = padded.slice(0, 2);
  const decimals = padded.slice(2, 4);
  return `${whole}.${decimals}`;
}

function normalizePercentForReviewSnapshot(value) {
  if (value === "" || value == null) return value;
  const normalized = normalizePercentForSave(value);
  if (!normalized) return value;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return value;
  return Number.isInteger(n) ? String(n) : normalized;
}

function normalizeOwnershipPercentsForReviewSnapshot(ownership = {}) {
  const normalizeRow = (row) => {
    if (!row || typeof row !== "object") return row;
    return {
      ...row,
      ownershipPercent: normalizePercentForReviewSnapshot(row.ownershipPercent),
      effectiveOwnershipPercent: normalizePercentForReviewSnapshot(
        row.effectiveOwnershipPercent
      ),
    };
  };

  return {
    ...ownership,
    shareholders: Array.isArray(ownership?.shareholders)
      ? ownership.shareholders.map(normalizeRow)
      : ownership?.shareholders,
    directShareholders: Array.isArray(ownership?.directShareholders)
      ? ownership.directShareholders.map(normalizeRow)
      : ownership?.directShareholders,
    ubos: Array.isArray(ownership?.ubos)
      ? ownership.ubos.map(normalizeRow)
      : ownership?.ubos,
    controllingPersonsNatural: Array.isArray(ownership?.controllingPersonsNatural)
      ? ownership.controllingPersonsNatural.map(normalizeRow)
      : ownership?.controllingPersonsNatural,
  };
}

function toPercentNumber(value) {
  const normalized = normalizePercentForSave(value);
  if (!normalized) return 0;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function toSnapshotPercentNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value ?? "").trim().replace(/%/g, "");
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : toPercentNumber(value);
}

function ownershipPercentWarning(snapshot) {
  const nodes = Array.isArray(snapshot?.nodes) ? snapshot.nodes : [];
  if (!nodes.length) return "";

  const hasMissingEntityName = (node) => {
    const name = String(node?.name || "").trim();
    return !name || name === "…" || name.toLowerCase() === "(unnamed)";
  };

  const childCountByParent = new Map();
  nodes.forEach((node) => {
    const parents = Array.isArray(node?.parentIDs) ? node.parentIDs : [];
    const parentId = String(parents[0] || "").trim();
    if (!parentId) return;

    childCountByParent.set(parentId, (childCountByParent.get(parentId) || 0) + 1);

  });

  const parentNeedsChildren = (node) => {
    const nodeId = String(node?.ID || node?.id || "").trim();
    if (!nodeId) return false;
    const type = String(
      nodeId === "X0" ? snapshot?.initialAHType || node?.type || "" : node?.type || ""
    ).trim();
    const category = String(node?.entityCategory || "").trim();
    if (!type || type === "Individual" || type === "Trust" || type === "Foundation") return false;
    if (category === "trust" || category === "foundation") return false;
    return (
      type === "PIC" ||
      type === "HoldingActive" ||
      type === "HoldingPassive" ||
      type === "Non Quoted Operational Company" ||
      type === "Quoted Company" ||
      category === "pic" ||
      category === "holding" ||
      category === "nonQuotedOperational" ||
      category === "quoted"
    );
  };

  const entityWithMissingName = nodes.find((node) => {
    if (!parentNeedsChildren(node)) return false;
    return hasMissingEntityName(node);
  });
  if (entityWithMissingName) {
    return "The name of an entity is missing";
  }

  const parentsWithoutChildren = [];
  for (const node of nodes) {
    const nodeId = String(node?.ID || node?.id || "").trim();
    if (parentNeedsChildren(node) && !childCountByParent.get(nodeId)) {
      parentsWithoutChildren.push(node);
    }
  }
  if (parentsWithoutChildren.length === 1) {
    const parentName = String(parentsWithoutChildren[0]?.name || "").trim();
    return `No shareholder are indicated for ${parentName || "an entity"}`;
  }
  if (parentsWithoutChildren.length > 1) {
    return "No shareholder are indicated for some entities";
  }

  if (invalidOwnershipPercentageEdges(snapshot, toSnapshotPercentNumber).length) {
    return OWNERSHIP_PERCENTAGE_WARNING;
  }

  return "";
}

function formatQuickPercentLabel(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${n.toFixed(2).replace(/\.00$/, "")}%`;
}

function formatPercentDisplay(value) {
  const normalized = normalizePercentForSave(value);
  if (!normalized) return "0 %";
  const n = Number(normalized);
  if (!Number.isFinite(n)) return `${normalized} %`;
  const label = Number.isInteger(n) ? String(n) : normalized;
  return `${label} %`;
}

function formatOptionalPercentDisplay(value) {
  const normalized = normalizePercentForSave(value);
  if (!normalized) return "";
  return formatPercentDisplay(value);
}

function capitalizeFirst(str) {
  const s = String(str || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function formatAssistantEntityCategory(node) {
  const category = String(node?.entityCategory || "").trim();
  if (category === "holding") {
    const holdingKind = String(node?.holdingKind || "").trim();
    if (holdingKind === "active") return "Active holding";
    if (holdingKind === "passive") return "Passive holding";
    return "Holding";
  }
  if (category === "pic") return "PIC";
  if (category === "nonQuotedOperational") return "OpCo";
  return (
    MOBILE_ASSISTANT_ENTITY_CATEGORY_OPTIONS.find((opt) => opt.value === category)
      ?.label || category
  );
}

function formatAssistantNodeSummary(node, showPercent = true) {
  const type = normalizeHolderType(node?.type);
  const name = String(node?.name || "").trim();
  if (!name) return "";

  const typeLabel =
    type === "entity" ? formatAssistantEntityCategory(node) : formatHolderTypeLabel(type);
  // No % below a PIC or passive holding, nor for a PIC/passive-holding client's shareholders.
  const percentLabel = showPercent && String(node?.ownershipPercent || "").trim()
    ? ` - ${formatPercentDisplay(node?.ownershipPercent)}`
    : "";

  return `${capitalizeFirst(name)} (${typeLabel})${percentLabel}`;
}


function getPercentDisplayMeta(rawValue, cursorIndex = null, keepWholeGroupActive = false) {
  if (sanitizePercent(rawValue) === "10000") {
    return {
      fullText: "100%",
      activeIndex: -1,
      activeIndexes: [],
      activeDigitIndex: -1,
      numericLength: 3,
      hideLeadingWholeZero: false,
    };
  }
  const normalized = normalizePercentForSave(rawValue) || "00.00";
  const cleaned = sanitizePercent(rawValue);
  const displayIndexByDigitIndex = [0, 1, 3, 4];
  const isInitialWholeGroup = (!cleaned && cursorIndex === 0) || keepWholeGroupActive;
  const hideLeadingWholeZero =
    cleaned.length >= 2 &&
    cleaned[0] === "0" &&
    cleaned[1] !== "0" &&
    cursorIndex != null &&
    cursorIndex >= 2;
  const activeDigitIndex =
    isInitialWholeGroup
      ? 0
      : cursorIndex != null
      ? cursorIndex >= 4
        ? -1
        : cursorIndex
      : cleaned.length >= 4
      ? -1
      : cleaned.length;
  const activeIndex =
    activeDigitIndex === -1
      ? -1
      : displayIndexByDigitIndex[activeDigitIndex] ?? 0;

  return {
    fullText: `${normalized}%`,
    activeIndex,
    activeIndexes: isInitialWholeGroup ? [0, 1] : activeIndex === -1 ? [] : [activeIndex],
    activeDigitIndex,
    numericLength: normalized.length,
    hideLeadingWholeZero,
  };
}

function getCountDisplayMeta(rawValue, cursorIndex = null) {
  const cleaned = String(rawValue || "").replace(/\D/g, "").slice(0, 2);
  const activeIndex =
    cursorIndex == null ? (cleaned.length ? cleaned.length - 1 : -1) : cursorIndex;
  return {
    fullText: cleaned || "0",
    activeIndex,
    numericLength: (cleaned || "0").length,
  };
}

function emptyShareholder() {
  return {
    _rowId: makeId(),
    name: "",
    holderType: "individual",
    ownershipPercent: "",
    nationality: "",
    countryOfResidence: "",
  };
}

function makeTrustParty(role) {
  return {
    _rowId: makeId(),
    role,
    name: "",
    trusteeKind: "",
    nationality: "",
    domicile: "",
  };
}

function makeDefaultTrustParties() {
  return [
    makeTrustParty("Settlor"),
    makeTrustParty("Trustee"),
    makeTrustParty("Beneficiary"),
  ];
}

function makeFoundationParty(role) {
  return {
    _rowId: makeId(),
    role,
    name: "",
    councilKind: "",
    nationality: "",
    domicile: "",
  };
}

function makeDefaultFoundationParties() {
  return [
    makeFoundationParty("Founder"),
    makeFoundationParty("Council member"),
    makeFoundationParty("Beneficiary"),
  ];
}

function ensureLegalArrangementsShape(ownership) {
  const o = ownership && typeof ownership === "object" ? ownership : {};
  const la =
    o.legalArrangements && typeof o.legalArrangements === "object"
      ? o.legalArrangements
      : {};
  const arrangements = Array.isArray(la.arrangements) ? la.arrangements : [];
  return { arrangements };
}

function normalizeArrangementType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "foundation") return "Foundation";
  return "Trust";
}

function makeAccountHolderArrangement(type = "Trust") {
  const arrangementType = normalizeArrangementType(type);
  return {
    _rowId: makeId(),
    arrangementType,
    source: "account_holder",
    sourceRefRowId: "",
    relatedShareholderName: "",
    autoCreated: true,
    name: "",
    parties:
      arrangementType === "Foundation"
        ? makeDefaultFoundationParties()
        : makeDefaultTrustParties(),
    };
}

function makeLinkedArrangement({
  type = "Trust",
  source = "manual",
  sourceRefRowId = "",
  relatedShareholderName = "",
  autoCreated = false,
  name = "",
  parties = null,
} = {}) {
  const arrangementType = normalizeArrangementType(type);
  return {
    _rowId: makeId(),
    arrangementType,
    source: source || "manual",
    sourceRefRowId: sourceRefRowId || "",
    relatedShareholderName: relatedShareholderName || "",
    autoCreated: !!autoCreated,
    name: name || "",
    parties:
      Array.isArray(parties) && parties.length
        ? parties
        : arrangementType === "Foundation"
        ? makeDefaultFoundationParties()
        : makeDefaultTrustParties(),
  };
}

function applyAccountHolderTrustResolutionToMobileItem(item, resolutionKind, sourceName) {
  const next = item && typeof item === "object" ? { ...(item || {}) } : {};
  const resolvedName = String(sourceName || "").trim();
  const nextKind = resolutionKind === "trustee" ? "trustee" : "trust";
  const trustParties = Array.isArray(next?.trustParties)
    ? next.trustParties.map((party) => ({ ...(party || {}) }))
    : makeDefaultTrustParties();

  const trusteeIdx = trustParties.findIndex(
    (party) => String(party?.role || "").trim() === "Trustee"
  );

  if (nextKind === "trust") {
    next.name = resolvedName;
    next._autoAccountHolderTrustName = resolvedName;

    if (trusteeIdx !== -1) {
      const trustee = { ...(trustParties[trusteeIdx] || {}) };
      trustee.name = "";
      trustee._autoAccountHolderTrusteeName = "";
      trustParties[trusteeIdx] = trustee;
    }
  } else {
    next.name = "";
    next._autoAccountHolderTrustName = "";

    if (trusteeIdx !== -1) {
      const trustee = { ...(trustParties[trusteeIdx] || {}) };
      trustee.name = resolvedName;
      trustee._autoAccountHolderTrusteeName = resolvedName;
      trustParties[trusteeIdx] = trustee;
    }
  }

  next.trustParties = trustParties;
  next._accountHolderTrustResolutionKind = nextKind;
  next._accountHolderTrustResolutionSourceName = resolvedName;
  return next;
}

function arrangementToMobileHolder(arrangement, fallbackItem = null) {
  const safe = arrangement && typeof arrangement === "object" ? arrangement : {};
  const arrangementType = normalizeArrangementType(safe?.arrangementType);
  const fallback =
    fallbackItem && typeof fallbackItem === "object" ? fallbackItem : {};
  const fallbackHolderType = normalizeHolderType(fallback?.holderType);
  const resolvedHolderType =
    arrangementType === "Foundation"
      ? "foundation"
      : arrangementType === "Trust"
      ? "trust"
      : fallbackHolderType === "foundation"
      ? "foundation"
      : "trust";
  return {
    _rowId: String(fallback?._rowId || safe?._rowId || makeId()),
    holderType: resolvedHolderType,
    ...nameFields(safe),
    name: String(safe?.name || fallback?.name || ""),
    ownershipPercent: String(fallback?.ownershipPercent || ""),
    nationality: String(fallback?.nationality || ""),
    countryOfResidence: String(fallback?.countryOfResidence || ""),
    trustParties:
      resolvedHolderType === "trust"
        ? Array.isArray(safe?.parties)
          ? safe.parties.map((party) => ({ ...(party || {}) }))
          : Array.isArray(fallback?.trustParties)
          ? fallback.trustParties.map((party) => ({ ...(party || {}) }))
          : makeDefaultTrustParties()
        : undefined,
    foundationParties:
      resolvedHolderType === "foundation"
        ? Array.isArray(safe?.parties)
          ? safe.parties.map((party) => ({ ...(party || {}) }))
          : Array.isArray(fallback?.foundationParties)
          ? fallback.foundationParties.map((party) => ({ ...(party || {}) }))
          : makeDefaultFoundationParties()
        : undefined,
  };
}

function mobileHolderToArrangement(item, existingArrangement) {
  const existing =
    existingArrangement && typeof existingArrangement === "object"
      ? existingArrangement
      : {};
  const holderType = normalizeHolderType(item?.holderType);
  const arrangementType =
    holderType === "foundation" ? "Foundation" : "Trust";
  return {
    ...existing,
    _rowId: String(existing?._rowId || item?._rowId || makeId()),
    arrangementType,
    source: "account_holder",
    sourceRefRowId: "",
    relatedShareholderName: String(existing?.relatedShareholderName || ""),
    autoCreated: true,
    ...nameFields(item),
    name: String(item?.name || ""),
    parties:
      arrangementType === "Foundation"
        ? Array.isArray(item?.foundationParties)
          ? item.foundationParties.map((party) => ({ ...(party || {}) }))
          : makeDefaultFoundationParties()
        : Array.isArray(item?.trustParties)
        ? item.trustParties.map((party) => ({ ...(party || {}) }))
        : makeDefaultTrustParties(),
  };
}

function emptyUbo() {
  return {
    _rowId: makeId(),
    holderType: "individual",
    relatedShareholderRowId: "",
    name: "",
    nationality: "",
    countryOfResidence: "",
    basisOfControl: "Indirect ownership",
    trustParties: makeDefaultTrustParties(),
    foundationParties: makeDefaultFoundationParties(),
  };
}

function emptySignatory() {
  return {
    _rowId: makeId(),
    name: "",
    capacity: "",
    nationality: "",
    countryOfResidence: "",
  };
}

function getTrustPartiesSorted(item) {
  return (Array.isArray(item?.trustParties)
    ? item.trustParties
    : makeDefaultTrustParties()
  )
    .slice()
    .sort((a, b) => {
      const order = {
        Settlor: 0,
        Trustee: 1,
        Protector: 2,
        Beneficiary: 3,
      };
      return (
        (order[String(a?.role || "")] ?? 99) -
        (order[String(b?.role || "")] ?? 99)
      );
    });
}

function getFoundationPartiesSorted(item) {
  return (Array.isArray(item?.foundationParties)
    ? item.foundationParties
    : makeDefaultFoundationParties()
  )
    .slice()
    .sort((a, b) => {
      const order = {
        Founder: 0,
        "Council member": 1,
        Guardian: 2,
        Beneficiary: 3,
      };
      return (
        (order[String(a?.role || "")] ?? 99) -
        (order[String(b?.role || "")] ?? 99)
      );
    });
}

const MOBILE_ASSISTANT_PRELIM_OPTIONS = [
  {
    value: "noneOfAbove",
    label: "Operating company (non-quoted)",
    hint: "A standard non-quoted company carrying out commercial business activities.",
  },
  {
    value: "holding",
    label: "Holding company",
    hint: "A company primarily used to hold interests in other companies.",
  },
  {
    value: "pic",
    label: "Domiciliary company (PIC)",
    hint: "A personal investment company used to hold assets or investments.",
  },
  {
    value: "trust",
    label: "Trust",
    hint: "A legal arrangement where trustees hold or manage assets for beneficiaries.",
  },
  {
    value: "foundation",
    label: "Foundation",
    hint: "A legal arrangement with separate legal personality for beneficiaries or a purpose.",
  },
  {
    value: "quoted",
    label: "Quoted company",
    hint: "A company listed on a regulated stock exchange.",
  },
];

const MOBILE_ASSISTANT_ENTITY_CATEGORY_OPTIONS = [
  {
    value: "nonQuotedOperational",
    label: "Operating company (non-quoted)",
    hint: "A standard non-quoted company carrying out commercial business activities.",
  },
  {
    value: "holding",
    label: "Holding company",
    hint: "A company primarily used to hold interests in other companies.",
  },
  {
    value: "pic",
    label: "Domiciliary Company (PIC)",
    hint: "A personal investment company used to hold assets or investments.",
  },
  {
    value: "quoted",
    label: "Quoted company",
    hint: "A company listed on a regulated stock exchange.",
  },
];

const MOBILE_ASSISTANT_HOLDING_OPTIONS = [
  {
    value: "active",
    label: "Active holding",
    hint:
      "Holds directly or indirectly a majority stake in one or more operating companies. Main purpose is NOT the administration of third-party assets.",
  },
  {
    value: "passive",
    label: "Passive holding",
    hint:
      "Mainly combines and manages assets of a family or a defined group. Often used to enable dividend distributions.",
  },
];

function makeAssistantOwnerNode() {
  return {
    id: makeId(),
    type: "",
    entityCategory: "",
    holdingKind: "",
    name: "",
    nationality: "",
    countryOfResidence: "",
    ownershipPercent: "",
    ownersCount: "",
    owners: [],
    trustParties: null,
    foundationParties: null,
  };
}

function cloneAssistantNodes(list) {
  return (Array.isArray(list) ? list : []).map((node) => ({
    ...(node || {}),
    owners: cloneAssistantNodes(node?.owners),
  }));
}

function cloneModalDraft(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

function getAssistantNodeAtPath(nodes, path) {
  let currentList = Array.isArray(nodes) ? nodes : [];
  let currentNode = null;
  for (let i = 0; i < path.length; i += 1) {
    currentNode = currentList?.[path[i]] || null;
    if (!currentNode) return null;
    currentList = Array.isArray(currentNode.owners) ? currentNode.owners : [];
  }
  return currentNode;
}

function updateAssistantNodeAtPath(nodes, path, updater) {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node, index) => {
    if (index !== path[0]) {
      return {
        ...(node || {}),
        owners: cloneAssistantNodes(node?.owners),
      };
    }
    if (path.length === 1) {
      return updater({
        ...(node || {}),
        owners: cloneAssistantNodes(node?.owners),
      });
    }
    return {
      ...(node || {}),
      owners: updateAssistantNodeAtPath(node?.owners || [], path.slice(1), updater),
    };
  });
}

function updateAssistantNodeById(nodes, nodeId, updater) {
  return (Array.isArray(nodes) ? nodes : []).map((node) => {
    const currentNode = {
      ...(node || {}),
      owners: cloneAssistantNodes(node?.owners),
    };
    if (String(currentNode?.id || "") === String(nodeId || "")) {
      const nextNode = updater(currentNode);
      return {
        ...(nextNode || currentNode),
        owners: cloneAssistantNodes((nextNode || currentNode)?.owners),
      };
    }
    return {
      ...currentNode,
      owners: updateAssistantNodeById(currentNode.owners, nodeId, updater),
    };
  });
}

function assistantPercentShouldApply(preliminaryMode) {
  return preliminaryMode !== "pic" && preliminaryMode !== "holdingPassive" && preliminaryMode !== "quoted";
}

// % is NOT required for shareholders of a PIC or passive holding entity
function entityNodeSkipsPercent(entityCategory, holdingKind) {
  if (entityCategory === "pic") return true;
  if (entityCategory === "holding" && holdingKind === "passive") return true;
  return false;
}

// Whether the assistant node `nodeId` holds a meaningful % in its direct parent: only below an
// operating company or active holding, or directly in a client that carries a %.
function assistantNodeParentUsesPercent(roots, nodeId, preliminaryMode) {
  const id = String(nodeId || "");
  function find(list, parentUsesPercent) {
    for (const node of Array.isArray(list) ? list : []) {
      if (String(node?.id || "") === id) return parentUsesPercent;
      const found = find(node?.owners, !entityNodeSkipsPercent(String(node?.entityCategory || ""), String(node?.holdingKind || "")));
      if (found !== null) return found;
    }
    return null;
  }
  return id ? find(roots, assistantPercentShouldApply(preliminaryMode)) === true : false;
}

// Entity categories that are treated as terminal — no shareholders needed. Only a quoted company (as on
// desktop): the owners of an operating company are asked, down to individuals, trusts, foundations or quoted
// companies.
function isTerminalEntityCategory(category) {
  return category === "quoted";
}

function findNextAssistantNodePath(nodes, preliminaryMode) {
  // shouldAskPercent is context-aware: depends on parent entity category, not just account holder mode
  function dfs(list, prefix, parentAskPercent) {
    for (let i = 0; i < list.length; i += 1) {
      const node = list[i] || {};
      const path = [...prefix, i];
      const type = normalizeHolderType(node?.type);

      if (!String(node?.type || "").trim()) return path;
      if (type === "legalArrangement") return path;
      if (type === "entity" && !String(node?.entityCategory || "").trim()) {
        return path;
      }
      if (
        type === "entity" &&
        String(node?.entityCategory || "") === "holding" &&
        !String(node?.holdingKind || "").trim()
      ) {
        return path;
      }
      if (!String(node?.name || "").trim()) return path;
      if (
        type === "individual" &&
        (!String(node?.nationality || "").trim() ||
          !String(node?.countryOfResidence || "").trim())
      ) {
        return path;
      }
      if (
        type === "entity" &&
        !String(node?.countryOfResidence || "").trim()
      ) {
        return path;
      }
      if (parentAskPercent && !String(node?.ownershipPercent || "").trim()) {
        return path;
      }
      if (type === "entity") {
        // Quoted companies are final — no shareholders required, don't descend
        if (!isTerminalEntityCategory(String(node?.entityCategory || ""))) {
          const count = Number.parseInt(String(node?.ownersCount || ""), 10);
          if (!Number.isFinite(count) || count < 1) return path;
          if ((Array.isArray(node?.owners) ? node.owners.length : 0) < count) {
            return path;
          }
          // Children of a PIC or passive holding entity don't need %
          const childAskPercent = !entityNodeSkipsPercent(
            String(node?.entityCategory || ""),
            String(node?.holdingKind || "")
          );
          const childPath = dfs(node?.owners || [], path, childAskPercent);
          if (childPath) return childPath;
        }
      } else {
        const childPath = dfs(node?.owners || [], path, parentAskPercent);
        if (childPath) return childPath;
      }
    }
    return null;
  }

  return dfs(Array.isArray(nodes) ? nodes : [], [], assistantPercentShouldApply(preliminaryMode));
}

function computeAssistantLeafRows(nodes, preliminaryMode, rootNodeId = "", parentFactor = 100, parentShouldUsePct = null, depth = 0) {
  const out = [];
  // parentShouldUsePct: null = derive from global mode; true/false = explicit per-level override
  const shouldUsePct = parentShouldUsePct !== null
    ? parentShouldUsePct
    : assistantPercentShouldApply(preliminaryMode);
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const type = normalizeHolderType(node?.type);
    const rawPct = shouldUsePct ? toPercentNumber(node?.ownershipPercent) : 100;
    const factor = shouldUsePct ? (parentFactor * rawPct) / 100 : parentFactor;
    const owners = Array.isArray(node?.owners) ? node.owners : [];
    const currentRootNodeId = rootNodeId || String(node?.id || "");
    if (type === "entity" && owners.length && !isTerminalEntityCategory(String(node?.entityCategory || ""))) {
      // Children of PIC or passive holding don't use %
      const childShouldUsePct = !entityNodeSkipsPercent(
        String(node?.entityCategory || ""),
        String(node?.holdingKind || "")
      );
      out.push(
        ...computeAssistantLeafRows(
          owners,
          preliminaryMode,
          currentRootNodeId,
          factor,
          childShouldUsePct,
          depth + 1
        )
      );
      return;
    }
    // The row keeps only the % held in the direct parent, and none where that parent (or the
    // client) does not carry a % — never a cumulated % or a default 100%.
    out.push({
      node,
      rootNodeId: currentRootNodeId,
      depth,
      effectiveOwnershipPercent: shouldUsePct
        ? normalizePercentForSave(String(Math.round(rawPct * 100)).padStart(4, "0"))
        : "",
    });
  });
  return out;
}

function escapeMermaidLabel(value) {
  return String(value || "")
    .replace(/"/g, "&quot;")
    .replace(/\n/g, "<br/>");
}

function escapeAssistantTreeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function assistantTreeIconClassForType(type) {
  if (type === "Individual") return "fa fa-user";
  if (type === "Trust" || type === "Foundation") return "fa fa-bookmark";
  if (type === "Holding (Active)" || type === "Holding (Passive)")
    return "fa fa-building";
  return "fa fa-building";
}

function assistantTreeIconTokenForType(type) {
  if (type === "Individual") return "fa:fa-user";
  if (type === "Trust" || type === "Foundation") return "fa:fa-bookmark";
  if (type === "Holding (Active)" || type === "Holding (Passive)")
    return "fa:fa-building";
  return "fa:fa-building";
}

function isSafariBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = String(navigator.userAgent || "");
  const vendor = String(navigator.vendor || "");
  return (
    /Safari/i.test(ua) &&
    /Apple/i.test(vendor) &&
    !/Chrome|Chromium|CriOS|FxiOS|Firefox|Edg|EdgiOS|OPR|OPT|SamsungBrowser|Android/i.test(
      ua
    )
  );
}

function toAssistantTreeHtmlLabel(html) {
  return String(html || "")
    .replace(/\r?\n/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, "'");
}

function toDisplayTitleCase(value) {
  return String(value || "").replace(/\p{L}[\p{L}'-]*/gu, (word) => {
    const [first = "", ...rest] = Array.from(word);
    return `${first.toLocaleUpperCase()}${rest.join("")}`;
  });
}

function buildAssistantTreeNodeHtml(
  name,
  typeText,
  isRoot = false,
  labelMode = "default"
) {
  const safeType = escapeAssistantTreeHtml(typeText || "Entity");
  const safeName = escapeAssistantTreeHtml(
    toDisplayTitleCase(name || (isRoot ? "" : "(unnamed)"))
  );
  const iconCls = assistantTreeIconClassForType(typeText);

  if (labelMode === "safariHtml") {
    return `
      <span class='otSafariNodeLabel' style='display:inline-block;text-align:center;white-space:nowrap;line-height:1;padding-bottom:0;vertical-align:top;'>
        <span class='otSafariNodeMain' style='display:flex;align-items:center;justify-content:center;gap:5px;font-size:14px;font-weight:700;color:#3f4d5a;line-height:.9;'>
          <i class='${iconCls}' style='font-size:13px;line-height:1;'></i>
          <span>${safeType}</span>
        </span>
        <span class='otSafariNodeSub' style='display:block;margin-top:10px;font-size:12px;font-weight:400;color:#6c7278;line-height:.7;'>${safeName}</span>
      </span>
    `;
  }

  return `
    <div style='padding:0px 10px; line-height:1.05; text-align:center;'>
      <div style='display:flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;'>
        <i class='${iconCls}' style='font-size:15px;line-height:1;'></i>
        <span style='font-weight:600;font-size:15px;line-height:1;'>${safeType}</span>
      </div>
      <div style='margin-top:8px;font-size:12px;opacity:.85;white-space:nowrap;line-height:1;'>${safeName}</div>
    </div>
  `;
}

function buildMobileAssistantSnapshot(
  entityName,
  roots,
  preliminaryMode,
  options = {}
) {
  const labelMode =
    options?.labelMode === "safariHtml" ? "safariHtml" : "default";
  const flowLines = [];
  const accountHolderType =
    preliminaryMode === "pic"
      ? "Domiciliary Company (PIC)"
      : preliminaryMode === "holdingActive"
      ? "Active Holding"
      : preliminaryMode === "holdingPassive"
      ? "Passive Holding"
      : preliminaryMode === "noneOfAbove"
      ? "Operating Company"
      : preliminaryMode === "quoted"
      ? "Quoted Company"
      : "Entity";
  const accountHolderHtml = buildAssistantTreeNodeHtml(
    entityName || "Account holder",
    accountHolderType,
    true,
    labelMode
  );
  const graphLines = [
    "graph TD",
    `X0("${toAssistantTreeHtmlLabel(accountHolderHtml)}")`,
  ];

  function walk(nodes, parentId = "X0", parentShowPercent = assistantPercentShouldApply(preliminaryMode)) {
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      const nodeId = `A_${String(node?.id || makeId())}`;
      const nodeType = normalizeHolderType(node?.type);
      const typeLabel = nodeType === "entity"
        ? formatAssistantEntityCategory(node)
        : formatHolderTypeLabel(node?.type);
      const nodeHtml = buildAssistantTreeNodeHtml(
        String(node?.name || "").trim(),
        typeLabel,
        false,
        labelMode
      );
      graphLines.push(`${nodeId}("${toAssistantTreeHtmlLabel(nodeHtml)}")`);
      if (
        parentShowPercent &&
        String(node?.ownershipPercent || "").trim()
      ) {
        graphLines.push(
          `${parentId} -->|"${escapeMermaidLabel(
            formatPercentDisplay(node?.ownershipPercent)
          )}"| ${nodeId}`
        );
        flowLines.push(
          `${parentId} -->|"${formatPercentDisplay(node?.ownershipPercent)}"| ${nodeId}`
        );
      } else {
        graphLines.push(`${parentId} --> ${nodeId}`);
        flowLines.push(`${parentId} --> ${nodeId}`);
      }
      // Children of this node show % on arrows only if this node is operational/active holding
      const childShowPercent = !entityNodeSkipsPercent(
        String(node?.entityCategory || ""),
        String(node?.holdingKind || "")
      );
      walk(node?.owners || [], nodeId, childShowPercent);
    });
  }

  walk(roots);
  graphLines.push(
    "classDef default fill:#BAD9CB,stroke:#BAD9CB,stroke-width:0px,color:#484848"
  );
  graphLines.push("linkStyle default stroke-width:2px,fill:none,stroke:#B0B0B0");

  return {
    mermaid: graphLines.join("\n"),
    initialAHType: accountHolderType,
    labelMode,
    nodes: [],
    flowLines,
  };
}

export default function KycMobileOwnership({
  form,
  separateNames = false,
  onSetAt,
  readOnly = false,
  continueAttempted = false,
  view = "all",
  // kyc-sent-mobile: the client entity type was answered in the company upload-first step; it is shown
  // here as one line (changeable) instead of the preliminary question.
  entityTypeAnsweredEarlier = false,
}) {
  const { ownershipLabel } = useOwnershipLabels();
  const ownership = form?.ownership || {};
  const customer = form?.customer || {};
  const shareholders = Array.isArray(ownership.shareholders)
    ? ownership.shareholders
    : [];
  const ubos = Array.isArray(ownership.ubos) ? ownership.ubos : [];
  const signatories = Array.isArray(ownership.signatories)
    ? ownership.signatories
    : [];
  const legalArrangements = ensureLegalArrangementsShape(ownership).arrangements;
  const uboFlags = ownership?.uboFlags || {};
  const isPicOrPassiveHolding = !!uboFlags?.pctNotMeaningful;
  const isQuotedCompany = !!uboFlags?.quotedCompany;
  const isTrustAccountHolder = !!uboFlags?.accountHolderTrust;
  const isFoundationAccountHolder = !!uboFlags?.accountHolderFoundation;
  const hasExplicitPreliminaryFlag =
    isPicOrPassiveHolding ||
    isQuotedCompany ||
    isTrustAccountHolder ||
    isFoundationAccountHolder;
  const isNoneOfAbove =
    !!ownership?.preliminaryNoneOfAbove || !hasExplicitPreliminaryFlag;
  const mobileEntryMode = String(ownership?.mobileEntryMode || "").trim();
  const mobileAssistantDraft =
    ownership?.mobileAssistantDraft &&
    typeof ownership.mobileAssistantDraft === "object"
      ? ownership.mobileAssistantDraft
      : {};
  const [assistantLockedPath, setAssistantLockedPath] = useState(null);
  const [assistantPrelimPending, setAssistantPrelimPending] = useState("noneOfAbove");
  const assistantPreliminaryMode = String(
    mobileAssistantDraft?.preliminaryMode || ""
  ).trim();
  const assistantRoots = cloneAssistantNodes(mobileAssistantDraft?.roots || []);
  const assistantDirectCount = String(mobileAssistantDraft?.directCount || "").trim();
  const assistantDirectCountNumber = Math.max(
    0,
    Number.parseInt(assistantDirectCount, 10) || 0
  );
  const nextAssistantPath = findNextAssistantNodePath(
    assistantRoots,
    assistantPreliminaryMode
  );
  const assistantCurrentPath =
    Array.isArray(assistantLockedPath) &&
    getAssistantNodeAtPath(assistantRoots, assistantLockedPath)
      ? assistantLockedPath
      : nextAssistantPath;
  const assistantCurrentNode = assistantCurrentPath
    ? getAssistantNodeAtPath(assistantRoots, assistantCurrentPath)
    : null;
  const assistantCurrentNodeType = normalizeHolderType(assistantCurrentNode?.type);
  const assistantCurrentDirectShareholderIndex =
    Array.isArray(assistantCurrentPath) && assistantCurrentPath.length === 1
      ? assistantCurrentPath[0]
      : -1;
  const assistantOtherDirectShareholdersTotal = assistantRoots.reduce(
    (sum, node, idx) =>
      idx === assistantCurrentDirectShareholderIndex
        ? sum
        : sum + toPercentNumber(node?.ownershipPercent),
    0
  );
  const assistantRemainingDirectOwnership = Math.max(
    0,
    Math.round((100 - assistantOtherDirectShareholdersTotal) * 100) / 100
  );
  const assistantCurrentNodeIndexInParent =
    Array.isArray(assistantCurrentPath) && assistantCurrentPath.length > 1
      ? assistantCurrentPath[assistantCurrentPath.length - 1]
      : -1;
  const assistantCurrentParentNode =
    Array.isArray(assistantCurrentPath) && assistantCurrentPath.length > 1
      ? getAssistantNodeAtPath(assistantRoots, assistantCurrentPath.slice(0, -1))
      : null;
  const assistantCurrentParentOwnersCount = assistantCurrentParentNode
    ? Math.max(0, Number.parseInt(String(assistantCurrentParentNode?.ownersCount || ""), 10) || 0)
    : 0;
  const assistantCurrentParentIsPic =
    assistantCurrentParentNode
      ? String(assistantCurrentParentNode?.entityCategory || "") === "pic" ||
        (String(assistantCurrentParentNode?.entityCategory || "") === "holding" &&
          String(assistantCurrentParentNode?.holdingKind || "") === "passive")
      : false;
  const assistantOtherIndirectShareholdersTotal =
    assistantCurrentParentNode && Array.isArray(assistantCurrentParentNode.owners)
      ? assistantCurrentParentNode.owners.reduce(
          (sum, node, idx) =>
            idx === assistantCurrentNodeIndexInParent
              ? sum
              : sum + toPercentNumber(node?.ownershipPercent),
          0
        )
      : 0;
  const assistantRemainingIndirectOwnership = Math.max(
    0,
    Math.round((100 - assistantOtherIndirectShareholdersTotal) * 100) / 100
  );
  const assistantAccountHolderArrangementType =
    mobileEntryMode === "assistant" &&
    (assistantPreliminaryMode === "trust" ||
      assistantPreliminaryMode === "foundation")
      ? assistantPreliminaryMode === "foundation"
        ? "Foundation"
        : "Trust"
      : "";
  const activeAccountHolderArrangementType =
    assistantAccountHolderArrangementType ||
    (isFoundationAccountHolder
      ? "Foundation"
      : isTrustAccountHolder
      ? "Trust"
      : "");
  const assistantIsComplete =
    !!assistantPreliminaryMode &&
    assistantPreliminaryMode !== "holding" &&
    (assistantPreliminaryMode === "quoted" ||
      assistantPreliminaryMode === "trust" ||
      assistantPreliminaryMode === "foundation" ||
      (assistantDirectCountNumber > 0 &&
        assistantRoots.length === assistantDirectCountNumber &&
        !assistantCurrentPath));
  const assistantResultGenerated = !!ownership?.assistantGenerated;
  const assistantTreeLabelMode = isSafariBrowser() ? "safariHtml" : "default";
  const isAssistantGeneratedReview = assistantResultGenerated;
  // Result page: a shareholder whose % is still missing (e.g. not printed on the company documents) is highlighted.
  const percentMissingOnResult = (item) =>
    isAssistantGeneratedReview && !isPicOrPassiveHolding && !normalizePercentForSave(item?.ownershipPercent);
  const hasAssistantSnapshot = !!ownership?.assistantTree?.mermaid;
  const desktopCompletionMethod = String(
    ownership?.desktopClientCompletionMethod || ""
  ).trim();
  const desktopEntityType = String(
    ownership?.desktopClientEntityType || ""
  ).trim();
  const desktopHoldingKind = String(
    ownership?.desktopClientHoldingKind || ""
  ).trim();
  const hasCanonicalOwnershipData =
    shareholders.length > 0 ||
    ubos.length > 0 ||
    legalArrangements.length > 0 ||
    hasAssistantSnapshot ||
    assistantResultGenerated;
  const shouldShowAssistantResult =
    mobileEntryMode === "assistant" &&
    (hasAssistantSnapshot || assistantResultGenerated) &&
    assistantPreliminaryMode !== "quoted" &&
    assistantPreliminaryMode !== "trust" &&
    assistantPreliminaryMode !== "foundation";
  const explicitManualEntryStarted = !!ownership?.mobileManualEntryStarted;
  const shouldShowLegacyManualPreliminary =
    mobileEntryMode === "manual" &&
    !hasCanonicalOwnershipData &&
    !isAssistantGeneratedReview;
  const accountHolderTrustNameKind = String(
    ownership?.accountHolderTrustNameKind || ""
  )
    .trim()
    .toLowerCase();

  // Entity type answered in the upload-first step: restored after "Close assistant" or "Restart", and
  // changed only through the one-line summary (with the same fields as the step).
  const answeredEntityType = clientEntityTypeOf(ownership);
  useEffect(() => {
    if (readOnly || !entityTypeAnsweredEarlier || !entityTypeComplete(answeredEntityType) || assistantPreliminaryMode) return;
    onSetAt?.(["ownership"], applyClientEntityType(ownership, answeredEntityType, customer?.name));
  }, [entityTypeAnsweredEarlier, assistantPreliminaryMode, answeredEntityType.type, answeredEntityType.holdingKind, answeredEntityType.trustKind]); // eslint-disable-line
  function changeAnsweredEntityType(choice) {
    const apply = () => onSetAt?.(["ownership"], applyClientEntityType(ownership, choice, customer?.name));
    if (typeChangeClearsDetails(ownership, choice)) {
      appConfirm({ group: "appConfirm", header: "Change the entity type?", acceptLabel: "Change", rejectLabel: "Keep",
        message: "The ownership details already entered will be removed.", accept: apply });
      return;
    }
    apply();
  }
  const isTrustOrFoundationAccountHolder =
    isTrustAccountHolder || isFoundationAccountHolder;
  const entityName = String(customer?.name || "").trim() || "This entity";
  const liveOwnershipSnapshot = useMemo(
    () =>
      buildLiveReviewOwnershipSnapshot({
        ownership: normalizeOwnershipPercentsForReviewSnapshot(ownership),
        accountHolderName: entityName,
      }),
    [ownership, entityName]
  );
  const liveOwnershipPercentWarning = useMemo(
    () => ownershipPercentWarning(liveOwnershipSnapshot),
    [liveOwnershipSnapshot]
  );
  const invalidPercentageEdges = useMemo(
    () => liveOwnershipPercentWarning === OWNERSHIP_PERCENTAGE_WARNING
      ? invalidOwnershipPercentageEdges(liveOwnershipSnapshot, toSnapshotPercentNumber) : [],
    [liveOwnershipPercentWarning, liveOwnershipSnapshot]
  );
  const hasExistingOwnershipData =
    shareholders.length > 0 || ubos.length > 0 || signatories.length > 0;
  const hasPreliminarySelection =
    hasExplicitPreliminaryFlag || isNoneOfAbove;
  const isPreliminaryValidated = hasPreliminarySelection;
  const needsTrustNameKindSelection =
    isTrustAccountHolder &&
    accountHolderTrustNameKind !== "trust" &&
    accountHolderTrustNameKind !== "trustee";
  const [assistantArrangementSummaryVisible, setAssistantArrangementSummaryVisible] =
    useState(false);

  useEffect(() => {
    if (mobileEntryMode !== "assistant") {
      setAssistantLockedPath(null);
      return;
    }
    if (!assistantPreliminaryMode || !assistantDirectCountNumber || !assistantRoots.length) {
      setAssistantLockedPath(nextAssistantPath || null);
      return;
    }
    if (
      Array.isArray(assistantLockedPath) &&
      getAssistantNodeAtPath(assistantRoots, assistantLockedPath)
    ) {
      return;
    }
    setAssistantLockedPath(nextAssistantPath || null);
  }, [
    mobileEntryMode,
    assistantPreliminaryMode,
    assistantDirectCountNumber,
    assistantRoots,
    nextAssistantPath,
    assistantLockedPath,
  ]);

  useEffect(() => {
    if (mobileEntryMode !== "assistant") return;
    if (!assistantPercentShouldApply(assistantPreliminaryMode)) return;
    if (!Array.isArray(assistantCurrentPath) || assistantCurrentPath.length !== 1) return;
    if (assistantDirectCountNumber <= 1) return;
    if (assistantCurrentDirectShareholderIndex !== assistantDirectCountNumber - 1) return;

    const expectedOwnershipPercent = normalizePercentForSave(
      String(Math.round(assistantRemainingDirectOwnership * 100)).padStart(4, "0")
    );
    if (
      String(assistantCurrentNode?.ownershipPercent || "").trim() ===
      expectedOwnershipPercent
    ) {
      return;
    }

    updateMobileAssistantDraft((prev) => ({
      ...(prev || {}),
      roots: updateAssistantNodeAtPath(
        cloneAssistantNodes(prev?.roots || []),
        assistantCurrentPath,
        (node) => ({
          ...node,
          ownershipPercent: expectedOwnershipPercent,
        })
      ),
    }));
  }, [
    mobileEntryMode,
    assistantPreliminaryMode,
    assistantCurrentPath,
    assistantCurrentNode?.ownershipPercent,
    assistantCurrentDirectShareholderIndex,
    assistantDirectCountNumber,
    assistantRemainingDirectOwnership,
  ]);

  useEffect(() => {
    if (mobileEntryMode !== "assistant") return;
    if (!Array.isArray(assistantCurrentPath) || assistantCurrentPath.length <= 1) return;
    if (!assistantCurrentParentNode) return;
    if (assistantCurrentParentIsPic) return;
    if (assistantCurrentParentOwnersCount < 1) return;
    if (assistantCurrentNodeIndexInParent !== assistantCurrentParentOwnersCount - 1) return;

    const expectedOwnershipPercent = normalizePercentForSave(
      String(Math.round(assistantRemainingIndirectOwnership * 100)).padStart(4, "0")
    );
    if (
      String(assistantCurrentNode?.ownershipPercent || "").trim() === expectedOwnershipPercent
    ) {
      return;
    }

    updateMobileAssistantDraft((prev) => ({
      ...(prev || {}),
      roots: updateAssistantNodeAtPath(
        cloneAssistantNodes(prev?.roots || []),
        assistantCurrentPath,
        (node) => ({
          ...node,
          ownershipPercent: expectedOwnershipPercent,
        })
      ),
    }));
  }, [
    mobileEntryMode,
    assistantPreliminaryMode,
    assistantCurrentPath,
    assistantCurrentNode?.ownershipPercent,
    assistantCurrentNodeIndexInParent,
    assistantCurrentParentOwnersCount,
    assistantCurrentParentIsPic,
    assistantRemainingIndirectOwnership,
  ]);

  // Auto-initialize one empty signatory when entering the signatories step
  useEffect(() => {
    if (view !== "signatories") return;
    if (signatories.length > 0) return;
    onSetAt?.(["ownership", "signatories"], [emptySignatory()]);
  }, [view]); // eslint-disable-line


  useEffect(() => {
    if (mobileEntryMode) return;
    if (
      assistantPreliminaryMode ||
      assistantDirectCountNumber > 0 ||
      assistantRoots.length > 0
    ) {
      setMobileEntryMode("assistant");
    }
  }, [
    assistantDirectCountNumber,
    assistantPreliminaryMode,
    assistantRoots.length,
    mobileEntryMode,
  ]);
  useEffect(() => {
    if (!assistantPreliminaryMode) return;
    // Map holdingActive/holdingPassive back to "holding" for the main picker
    const pickerValue =
      assistantPreliminaryMode === "holdingActive" || assistantPreliminaryMode === "holdingPassive"
        ? "holding"
        : assistantPreliminaryMode;
    if (MOBILE_ASSISTANT_PRELIM_OPTIONS.some((o) => o.value === pickerValue)) {
      setAssistantPrelimPending(pickerValue);
    }
  }, [assistantPreliminaryMode]);

  // Auto-commit quoted company immediately — no shareholder data needed,
  // so the parent Continue button becomes enabled without an extra step.
  useEffect(() => {
    if (assistantPreliminaryMode !== "quoted") return;
    if (assistantResultGenerated) return;
    if (readOnly) return;
    applyAssistantOutcome();
  }, [assistantPreliminaryMode, assistantResultGenerated, readOnly]); // eslint-disable-line


  const [countrySheetOpen, setCountrySheetOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [activeCountryField, setActiveCountryField] = useState(null);
  const [typeSheetOpen, setTypeSheetOpen] = useState(false);
  const [activeTypeIndex, setActiveTypeIndex] = useState(null);
  const [activeTypeListKey, setActiveTypeListKey] = useState("shareholders");
  const [pendingAddListKey, setPendingAddListKey] = useState("");
  const [trusteeKindModal, setTrusteeKindModal] = useState({
    open: false,
    index: -1,
    listKey: "shareholders",
    partyId: "",
    partyCollection: "trustParties",
    kindField: "trusteeKind",
    title: "Select trustee type",
    focusNameAfterSelect: false,
  });
  const [signatoryCapacityModal, setSignatoryCapacityModal] = useState({
    open: false,
    index: -1,
  });
  const [signatoryNameFocusIndex, setSignatoryNameFocusIndex] = useState(null);

  // Hide the page progress header while the signatory name overlay is open
  useEffect(() => {
    const cls = "kycSuggestOverlayOpen";
    if (signatoryNameFocusIndex !== null) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [signatoryNameFocusIndex]);

  const [basisOfControlModal, setBasisOfControlModal] = useState({
    open: false,
    index: -1,
  });
  const [ownershipModalOpen, setOwnershipModalOpen] = useState(false);
  const [activeOwnershipIndex, setActiveOwnershipIndex] = useState(null);
  const [activeOwnershipTarget, setActiveOwnershipTarget] = useState({
    mode: "shareholder",
    label: "OWNERSHIP %",
  });
  const [ownershipDraft, setOwnershipDraft] = useState("");
  const [ownershipCursorIndex, setOwnershipCursorIndex] = useState(null);
  const ownershipWholeSeedRef = useRef("");
  const ownershipWholeGroupActiveRef = useRef(false);
  const [trustEditModal, setTrustEditModal] = useState({
    open: false,
    index: -1,
    listKey: "shareholders",
  });
  const trustEditSnapshotRef = useRef(null);
  const [controllingEntityModalOpen, setControllingEntityModalOpen] =
    useState(false);
  const [controllingEntityModalMode, setControllingEntityModalMode] =
    useState("individual");
  const [deletingItemKeys, setDeletingItemKeys] = useState({});
  const [pendingCarouselTarget, setPendingCarouselTarget] = useState(null);
  const [carouselInitialIndex, setCarouselInitialIndex] = useState({
    shareholders: 0,
    ubos: 0,
    signatories: 0,
  });
  const [carouselRenderNonce, setCarouselRenderNonce] = useState({
    shareholders: 0,
    ubos: 0,
    signatories: 0,
  });
  const shareholderSwiperRef = useRef(null);
  const uboSwiperRef = useRef(null);
  const signatorySwiperRef = useRef(null);
  const assistantFoundationAutoOpenRef = useRef(false);
  const countrySearchRef = useRef(null);
  const trusteeNameInputRefs = useRef({});
  const trustEditorPartyRefs = useRef({});
  const suppressTrusteeNameModalRef = useRef("");
  const deleteItemTimersRef = useRef({});
  const carouselJumpTimersRef = useRef([]);
  const basisOfControlModalRef = useRef(null);

  useEffect(() => {
    if (!countrySheetOpen) return;
    const frame = window.requestAnimationFrame(() => {
      countrySearchRef.current?.focus();
      countrySearchRef.current?.select?.();
    });
    const timer = window.setTimeout(() => {
      countrySearchRef.current?.focus();
      countrySearchRef.current?.select?.();
    }, 120);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [countrySheetOpen]);

  useEffect(() => {
    if (!basisOfControlModal.open) return;
    const frame = window.requestAnimationFrame(() => {
      basisOfControlModalRef.current?.focus?.();
    });
    const timer = window.setTimeout(() => {
      basisOfControlModalRef.current?.focus?.();
    }, 80);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [basisOfControlModal.open]);

  useEffect(() => {
    if (!basisOfControlModal.open) return;

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        confirmBasisOfControlSelection();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeBasisOfControlModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [basisOfControlModal.open, ubos, basisOfControlModal.index]);

  useEffect(() => {
    return () => {
      Object.values(deleteItemTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      carouselJumpTimersRef.current.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
    };
  }, []);

  useEffect(() => {
    if (readOnly) return;
    const current = Array.isArray(ownership.signatories) ? ownership.signatories : [];
    if (!current.length) return;

    let changed = false;
    const next = current.map((item) => {
      const normalizedCapacity = normalizeSignatoryCapacity(item?.capacity);
      if (normalizedCapacity === String(item?.capacity || "")) return item;
      changed = true;
      return {
        ...(item || {}),
        capacity: normalizedCapacity,
      };
    });

    if (changed) {
      setList("signatories", next);
    }
  }, [ownership.signatories, readOnly]);

  function scheduleCarouselJump(listKey, index) {
    const currentSwiper = getSwiperByListKey(listKey);
    const startIndex =
      typeof currentSwiper?.activeIndex === "number"
        ? currentSwiper.activeIndex
        : index;

    setCarouselInitialIndex((current) => ({
      ...current,
      [listKey]: startIndex,
    }));
    setCarouselRenderNonce((current) => ({
      ...current,
      [listKey]: (current?.[listKey] || 0) + 1,
    }));

    const run = () => {
      const swiper = getSwiperByListKey(listKey);
      if (!swiper) return;
      swiper.update?.();
      swiper.slideTo?.(index, 320);
    };

    run();
    [80, 180, 320, 520].forEach((delay) => {
      const timerId = window.setTimeout(run, delay);
      carouselJumpTimersRef.current.push(timerId);
    });
  }

  function getSwiperByListKey(listKey) {
    if (listKey === "shareholders") return shareholderSwiperRef.current;
    if (listKey === "ubos") return uboSwiperRef.current;
    if (listKey === "signatories") return signatorySwiperRef.current;
    return null;
  }

  function getListLengthByKey(listKey) {
    if (listKey === "shareholders") return shareholders.length;
    if (listKey === "ubos") return ubos.length;
    if (listKey === "signatories") return signatories.length;
    return 0;
  }

  function tryFocusPendingCarouselTarget() {
    if (!pendingCarouselTarget) return false;
    const swiper = getSwiperByListKey(pendingCarouselTarget.listKey);
    const listLength = getListLengthByKey(pendingCarouselTarget.listKey);
    if (!swiper || pendingCarouselTarget.index >= listLength) return false;

    swiper.update?.();
    swiper.slideTo?.(pendingCarouselTarget.index, 320);
    return true;
  }

  useEffect(() => {
    if (!pendingCarouselTarget) return;

    const attempt = () => {
      if (tryFocusPendingCarouselTarget()) {
        setPendingCarouselTarget(null);
      }
    };

    const frame = window.requestAnimationFrame(attempt);
    const timerA = window.setTimeout(attempt, 100);
    const timerB = window.setTimeout(attempt, 220);
    const timerC = window.setTimeout(attempt, 380);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timerA);
      window.clearTimeout(timerB);
      window.clearTimeout(timerC);
    };
  }, [
    pendingCarouselTarget,
    shareholders.length,
    ubos.length,
    signatories.length,
    typeSheetOpen,
    controllingEntityModalOpen,
  ]);

  const filteredCountryOptions = COUNTRY_OPTIONS.filter((opt) =>
    countryMatchesSearch(opt, countrySearch)
  );
  const accountHolderArrangement = activeAccountHolderArrangementType
    ? legalArrangements.find((arrangement) => {
        if (String(arrangement?.source || "") !== "account_holder") return false;
        return normalizeArrangementType(arrangement?.arrangementType) ===
          activeAccountHolderArrangementType;
      }) || null
    : null;
  const getShareholderArrangement = (shareholderItem) => {
    const rowId = String(shareholderItem?._rowId || "").trim();
    const holderType = normalizeHolderType(shareholderItem?.holderType);
    if (!rowId || !["trust", "foundation"].includes(holderType)) return null;
    return (
      legalArrangements.find((arrangement) => {
        if (String(arrangement?.source || "") !== "shareholder") return false;
        if (String(arrangement?.sourceRefRowId || "") !== rowId) return false;
        return (
          normalizeArrangementType(arrangement?.arrangementType) ===
          (holderType === "foundation" ? "Foundation" : "Trust")
        );
      }) || null
    );
  };
  const accountHolderArrangementItem = activeAccountHolderArrangementType
    ? (() => {
        const baseItem = arrangementToMobileHolder(
          accountHolderArrangement ||
            makeAccountHolderArrangement(activeAccountHolderArrangementType),
          {
            _rowId: String(
              accountHolderArrangement?._rowId ||
                `account_holder_${activeAccountHolderArrangementType.toLowerCase()}`
            ),
          }
        );
        if (accountHolderArrangement) return baseItem;
        if (activeAccountHolderArrangementType === "Foundation") {
          return {
            ...(baseItem || {}),
            ...nameFields(baseItem),
            name: String(baseItem?.name || entityName || ""),
          };
        }
        if (activeAccountHolderArrangementType === "Trust") {
          if (accountHolderTrustNameKind === "trust") {
            return {
              ...(baseItem || {}),
              ...nameFields(baseItem),
              name: String(baseItem?.name || entityName || ""),
            };
          }
          if (accountHolderTrustNameKind === "trustee") {
            const trustParties = Array.isArray(baseItem?.trustParties)
              ? baseItem.trustParties.map((party) => ({ ...(party || {}) }))
              : makeDefaultTrustParties();
            return {
              ...(baseItem || {}),
              trustParties: trustParties.map((party) =>
                String(party?.role || "") === "Trustee"
                  ? {
                      ...(party || {}),
                      ...nameFields(party),
                      name: String(party?.name || entityName || ""),
                    }
                  : party
              ),
            };
          }
        }
        return baseItem;
      })()
    : null;
  const assistantArrangementReady =
    assistantPreliminaryMode === "foundation"
      ? assistantArrangementSummaryVisible &&
        isFoundationHolderComplete(accountHolderArrangementItem)
      : assistantPreliminaryMode === "trust"
      ? // Complete only with the trust name and every party filled in (the name alone, pre-filled from the
        // client name, is not enough), as for a foundation.
        assistantArrangementSummaryVisible &&
        !needsTrustNameKindSelection &&
        isTrustHolderComplete(accountHolderArrangementItem)
      : false;

  // Auto-commit trust/foundation as soon as details are complete —
  // sets the accountHolderTrust/Foundation flag so the parent Continue
  // button becomes enabled without a separate "Proceed" step.
  useEffect(() => {
    if (assistantPreliminaryMode !== "trust" && assistantPreliminaryMode !== "foundation") return;
    if (!assistantArrangementReady) return;
    if (readOnly) return;
    applyAssistantOutcome();
  }, [assistantPreliminaryMode, assistantArrangementReady, readOnly]); // eslint-disable-line

  useEffect(() => {
    if (mobileEntryMode !== "assistant") {
      setAssistantArrangementSummaryVisible(false);
      assistantFoundationAutoOpenRef.current = false;
      return;
    }
    if (assistantPreliminaryMode === "trust") {
      if (
        accountHolderTrustNameKind === "trust" ||
        accountHolderTrustNameKind === "trustee"
      ) {
        setAssistantArrangementSummaryVisible(
          mobileArrangementHasVisibleDetails(accountHolderArrangementItem)
        );
      } else {
        setAssistantArrangementSummaryVisible(false);
      }
      return;
    }
    if (assistantPreliminaryMode === "foundation") {
      if (!assistantFoundationAutoOpenRef.current) {
        assistantFoundationAutoOpenRef.current = true;
        setAssistantArrangementSummaryVisible(false);
      }
      return;
    }
    assistantFoundationAutoOpenRef.current = false;
    setAssistantArrangementSummaryVisible(false);
  }, [
    mobileEntryMode,
    assistantPreliminaryMode,
    accountHolderTrustNameKind,
    accountHolderArrangementItem,
  ]);

  useEffect(() => {
    if (mobileEntryMode !== "assistant") return;
    if (assistantPreliminaryMode !== "foundation") return;
    if (assistantArrangementSummaryVisible) return;
    if (trustEditModal.open) return;
    if (!assistantFoundationAutoOpenRef.current) return;

    assistantFoundationAutoOpenRef.current = false;
    const frame = window.requestAnimationFrame(() => {
      openTrustEditModal(0, "accountHolderArrangement");
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [
    mobileEntryMode,
    assistantPreliminaryMode,
    assistantArrangementSummaryVisible,
    trustEditModal.open,
  ]);

  const visibleShareholders = isTrustOrFoundationAccountHolder
    ? accountHolderArrangementItem
      ? [accountHolderArrangementItem]
      : []
    : shareholders.map((item) => {
        const holderType = normalizeHolderType(item?.holderType);
        if (!["trust", "foundation"].includes(holderType)) return item;
        return arrangementToMobileHolder(getShareholderArrangement(item), item);
      });
  const shareholderArrangementMap = visibleShareholders.reduce((acc, item) => {
    const rowId = String(item?._rowId || "").trim();
    if (!rowId) return acc;
    const arrangement = getShareholderArrangement(item);
    if (arrangement) acc[rowId] = arrangement;
    return acc;
  }, {});
  const visibleShareholderTreeNodeIds = new Set(
    visibleShareholders
      .map((item) => String(item?.treeNodeId || "").trim())
      .filter(Boolean)
  );
  const visibleUbos = (
    isAssistantGeneratedReview && visibleShareholderTreeNodeIds.size
      ? ubos.filter((item) => {
          const treeNodeId = String(item?.treeNodeId || "").trim();
          return !treeNodeId || !visibleShareholderTreeNodeIds.has(treeNodeId);
        })
      : ubos
  );
	  const hasEligibleControllingEntityShareholder = visibleShareholders.some((item) => {
	    if (normalizeHolderType(item?.holderType) !== "entity") return false;
	    if (/^quoted/i.test(String(item?.shareholderEntityType || "").trim())) return false;
	    return true;
	  });
  const eligibleControllingEntities = visibleShareholders
    .map((item, index) => ({
      rowId: String(item?._rowId || `shareholder_${index}`),
      ...nameFields(item),
      name: String(item?.name || "").trim() || `Entity ${index + 1}`,
      holderType: normalizeHolderType(item?.holderType),
      ownershipPercent: item?.ownershipPercent,
      shareholderEntityType: String(item?.shareholderEntityType || ""),
    }))
    .filter((item) => {
	      if (item.holderType !== "entity") return false;
	      if (/^quoted/i.test(item.shareholderEntityType.trim())) return false;
	      return true;
	    });
  const shouldShowControllingPersons =
    !isQuotedCompany &&
    !isTrustOrFoundationAccountHolder &&
    (Boolean(uboFlags?.hasOtherControl) ||
      hasEligibleControllingEntityShareholder ||
      visibleUbos.length > 0);

  // Pool of individual persons from step 2 — used for signatory name suggestions.
  const signatoryIndividualPool = useMemo(() => {
    const out = [];
    const seen = new Set();
    const personKey = (item) =>
      [
        String(item?.name || "").trim().toLowerCase().replace(/\s+/g, " "),
        String(item?.nationality || "").trim().toUpperCase(),
        String(item?.countryOfResidence || "").trim().toUpperCase(),
      ].join("|");
    const addPerson = (item) => {
      if (normalizeHolderType(item?.holderType) !== "individual") return;
      const name = String(item?.name || "").trim();
      const key = personKey(item);
      if (!name || seen.has(key)) return;
      seen.add(key);
      out.push({
        ...nameFields(item),
        name,
        nationality: String(item?.nationality || ""),
        countryOfResidence: String(item?.countryOfResidence || ""),
      });
    };
    shareholders.forEach(addPerson);
    ubos.forEach(addPerson);
    return out;
  }, [shareholders, ubos]); // eslint-disable-line

  // Filtered suggestions for the currently focused signatory name input.
  const signatoryNameQuery =
    signatoryNameFocusIndex !== null
      ? String(signatories[signatoryNameFocusIndex]?.name || "")
      : "";
  const signatoryPersonKey = (item) =>
    [
      String(item?.name || "").trim().toLowerCase().replace(/\s+/g, " "),
      String(item?.nationality || "").trim().toUpperCase(),
      String(item?.countryOfResidence || "").trim().toUpperCase(),
    ].join("|");
  // Known individuals (shareholders, UBOs) not yet signatories: one tap adds them with their details.
  const signatoryQuickAdd = useMemo(() => {
    const existing = new Set(
      signatories.filter((row) => String(row?.name || "").trim()).map(signatoryPersonKey)
    );
    return signatoryIndividualPool.filter((person) => !existing.has(signatoryPersonKey(person)));
  }, [signatories, signatoryIndividualPool]); // eslint-disable-line
  const knownSignatoryDetails = (person) => ({
    ...nameFields(person),
    name: person.name,
    nationality: person.nationality,
    countryOfResidence: person.countryOfResidence,
  });
  // An empty signatory offers them in its own card ("Tap a name to prefill…"); otherwise they appear above
  // "+ Add another signatory" and add a new signatory.
  const hasEmptySignatory = signatories.some((row) => !String(row?.name || "").trim());

  const signatoryNameMatches = useMemo(() => {
    if (signatoryNameFocusIndex === null) return [];
    const q = signatoryNameQuery.trim().toLowerCase();
    if (!q) return [];
    const existingKeys = new Set(
      signatories
        .filter((_, idx) => idx !== signatoryNameFocusIndex)
        .filter((row) => String(row?.name || "").trim())
        .map(signatoryPersonKey)
    );
    const availablePool = signatoryIndividualPool.filter(
      (person) => !existingKeys.has(signatoryPersonKey(person))
    );
    // Full-name prefix matches (e.g. "Fa" → "Fabien Richard")
    const fullPrefix = availablePool.filter((p) =>
      p.name.toLowerCase().startsWith(q)
    );
    // Word-prefix matches on non-first words (e.g. "Rich" → "Fabien Richard")
    const wordPrefix = availablePool.filter((p) => {
      if (p.name.toLowerCase().startsWith(q)) return false; // already in fullPrefix
      return p.name
        .toLowerCase()
        .split(/\s+/)
        .slice(1) // skip first word — already covered by fullPrefix
        .some((word) => word.startsWith(q));
    });
    return [...fullPrefix, ...wordPrefix];
  }, [signatoryIndividualPool, signatoryNameQuery, signatoryNameFocusIndex, signatories]); // eslint-disable-line

  function applyPreliminarySelection(mode) {
    if (readOnly) return;
    const nextFlags = {
      ...uboFlags,
      pctNotMeaningful: mode === "pic",
      quotedCompany: mode === "quoted",
      accountHolderTrust: mode === "trust",
      accountHolderFoundation: mode === "foundation",
    };

    const nextLegalArrangements = (Array.isArray(legalArrangements)
      ? legalArrangements
      : []
    ).filter((arrangement) => {
      if (String(arrangement?.source || "") !== "account_holder") return true;
      const arrangementType = normalizeArrangementType(arrangement?.arrangementType);
      if (mode === "trust") return arrangementType === "Trust";
      if (mode === "foundation") return arrangementType === "Foundation";
      return false;
    });

    onSetAt?.(["ownership", "uboFlags"], nextFlags);
    onSetAt?.(["ownership", "preliminaryNoneOfAbove"], mode === "noneOfAbove");
    const nextProfile = desktopProfileForPreliminaryMode(mode);
    onSetAt?.(["ownership", "desktopClientEntityType"], nextProfile.entityType);
    onSetAt?.(["ownership", "desktopClientHoldingKind"], nextProfile.holdingKind);
    onSetAt?.(["ownership", "legalArrangements"], {
      arrangements: nextLegalArrangements,
    });
    if (mobileEntryMode === "assistant") {
      updateMobileAssistantDraft((prev) => ({
        ...(prev || {}),
        preliminaryMode: mode === "none" ? "" : mode,
      }));
    }
    if (mode !== "trust") {
      onSetAt?.(["ownership", "accountHolderTrustNameKind"], "");
    }
    setAssistantArrangementSummaryVisible(false);
  }

  function setPreliminaryFlag(flagKey, checked) {
    if (readOnly) return;
    if (!checked) {
      applyPreliminarySelection("none");
      return;
    }
    const modeByFlag = {
      pctNotMeaningful: "pic",
      quotedCompany: "quoted",
      accountHolderTrust: "trust",
      accountHolderFoundation: "foundation",
    };
    const nextMode = modeByFlag[flagKey] || "none";
    applyPreliminarySelection(nextMode);
    if (
      mobileEntryMode === "manual" &&
      nextMode === "foundation" &&
      typeof window !== "undefined"
    ) {
      window.requestAnimationFrame(() => {
        openTrustEditModal(0, "accountHolderArrangement");
      });
    }
  }

  function setPreliminaryNoneOfAbove(checked) {
    if (readOnly) return;
    applyPreliminarySelection(checked ? "noneOfAbove" : "none");
  }

  function setMobileEntryMode(nextMode) {
    if (readOnly) return;
    onSetAt?.(["ownership", "mobileEntryMode"], nextMode || "");
  }

  function inferMobileAssistantPreliminaryMode() {
    if (isQuotedCompany || desktopEntityType === "quoted") return "quoted";
    if (isTrustAccountHolder || desktopEntityType === "trust") return "trust";
    if (isFoundationAccountHolder || desktopEntityType === "foundation") {
      return "foundation";
    }
    if (
      isPicOrPassiveHolding ||
      desktopEntityType === "pic" ||
      (desktopEntityType === "holding" && desktopHoldingKind === "passive")
    ) {
      return "holdingPassive";
    }
    if (desktopEntityType === "holding" && desktopHoldingKind === "active") {
      return "holdingActive";
    }
    if (
      isNoneOfAbove ||
      desktopEntityType === "operating" ||
      desktopEntityType === "operational"
    ) {
      return "noneOfAbove";
    }
    return "";
  }

  useEffect(() => {
    const inferredPreliminaryMode = inferMobileAssistantPreliminaryMode();
    const shouldUseAssistant =
      desktopCompletionMethod === "guided" ||
      desktopCompletionMethod === "visual" ||
      hasAssistantSnapshot ||
      assistantResultGenerated ||
      (hasCanonicalOwnershipData &&
        mobileEntryMode === "manual" &&
        !explicitManualEntryStarted);

    if (readOnly || !hasCanonicalOwnershipData) return;
    if (mobileEntryMode === "assistant") return;
    if (mobileEntryMode && !shouldUseAssistant) return;

    if (shouldUseAssistant) {
      onSetAt?.(["ownership", "mobileEntryMode"], "assistant");
      onSetAt?.(["ownership", "assistantGenerated"], true);
      if (!assistantPreliminaryMode) {
        onSetAt?.(["ownership", "mobileAssistantDraft"], {
          ...(ownership?.mobileAssistantDraft || {}),
          preliminaryMode: inferredPreliminaryMode,
          directCount: String(shareholders.length || ""),
          roots: Array.isArray(ownership?.mobileAssistantDraft?.roots)
            ? ownership.mobileAssistantDraft.roots
            : [],
        });
      }
      return;
    }

    onSetAt?.(["ownership", "mobileEntryMode"], "manual");
  }, [
    readOnly,
    mobileEntryMode,
    hasCanonicalOwnershipData,
    explicitManualEntryStarted,
    desktopCompletionMethod,
    hasAssistantSnapshot,
    assistantResultGenerated,
    assistantPreliminaryMode,
    desktopEntityType,
    desktopHoldingKind,
    isQuotedCompany,
    isTrustAccountHolder,
    isFoundationAccountHolder,
    isPicOrPassiveHolding,
    isNoneOfAbove,
    shareholders.length,
    ownership?.mobileAssistantDraft,
  ]); // eslint-disable-line

  // Company documents confirmed ("Use these details"): the assistant's answers were prepared from them.
  // - "generate" (only individual shareholders): its result is generated once without showing the chat, and the
  //   client lands on the result to verify it.
  // - "chat" (a company among the shareholders): the chat opens already filled, where the client continues with
  //   the owners behind that company.
  // Then ("link"), once the shareholders exist, the signatories filled from the documents are linked to the same
  // shareholders (as on desktop).
  const documentPrefillOwnership = ownership?.documentPrefillOwnership;
  useEffect(() => {
    if (readOnly || view === "signatories" || !documentPrefillOwnership) return;
    if (documentPrefillOwnership === "link") {
      if (!shareholders.length) return;
      const linked = linkPrefilledSignatories(ownership, customer?.documentPrefill?.signatories);
      onSetAt?.(["ownership"], { ...linked, documentPrefillOwnership: "" });
      return;
    }
    const mode = assistantPreliminaryMode || inferMobileAssistantPreliminaryMode();
    if (!assistantRoots.length || !["noneOfAbove", "holdingActive", "holdingPassive"].includes(mode)) return;
    onSetAt?.(["ownership", "mobileAssistantDraft"], { ...(mobileAssistantDraft || {}), preliminaryMode: mode });
    if (documentPrefillOwnership === "chat") setMobileEntryMode("assistant");
    else applyAssistantOutcome(mode);
    onSetAt?.(["ownership", "documentPrefillOwnership"], "link");
  }, [readOnly, view, documentPrefillOwnership, shareholders.length]); // eslint-disable-line

  function updateMobileAssistantDraft(updater) {
    if (readOnly) return;
    const base =
      ownership?.mobileAssistantDraft &&
      typeof ownership.mobileAssistantDraft === "object"
        ? ownership.mobileAssistantDraft
        : {};
    const next =
      typeof updater === "function" ? updater(base) : { ...base, ...(updater || {}) };
    onSetAt?.(["ownership", "mobileAssistantDraft"], next);
  }

  function resetMobileAssistantDraft(preliminaryMode = "") {
    if (readOnly) return;
    onSetAt?.(["ownership", "assistantGenerated"], false);
    onSetAt?.(["ownership", "mobileAssistantDraft"], {
      preliminaryMode,
      directCount: "",
      roots: [],
    });
    // If an auto-committed mode was active, clear its flags so the parent
    // Continue button goes back to disabled until a valid mode is re-selected.
    if (
      assistantPreliminaryMode === "quoted" ||
      assistantPreliminaryMode === "trust" ||
      assistantPreliminaryMode === "foundation"
    ) {
      onSetAt?.(["ownership", "uboFlags"], {
        ...(ownership?.uboFlags || {}),
        quotedCompany: false,
        accountHolderTrust: false,
        accountHolderFoundation: false,
      });
    }
  }

  function startManualEntryMode() {
    onSetAt?.(["ownership", "assistantGenerated"], false);
    onSetAt?.(["ownership", "mobileManualEntryStarted"], true);
    setMobileEntryMode("manual");
  }

  function startAssistantMode() {
    onSetAt?.(["ownership", "assistantGenerated"], false);
    onSetAt?.(["ownership", "mobileManualEntryStarted"], false);
    setMobileEntryMode("assistant");
    if (!ownership?.mobileAssistantDraft) {
      resetMobileAssistantDraft("");
    }
  }

  function restartAssistantFromScratch() {
    if (readOnly) return;
    onSetAt?.(["ownership", "shareholders"], []);
    onSetAt?.(["ownership", "ubos"], []);
    onSetAt?.(["ownership", "legalArrangements"], { arrangements: [] });
    onSetAt?.(["ownership", "assistantTree"], null);
    onSetAt?.(["ownership", "assistantGenerated"], false);
    onSetAt?.(["ownership", "uboFlags"], {
      pctNotMeaningful: false,
      quotedCompany: false,
      accountHolderTrust: false,
      accountHolderFoundation: false,
      hasOtherControl: false,
    });
    onSetAt?.(["ownership", "preliminaryNoneOfAbove"], false);
    onSetAt?.(["ownership", "accountHolderTrustNameKind"], "");
    onSetAt?.(["ownership", "mobileAssistantDraft"], {
      preliminaryMode: "",
      directCount: "",
      roots: [],
    });
    setAssistantArrangementSummaryVisible(false);
    setMobileEntryMode("assistant");
  }

  function invalidateAssistantSnapshot() {
    onSetAt?.(["ownership", "assistantTree"], null);
  }

  function getAssistantSnapshotMode() {
    if (assistantPreliminaryMode) return assistantPreliminaryMode;
    if (isQuotedCompany) return "quoted";
    if (isPicOrPassiveHolding) return "pic";
    if (isTrustAccountHolder) return "trust";
    if (isFoundationAccountHolder) return "foundation";
    return "noneOfAbove";
  }

  function syncAssistantRootsWithCurrentOwnership(nextShareholders, nextUbos) {
    let nextRoots = cloneAssistantNodes(assistantRoots);
    const shareholderTreeNodeIds = new Set();

    (Array.isArray(nextShareholders) ? nextShareholders : []).forEach((row) => {
      const treeNodeId = String(row?.treeNodeId || "").trim();
      if (!treeNodeId) return;
      shareholderTreeNodeIds.add(treeNodeId);
      nextRoots = updateAssistantNodeById(nextRoots, treeNodeId, (node) => ({
        ...node,
        type: normalizeHolderType(row?.holderType) || node?.type || "",
        ...nameFields(row),
        name: String(row?.name || "").trim(),
        ownershipPercent:
          String(row?.ownershipPercent || "").trim() || String(node?.ownershipPercent || ""),
      }));
    });

    (Array.isArray(nextUbos) ? nextUbos : []).forEach((row) => {
      const treeNodeId = String(row?.treeNodeId || "").trim();
      if (!treeNodeId || shareholderTreeNodeIds.has(treeNodeId)) return;
      nextRoots = updateAssistantNodeById(nextRoots, treeNodeId, (node) => ({
        ...node,
        type: normalizeHolderType(row?.holderType) || node?.type || "",
        ...nameFields(row),
        name: String(row?.name || "").trim(),
      }));
    });

    return nextRoots;
  }

  function refreshAssistantSnapshot(nextShareholders, nextUbos) {
    if (!assistantResultGenerated) return;
    const snapshotMode = getAssistantSnapshotMode();
    if (
      snapshotMode === "trust" ||
      snapshotMode === "foundation" ||
      !assistantRoots.length
    ) {
      return;
    }

    const nextRoots = syncAssistantRootsWithCurrentOwnership(
      nextShareholders,
      nextUbos
    );
    onSetAt?.(["ownership", "mobileAssistantDraft"], {
      ...(mobileAssistantDraft || {}),
      preliminaryMode: snapshotMode,
      directCount:
        String(mobileAssistantDraft?.directCount || "").trim() ||
        String(nextRoots.length),
      roots: nextRoots,
    });
    onSetAt?.(
      ["ownership", "assistantTree"],
      buildMobileAssistantSnapshot(entityName, nextRoots, snapshotMode, {
        labelMode: assistantTreeLabelMode,
      })
    );
  }

  useEffect(() => {
    if (!assistantResultGenerated) return;
    const snapshotMode = getAssistantSnapshotMode();
    if (
      snapshotMode === "trust" ||
      snapshotMode === "foundation" ||
      !assistantRoots.length
    ) {
      return;
    }

    const nextRoots = syncAssistantRootsWithCurrentOwnership(shareholders, ubos);
    const nextSnapshot = buildMobileAssistantSnapshot(
      entityName,
      nextRoots,
      snapshotMode,
      { labelMode: assistantTreeLabelMode }
    );
    const currentRootsSignature = JSON.stringify(assistantRoots);
    const nextRootsSignature = JSON.stringify(nextRoots);
    const currentMermaid = String(ownership?.assistantTree?.mermaid || "");

    if (
      currentRootsSignature === nextRootsSignature &&
      currentMermaid === String(nextSnapshot?.mermaid || "")
    ) {
      return;
    }

    onSetAt?.(["ownership", "mobileAssistantDraft"], {
      ...(mobileAssistantDraft || {}),
      preliminaryMode: snapshotMode,
      directCount:
        String(mobileAssistantDraft?.directCount || "").trim() ||
        String(nextRoots.length),
      roots: nextRoots,
    });
    onSetAt?.(["ownership", "assistantTree"], nextSnapshot);
  }, [
    assistantResultGenerated,
    assistantRoots,
    entityName,
    isFoundationAccountHolder,
    isPicOrPassiveHolding,
    isQuotedCompany,
    isTrustAccountHolder,
    mobileAssistantDraft,
    onSetAt,
    ownership?.assistantTree?.mermaid,
    shareholders,
    ubos,
    assistantPreliminaryMode,
  ]);

  function didShareholderTreeFieldsChange(current, next) {
    const currentSignature = JSON.stringify(
      (Array.isArray(current) ? current : []).map((item) => ({
        rowId: String(item?._rowId || ""),
        treeNodeId: String(item?.treeNodeId || ""),
        holderType: normalizeHolderType(item?.holderType),
        ...nameFields(item),
        name: String(item?.name || "").trim(),
        ownershipPercent: String(item?.ownershipPercent || "").trim(),
      }))
    );
    const nextSignature = JSON.stringify(
      (Array.isArray(next) ? next : []).map((item) => ({
        rowId: String(item?._rowId || ""),
        treeNodeId: String(item?.treeNodeId || ""),
        holderType: normalizeHolderType(item?.holderType),
        ...nameFields(item),
        name: String(item?.name || "").trim(),
        ownershipPercent: String(item?.ownershipPercent || "").trim(),
      }))
    );
    return currentSignature !== nextSignature;
  }

  function didUboTreeFieldsChange(current, next) {
    const currentSignature = JSON.stringify(
      (Array.isArray(current) ? current : []).map((item) => ({
        rowId: String(item?._rowId || ""),
        treeNodeId: String(item?.treeNodeId || ""),
        holderType: normalizeHolderType(item?.holderType),
        ...nameFields(item),
        name: String(item?.name || "").trim(),
      }))
    );
    const nextSignature = JSON.stringify(
      (Array.isArray(next) ? next : []).map((item) => ({
        rowId: String(item?._rowId || ""),
        treeNodeId: String(item?.treeNodeId || ""),
        holderType: normalizeHolderType(item?.holderType),
        ...nameFields(item),
        name: String(item?.name || "").trim(),
      }))
    );
    return currentSignature !== nextSignature;
  }

  function applyAssistantPreliminaryMode(mode) {
    const nextProfile = desktopProfileForPreliminaryMode(mode);
    onSetAt?.(["ownership", "desktopClientEntityType"], nextProfile.entityType);
    onSetAt?.(["ownership", "desktopClientHoldingKind"], nextProfile.holdingKind);
    updateMobileAssistantDraft({
      preliminaryMode: mode || "",
      directCount: "",
      roots: [],
    });
  }

  function setAssistantDirectCount(value) {
    updateMobileAssistantDraft((prev) => ({
      ...(prev || {}),
      directCount: String(value || ""),
    }));
  }


  function updateAssistantCurrentNode(patch) {
    if (!assistantCurrentPath) return;
    updateMobileAssistantDraft((prev) => ({
      ...(prev || {}),
      roots: updateAssistantNodeAtPath(
        cloneAssistantNodes(prev?.roots || []),
        assistantCurrentPath,
        (node) => ({
          ...node,
          ...(patch || {}),
        })
      ),
    }));
  }

  function updateAssistantNodeAtModalPath(patch) {
    const path = trustEditSnapshotRef.current?.path;
    if (!Array.isArray(path) || !path.length) {
      updateAssistantCurrentNode(patch);
      return;
    }
    updateMobileAssistantDraft((prev) => ({
      ...(prev || {}),
      roots: updateAssistantNodeAtPath(
        cloneAssistantNodes(prev?.roots || []),
        path,
        (node) => ({
          ...node,
          ...(typeof patch === "function" ? patch(node) : patch || {}),
        })
      ),
    }));
  }

  function advanceAssistantCurrentNode() {
    setAssistantLockedPath(nextAssistantPath || null);
  }

  function applyAssistantNodeType(nextType) {
    const normalizedType = normalizeHolderType(nextType);
    // A sole shareholder holds 100%, but only where the client carries a % at all.
    const isSingleDirectShareholder =
      assistantCurrentPath?.length === 1 && assistantDirectCountNumber === 1 &&
      assistantPercentShouldApply(assistantPreliminaryMode);
    updateAssistantCurrentNode({
      type: normalizedType,
      entityCategory: normalizedType === "entity" ? "" : "nonQuotedOperational",
      holdingKind: "",
      nationality: "",
      countryOfResidence: "",
      ownershipPercent: isSingleDirectShareholder ? "100.00" : "",
      ownersCount: normalizedType === "entity" ? "" : "0",
      owners: [],
      trustParties: normalizedType === "trust" ? makeDefaultTrustParties() : null,
      foundationParties: normalizedType === "foundation" ? makeDefaultFoundationParties() : null,
    });
  }

  function applyAssistantEntityCategory(nextCategory) {
    updateAssistantCurrentNode({
      entityCategory: nextCategory || "nonQuotedOperational",
      holdingKind: nextCategory === "holding" ? "" : "",
      ...(isTerminalEntityCategory(nextCategory || "") ? { ownersCount: "0", owners: [] } : {}),
    });
  }

  function applyAssistantHoldingKind(nextKind) {
    updateAssistantCurrentNode({
      holdingKind: nextKind || "",
    });
  }

  function confirmAssistantCurrentEntityOwnersCount() {
    if (!assistantCurrentPath || assistantCurrentNodeType !== "entity") return;
    const nextCount = Math.max(
      1,
      Number.parseInt(String(assistantCurrentNode?.ownersCount || ""), 10) || 1
    );
    updateMobileAssistantDraft((prev) => ({
      ...(prev || {}),
      roots: updateAssistantNodeAtPath(
        cloneAssistantNodes(prev?.roots || []),
        assistantCurrentPath,
        (node) => ({
          ...node,
          ownersCount: String(nextCount),
          owners: Array.from({ length: nextCount }, () => makeAssistantOwnerNode()),
        })
      ),
    }));
    setAssistantLockedPath([...assistantCurrentPath, 0]);
  }

  // mode: the organisation type when it is not in the draft yet (result generated from the company documents).
  function buildAssistantOutcome(mode = "") {
    const preliminaryMode = mode || assistantPreliminaryMode || "noneOfAbove";
    if (preliminaryMode === "quoted") {
      return {
        nextFlags: {
          ...uboFlags,
          pctNotMeaningful: false,
          quotedCompany: true,
          accountHolderTrust: false,
          accountHolderFoundation: false,
          hasOtherControl: false,
        },
        directShareholderRows: [],
        nextUbos: [],
        snapshot: null,
      };
    }
    if (preliminaryMode === "trust" || preliminaryMode === "foundation") {
      return {
        nextFlags: {
          ...uboFlags,
          pctNotMeaningful: false,
          quotedCompany: false,
          accountHolderTrust: preliminaryMode === "trust",
          accountHolderFoundation: preliminaryMode === "foundation",
          hasOtherControl: false,
        },
        directShareholderRows: [],
        nextUbos: [],
        snapshot: null,
      };
    }

    const nextFlags = {
      ...uboFlags,
      pctNotMeaningful: preliminaryMode === "pic" || preliminaryMode === "holdingPassive",
      quotedCompany: false,
      accountHolderTrust: false,
      accountHolderFoundation: false,
      hasOtherControl: false,
    };

    const directShareholderRows = assistantRoots.map((node) => ({
      _rowId: makeId(),
      treeNodeId: String(node?.id || ""),
      ...nameFields(node),
      name: String(node?.name || "").trim(),
      holderType: normalizeHolderType(node?.type),
      beneficialOwnerType: "Beneficial owner",
      nationality: String(node?.nationality || ""),
      countryOfResidence: String(node?.countryOfResidence || ""),
      ownershipPercent: String(node?.ownershipPercent || ""),
      shareholderEntityType:
        normalizeHolderType(node?.type) === "entity"
          ? String(
              node?.entityCategory === "holding"
                ? node?.holdingKind === "passive"
                  ? "HoldingPassive"
                  : "HoldingActive"
                : node?.entityCategory === "nonQuotedOperational"
                ? "Operating Company"
                : node?.entityCategory || "Operating Company"
            )
          : formatHolderTypeLabel(node?.type),
      trustParties:
        normalizeHolderType(node?.type) === "trust"
          ? (Array.isArray(node?.trustParties) ? node.trustParties : makeDefaultTrustParties())
          : undefined,
      foundationParties:
        normalizeHolderType(node?.type) === "foundation"
          ? (Array.isArray(node?.foundationParties) ? node.foundationParties : makeDefaultFoundationParties())
          : undefined,
    }));

    const rootNodeIdToRowId = new Map();
    directShareholderRows.forEach((row) => {
      if (row?.treeNodeId && row?._rowId) {
        rootNodeIdToRowId.set(row.treeNodeId, row._rowId);
      }
    });

    const leaves = computeAssistantLeafRows(
      assistantRoots,
      preliminaryMode,
      "",
      100
    );
    const nextUbos = leaves
      .filter(({ node, depth: leafDepth }) => {
        const type = normalizeHolderType(node?.type);
        const isQuotedEntity = type === "entity" && /^quoted/i.test(String(node?.entityCategory || "").trim());
        if (!["individual", "trust", "foundation"].includes(type) && !isQuotedEntity) return false;
        // Direct shareholders (depth 0) appear only in the Direct Shareholders section, not Controlling Persons
        if (leafDepth === 0) return false;
        return true;
      })
      .map(({ node, rootNodeId, effectiveOwnershipPercent }) => ({
        ...emptyUbo(),
        _rowId: makeId(),
        treeNodeId: String(node?.id || ""),
        holderType: normalizeHolderType(node?.type),
        relatedShareholderRowId: rootNodeIdToRowId.get(rootNodeId) || "",
        ...nameFields(node),
        name: String(node?.name || "").trim(),
        nationality: String(node?.nationality || ""),
        countryOfResidence: String(node?.countryOfResidence || ""),
        shareholderEntityType:
          normalizeHolderType(node?.type) === "entity"
            ? String(node?.entityCategory === "quoted" ? "quoted" : node?.entityCategory || "")
            : undefined,
        basisOfControl: "Indirect ownership",
        ownershipPercent: effectiveOwnershipPercent,
        trustParties:
          normalizeHolderType(node?.type) === "trust"
            ? (Array.isArray(node?.trustParties) ? node.trustParties : makeDefaultTrustParties())
            : undefined,
        foundationParties:
          normalizeHolderType(node?.type) === "foundation"
            ? (Array.isArray(node?.foundationParties) ? node.foundationParties : makeDefaultFoundationParties())
            : undefined,
      }));

    return {
      nextFlags,
      directShareholderRows,
      nextUbos,
      snapshot: buildMobileAssistantSnapshot(
        entityName,
        assistantRoots,
        preliminaryMode,
        { labelMode: assistantTreeLabelMode }
      ),
    };
  }

  function applyAssistantOutcome(mode = "") {
    if (readOnly) return;
    const preliminaryMode = mode || assistantPreliminaryMode;
    const { nextFlags, directShareholderRows, nextUbos, snapshot } =
      buildAssistantOutcome(preliminaryMode);

    // Build legal arrangements for trust/foundation shareholders
    const existingOtherArrangements = (Array.isArray(legalArrangements) ? legalArrangements : [])
      .filter((a) => String(a?.source || "") !== "shareholder");
    const shareholderArrangements = directShareholderRows
      .filter((row) => ["trust", "foundation"].includes(normalizeHolderType(row?.holderType)))
      .map((row) => {
        const holderType = normalizeHolderType(row?.holderType);
        return makeLinkedArrangement({
          type: holderType === "foundation" ? "Foundation" : "Trust",
          source: "shareholder",
          sourceRefRowId: String(row?._rowId || ""),
          relatedShareholderName: String(row?.name || ""),
          autoCreated: true,
          ...nameFields(row),
          name: String(row?.name || ""),
          parties: holderType === "foundation"
            ? (Array.isArray(row?.foundationParties) ? row.foundationParties : makeDefaultFoundationParties())
            : (Array.isArray(row?.trustParties) ? row.trustParties : makeDefaultTrustParties()),
        });
      });
    const nextLegalArrangements = [...existingOtherArrangements, ...shareholderArrangements];

    onSetAt?.(["ownership", "uboFlags"], nextFlags);
    onSetAt?.(["ownership", "preliminaryNoneOfAbove"], preliminaryMode === "noneOfAbove" || preliminaryMode === "holdingActive");
    const nextProfile = desktopProfileForPreliminaryMode(preliminaryMode);
    onSetAt?.(["ownership", "desktopClientEntityType"], nextProfile.entityType);
    onSetAt?.(["ownership", "desktopClientHoldingKind"], nextProfile.holdingKind);
    onSetAt?.(["ownership", "shareholders"], directShareholderRows);
    onSetAt?.(["ownership", "ubos"], nextUbos);
    onSetAt?.(["ownership", "legalArrangements"], { arrangements: nextLegalArrangements });
    onSetAt?.(["ownership", "assistantTree"], snapshot);
    onSetAt?.(["ownership", "assistantGenerated"], true);
    setMobileEntryMode("assistant");
  }

  function setAccountHolderTrustKind(value) {
    if (readOnly) return;
    const nextValue = value === "trustee" ? "trustee" : "trust";
    onSetAt?.(["ownership", "accountHolderTrustNameKind"], nextValue);
    const currentItem =
      accountHolderArrangementItem ||
      arrangementToMobileHolder(makeAccountHolderArrangement("Trust"));
    const nextItem = applyAccountHolderTrustResolutionToMobileItem(
      currentItem,
      nextValue,
      entityName
    );
    upsertAccountHolderArrangement(nextItem);
  }

  function getActiveCountrySelectedValue() {
    if (!activeCountryField?.fieldKey) return "";
    if (activeCountryField?.listKey === "assistantCurrentNode") {
      return String(assistantCurrentNode?.[activeCountryField.fieldKey] || "").trim();
    }
    if (activeCountryField?.partyCollection === "signatories") {
      const activeItem = signatories?.[activeCountryField.index] || {};
      return String(activeItem?.[activeCountryField.fieldKey] || "").trim();
    }
    if (activeCountryField?.listKey === "ubos") {
      if (activeCountryField?.partyId) {
        const activeHolder = ubos?.[activeCountryField.index] || {};
        const collectionKey = activeCountryField.partyCollection || "trustParties";
        const parties = Array.isArray(activeHolder?.[collectionKey])
          ? activeHolder[collectionKey]
          : [];
        const activeParty = parties.find(
          (party) =>
            String(party?._rowId || "") === String(activeCountryField.partyId || "")
        );
        return String(activeParty?.[activeCountryField.fieldKey] || "").trim();
      }
      const activeItem = ubos?.[activeCountryField?.index] || {};
      return String(activeItem?.[activeCountryField.fieldKey] || "").trim();
    }
    if (activeCountryField?.partyId) {
      const activeHolder = visibleShareholders?.[activeCountryField.index] || {};
      const collectionKey = activeCountryField.partyCollection || "trustParties";
      const parties = Array.isArray(activeHolder?.[collectionKey])
        ? activeHolder[collectionKey]
        : [];
      const activeParty = parties.find(
        (party) =>
          String(party?._rowId || "") === String(activeCountryField.partyId || "")
      );
      return String(activeParty?.[activeCountryField.fieldKey] || "").trim();
    }
    const activeItem = visibleShareholders?.[activeCountryField?.index] || {};
    return String(activeItem?.[activeCountryField.fieldKey] || "").trim();
  }

function formatCountryShort(value) {
  const code = String(value || "")
    .trim()
    .toUpperCase();
  if (!code) return "Choose";
  return toIso3(code) || code;
}

function formatCountryFull(value) {
  const code = String(value || "")
    .trim()
    .toUpperCase();
  if (!code) return "Choose";
  const match = COUNTRY_OPTIONS.find(
    (opt) => String(opt?.value || "").trim().toUpperCase() === code
  );
  return match?.label || formatCountryShort(code);
}

function mobileArrangementHasVisibleDetails(item) {
  if (!item || typeof item !== "object") return false;
  if (String(item?.name || "").trim()) return true;
  const trustParties = Array.isArray(item?.trustParties) ? item.trustParties : [];
  const foundationParties = Array.isArray(item?.foundationParties)
    ? item.foundationParties
    : [];
  return [...trustParties, ...foundationParties].some((party) =>
    String(party?.name || "").trim()
  );
}

function isTrustPartyComplete(party) {
  const role = String(party?.role || "").trim();
  const name = String(party?.name || "").trim();
  const trusteeKind = String(party?.trusteeKind || "").trim().toLowerCase();
  const nationality = String(party?.nationality || "").trim();
  const domicile = String(party?.domicile || "").trim();
  if (!name) return false;
  if (role === "Trustee") {
    if (!["individual", "entity"].includes(trusteeKind)) return false;
    if (trusteeKind === "entity") return !!domicile;
    return !!nationality && !!domicile;
  }
  return !!nationality && !!domicile;
}

function isTrustHolderComplete(item) {
  if (!String(item?.name || "").trim()) return false;
  const parties = Array.isArray(item?.trustParties) ? item.trustParties : [];
  if (!parties.length) return false;
  return parties.every(isTrustPartyComplete);
}

function isFoundationPartyComplete(party) {
  const role = String(party?.role || "").trim();
  const name = String(party?.name || "").trim();
  const nationality = String(party?.nationality || "").trim();
  const domicile = String(party?.domicile || "").trim();
  const councilKind = String(party?.councilKind || "")
    .trim()
    .toLowerCase();

  if (!name) return false;
  if (role === "Council member") {
    if (!["individual", "entity"].includes(councilKind)) return false;
    if (councilKind === "entity") return !!domicile;
    return !!nationality && !!domicile;
  }
  return !!nationality && !!domicile;
}

function isFoundationHolderComplete(item) {
  if (!String(item?.name || "").trim()) return false;
  const parties = Array.isArray(item?.foundationParties)
    ? item.foundationParties
    : [];
  if (!parties.length) return false;
  return parties.every(isFoundationPartyComplete);
}

  function openShareholderCountrySheet(
    index,
    fieldKey,
    title,
    partyId = "",
    partyCollection = "",
    listKey = "shareholders"
  ) {
    if (readOnly) return;
    setCountrySearch("");
    setActiveCountryField({
      index,
      fieldKey,
      title,
      partyId,
      partyCollection,
      listKey,
    });
    setCountrySheetOpen(true);
  }

  function openAssistantCurrentCountrySheet(fieldKey, title) {
    if (readOnly || !assistantCurrentNode) return;
    setCountrySearch("");
    setActiveCountryField({
      index: -1,
      fieldKey,
      title,
      partyId: "",
      partyCollection: "",
      listKey: "assistantCurrentNode",
    });
    setCountrySheetOpen(true);
  }

  function closeCountrySheet() {
    setCountrySheetOpen(false);
    setActiveCountryField(null);
  }

  function openShareholderTypeSheet(index, listKey = "shareholders") {
    if (readOnly) return;
    setPendingAddListKey("");
    setActiveTypeIndex(index);
    setActiveTypeListKey(listKey);
    setTypeSheetOpen(true);
  }

  function openAddTypeSheet(listKey = "shareholders") {
    if (readOnly) return;
    setPendingAddListKey(listKey);
    setActiveTypeIndex(null);
    setActiveTypeListKey(listKey);
    setTypeSheetOpen(true);
  }

  function closeTypeSheet() {
    setTypeSheetOpen(false);
    setActiveTypeIndex(null);
    setActiveTypeListKey("shareholders");
    setPendingAddListKey("");
  }

  function openSignatoryCapacityModal(index) {
    if (readOnly) return;
    setSignatoryCapacityModal({ open: true, index });
  }

  function openTrusteeKindModal(
    index,
    partyId,
    listKey = "shareholders",
    partyCollection = "trustParties",
    kindField = "trusteeKind",
    title = "Select trustee type"
  ) {
    if (readOnly) return;
    setTrusteeKindModal({
      open: true,
      index,
      listKey,
      partyId: String(partyId || ""),
      partyCollection,
      kindField,
      title,
      focusNameAfterSelect: false,
    });
  }

  function openTrusteeKindModalFromNameField(
    index,
    partyId,
    listKey = "shareholders",
    partyCollection = "trustParties",
    kindField = "trusteeKind",
    title = "Select trustee type"
  ) {
    if (readOnly) return;
    setTrusteeKindModal({
      open: true,
      index,
      listKey,
      partyId: String(partyId || ""),
      partyCollection,
      kindField,
      title,
      focusNameAfterSelect: true,
    });
  }

  function closeTrusteeKindModal() {
    setTrusteeKindModal({
      open: false,
      index: -1,
      listKey: "shareholders",
      partyId: "",
      partyCollection: "trustParties",
      kindField: "trusteeKind",
      title: "Select trustee type",
      focusNameAfterSelect: false,
    });
  }

  function applyTrusteeKind(value) {
    const index = trusteeKindModal.index;
    const listKey = trusteeKindModal.listKey || "shareholders";
    const partyId = String(trusteeKindModal.partyId || "");
    const partyCollection = trusteeKindModal.partyCollection || "trustParties";
    const kindField = trusteeKindModal.kindField || "trusteeKind";
    const shouldFocusNameAfterSelect = !!trusteeKindModal.focusNameAfterSelect;
    if (index == null || index < 0 || !partyId) return;
    updateHolderPartyAt(
      listKey,
      index,
      partyCollection,
      partyId,
      kindField,
      value || ""
    );
    closeTrusteeKindModal();
    if (shouldFocusNameAfterSelect) {
      const refKey = `${index}:${partyId}`;
      suppressTrusteeNameModalRef.current = refKey;
      window.setTimeout(() => {
        const input = trusteeNameInputRefs.current?.[refKey];
        input?.focus?.();
      }, 0);
    }
  }

  function closeSignatoryCapacityModal() {
    setSignatoryCapacityModal({ open: false, index: -1 });
  }

  function openBasisOfControlModal(index) {
    if (readOnly) return;
    setBasisOfControlModal({ open: true, index });
  }

  function closeBasisOfControlModal() {
    setBasisOfControlModal({ open: false, index: -1 });
  }

  function applySignatoryCapacity(value) {
    if (signatoryCapacityModal.index == null || signatoryCapacityModal.index < 0)
      return;
    updateItem("signatories", signatoryCapacityModal.index, {
      capacity: normalizeSignatoryCapacity(value),
    });
    closeSignatoryCapacityModal();
  }

  function applyBasisOfControl(value) {
    if (basisOfControlModal.index == null || basisOfControlModal.index < 0) return;
    updateItem("ubos", basisOfControlModal.index, {
      basisOfControl: value || "",
    });
    closeBasisOfControlModal();
  }

  function confirmBasisOfControlSelection() {
    const currentValue = String(
      ubos?.[basisOfControlModal.index]?.basisOfControl || ""
    ).trim();
    applyBasisOfControl(currentValue || "Indirect ownership");
  }

  function applyShareholderType(nextType) {
    const normalizedType = normalizeHolderType(nextType);
    if (activeTypeIndex == null) {
      if (!pendingAddListKey) return;
      if (pendingAddListKey === "shareholders") {
        const nextIndex = shareholders.length;
        const nextItem = {
          ...emptyShareholder(),
          holderType: normalizedType,
          ...(normalizedType === "trust"
            ? { trustParties: makeDefaultTrustParties() }
            : {}),
          ...(normalizedType === "foundation"
            ? { foundationParties: makeDefaultFoundationParties() }
            : {}),
        };
        addItem("shareholders", () => nextItem);
        if (["trust", "foundation"].includes(normalizedType)) {
          const nextArrangement = makeLinkedArrangement({
            type: normalizedType === "foundation" ? "Foundation" : "Trust",
            source: "shareholder",
            sourceRefRowId: String(nextItem?._rowId || ""),
            relatedShareholderName: String(nextItem?.name || ""),
            autoCreated: true,
            ...nameFields(nextItem),
            name: String(nextItem?.name || ""),
            parties:
              normalizedType === "foundation"
                ? nextItem.foundationParties
                : nextItem.trustParties,
          });
          upsertLegalArrangement(
            (arrangement) =>
              String(arrangement?.source || "") === "shareholder" &&
              String(arrangement?.sourceRefRowId || "") ===
                String(nextItem?._rowId || ""),
            nextArrangement
          );
        }
        scheduleCarouselJump("shareholders", nextIndex);
      } else if (pendingAddListKey === "ubos") {
        closeTypeSheet();
        if (!eligibleControllingEntities.length) return;
        setControllingEntityModalMode(normalizedType);
        setControllingEntityModalOpen(true);
        return;
      }
      closeTypeSheet();
      return;
    }
    const currentList = activeTypeListKey === "ubos" ? ubos : shareholders;
    const current = currentList?.[activeTypeIndex] || {};
    const previousType = normalizeHolderType(current?.holderType);
    const nextItem = {
      ...(current || {}),
      holderType: normalizedType,
      ...(normalizedType === "trust" &&
      !Array.isArray(current?.trustParties)
        ? { trustParties: makeDefaultTrustParties() }
        : {}),
      ...(normalizedType === "foundation" &&
      !Array.isArray(current?.foundationParties)
        ? { foundationParties: makeDefaultFoundationParties() }
        : {}),
    };
    updateItem(activeTypeListKey, activeTypeIndex, nextItem);
    if (
      activeTypeListKey === "shareholders" &&
      ["trust", "foundation"].includes(normalizedType)
    ) {
      const rowId = String(nextItem?._rowId || "").trim();
      const nextArrangement = makeLinkedArrangement({
        type: normalizedType === "foundation" ? "Foundation" : "Trust",
        source: "shareholder",
        sourceRefRowId: rowId,
        relatedShareholderName: String(nextItem?.name || ""),
        autoCreated: true,
        ...nameFields(nextItem),
        name: String(nextItem?.name || ""),
        parties:
          normalizedType === "foundation"
            ? nextItem.foundationParties
            : nextItem.trustParties,
      });
      upsertLegalArrangement(
        (arrangement) =>
          String(arrangement?.source || "") === "shareholder" &&
          String(arrangement?.sourceRefRowId || "") === rowId,
        nextArrangement
      );
    }
    if (
      activeTypeListKey === "shareholders" &&
      ["trust", "foundation"].includes(previousType) &&
      !["trust", "foundation"].includes(normalizedType)
    ) {
      const rowId = String(current?._rowId || "").trim();
      if (rowId) {
        setLegalArrangements(
          legalArrangements.filter(
            (arrangement) =>
              !(
                String(arrangement?.source || "") === "shareholder" &&
                String(arrangement?.sourceRefRowId || "") === rowId
              )
          )
        );
      }
    }
    setTypeSheetOpen(false);
    setActiveTypeIndex(null);
    setActiveTypeListKey("shareholders");
    setPendingAddListKey("");
  }

  function openOwnershipModal(index) {
    if (readOnly) return;
    const initialDraft = sanitizePercent(shareholders?.[index]?.ownershipPercent || "");
    setActiveOwnershipIndex(index);
    setActiveOwnershipTarget({
      mode: "shareholder",
      label: "OWNERSHIP %",
    });
    setOwnershipDraft(initialDraft);
    setOwnershipCursorIndex(initialDraft === "10000" ? null : 0);
    ownershipWholeSeedRef.current = "";
    ownershipWholeGroupActiveRef.current = false;
    setOwnershipModalOpen(true);
  }

  function openAssistantDirectCountModal() {
    if (readOnly) return;
    setActiveOwnershipIndex(null);
    setActiveOwnershipTarget({
      mode: "assistantDirectCount",
      label: "DIRECT SHAREHOLDERS",
    });
    setOwnershipDraft(String(assistantDirectCount || "").replace(/\D/g, "").slice(0, 2));
    setOwnershipCursorIndex(null);
    ownershipWholeSeedRef.current = "";
    ownershipWholeGroupActiveRef.current = false;
    setOwnershipModalOpen(true);
  }

  function openAssistantCurrentOwnersCountModal() {
    if (readOnly || !assistantCurrentNode) return;
    setActiveOwnershipIndex(null);
    setActiveOwnershipTarget({
      mode: "assistantCurrentOwnersCount",
      label: "NUMBER OF SHAREHOLDERS",
    });
    setOwnershipDraft(String(assistantCurrentNode?.ownersCount || "").replace(/\D/g, "").slice(0, 2));
    setOwnershipCursorIndex(null);
    ownershipWholeSeedRef.current = "";
    ownershipWholeGroupActiveRef.current = false;
    setOwnershipModalOpen(true);
  }

  function openAssistantCurrentOwnershipModal() {
    if (readOnly || !assistantCurrentNode) return;
    setActiveOwnershipIndex(null);
    setActiveOwnershipTarget({
      mode: "assistantCurrentOwnership",
      label: "OWNERSHIP %",
    });
    const initialDraft = sanitizePercent(
      String(assistantCurrentNode?.ownershipPercent || "")
    );
    setOwnershipDraft(initialDraft);
    setOwnershipCursorIndex(initialDraft === "10000" ? null : 0);
    ownershipWholeSeedRef.current = "";
    ownershipWholeGroupActiveRef.current = false;
    setOwnershipModalOpen(true);
  }

  function closeOwnershipModal() {
    setOwnershipModalOpen(false);
    setActiveOwnershipIndex(null);
    setActiveOwnershipTarget({
      mode: "shareholder",
      label: "OWNERSHIP %",
    });
    setOwnershipDraft("");
    setOwnershipCursorIndex(null);
    ownershipWholeSeedRef.current = "";
    ownershipWholeGroupActiveRef.current = false;
  }

  function applyOwnershipDraft() {
    if (activeOwnershipTarget.mode === "assistantDirectCount") {
      const nextCountDraft = String(ownershipDraft || "").replace(/\D/g, "").slice(0, 2);
      const nextCount = Math.max(0, Number.parseInt(nextCountDraft, 10) || 0);
      setAssistantDirectCount(nextCountDraft);
      const nextRoots = Array.from({ length: nextCount }, () => ({
        ...makeAssistantOwnerNode(),
        ownershipPercent: nextCount === 1 && assistantPercentShouldApply(assistantPreliminaryMode) ? "10000" : "",
      }));
      updateMobileAssistantDraft((prev) => ({
        ...(prev || {}),
        directCount: String(nextCount),
        roots: nextRoots,
      }));
      closeOwnershipModal();
      return;
    }
    if (activeOwnershipTarget.mode === "assistantCurrentOwnersCount") {
      const nextCountDraft = String(ownershipDraft || "").replace(/\D/g, "").slice(0, 2);
      updateAssistantCurrentNode({ ownersCount: nextCountDraft });
      closeOwnershipModal();
      return;
    }
    if (activeOwnershipTarget.mode === "assistantCurrentOwnership") {
      updateAssistantCurrentNode({
        ownershipPercent: normalizePercentForSave(ownershipDraft),
      });
      closeOwnershipModal();
      return;
    }
    if (activeOwnershipIndex == null) return;
    updateItem("shareholders", activeOwnershipIndex, {
      ownershipPercent: normalizePercentForSave(ownershipDraft),
    });
    closeOwnershipModal();
  }

  function openTrustEditModal(index, listKey = "shareholders") {
    const snapshot =
      listKey === "assistantCurrentNode"
        ? {
            listKey,
            index,
            path: Array.isArray(assistantCurrentPath) ? [...assistantCurrentPath] : [],
            item: cloneModalDraft(assistantCurrentNode),
          }
        : null;
    trustEditSnapshotRef.current = snapshot;
    setTrustEditModal({ open: true, index, listKey });
  }

  function closeTrustEditModal(saved = false) {
    const shouldSave = saved === true;
    const snapshot = trustEditSnapshotRef.current;
    if (
      !shouldSave &&
      snapshot?.listKey === "assistantCurrentNode" &&
      Array.isArray(snapshot.path) &&
      snapshot.path.length
    ) {
      updateMobileAssistantDraft((prev) => ({
        ...(prev || {}),
        roots: updateAssistantNodeAtPath(
          cloneAssistantNodes(prev?.roots || []),
          snapshot.path,
          (node) => ({
            ...(node || {}),
            ...(snapshot.item || {}),
            owners: cloneAssistantNodes((snapshot.item || node)?.owners),
          })
        ),
      }));
    }
    if (
      shouldSave &&
      mobileEntryMode === "assistant" &&
      trustEditModal.listKey === "accountHolderArrangement" &&
      (assistantPreliminaryMode === "trust" ||
        assistantPreliminaryMode === "foundation")
    ) {
      setAssistantArrangementSummaryVisible(true);
    }
    if (shouldSave && trustEditModal.listKey === "assistantCurrentNode") {
      trustEditSnapshotRef.current = null;
      setTrustEditModal({ open: false, index: -1, listKey: "shareholders" });
      advanceAssistantCurrentNode();
      return;
    }
    trustEditSnapshotRef.current = null;
    setTrustEditModal({ open: false, index: -1, listKey: "shareholders" });
  }

  function handleAccountHolderTrustNameKindSelection(nextValue) {
    setAssistantArrangementSummaryVisible(false);
    setAccountHolderTrustKind(nextValue);
    openTrustEditModal(0, "accountHolderArrangement");
  }

  function getListItemByKey(listKey, index) {
    if (listKey === "accountHolderArrangement") return accountHolderArrangementItem;
    if (listKey === "assistantCurrentNode") {
      const modalPath = trustEditSnapshotRef.current?.path;
      const node = Array.isArray(modalPath) && modalPath.length
        ? getAssistantNodeAtPath(assistantRoots, modalPath)
        : assistantCurrentNode;
      if (!node) return null;
      return {
        ...node,
        holderType: node.type,
        trustParties: Array.isArray(node.trustParties) ? node.trustParties : makeDefaultTrustParties(),
        foundationParties: Array.isArray(node.foundationParties) ? node.foundationParties : makeDefaultFoundationParties(),
      };
    }
    if (listKey === "ubos") return ubos?.[index] || {};
    return visibleShareholders?.[index] || {};
  }

  function updateHolderAt(listKey, index, patch) {
    if (listKey === "accountHolderArrangement") {
      updateAccountHolderArrangement(patch);
      return;
    }
    if (listKey === "assistantCurrentNode") {
      updateAssistantNodeAtModalPath(patch);
      return;
    }
    if (listKey === "ubos") {
      updateItem("ubos", index, patch);
      return;
    }
    const current = getListItemByKey(listKey, index);
    const holderType = normalizeHolderType(current?.holderType);
    updateItem("shareholders", index, patch);
    if (["trust", "foundation"].includes(holderType)) {
      upsertShareholderArrangement(index, {
        ...(current || {}),
        ...(patch || {}),
      });
    }
  }

  function updateHolderPartyAt(listKey, index, partyCollection, partyId, key, value) {
    if (listKey === "accountHolderArrangement") {
      updateAccountHolderArrangementParty(partyCollection, partyId, key, value);
      return;
    }
    if (listKey === "assistantCurrentNode") {
      const node = getListItemByKey(listKey, index);
      const fallback = partyCollection === "foundationParties" ? makeDefaultFoundationParties() : makeDefaultTrustParties();
      const parties = Array.isArray(node?.[partyCollection]) ? node[partyCollection] : fallback;
      const nextParties = parties.map((party) =>
        String(party?._rowId || "") === String(partyId) ? { ...(party || {}), ...(typeof key === "object" ? key : { [key]: value }) } : party
      );
      updateAssistantNodeAtModalPath({ [partyCollection]: nextParties });
      return;
    }
    const current = getListItemByKey(listKey, index);
    const fallback =
      partyCollection === "foundationParties"
        ? makeDefaultFoundationParties()
        : makeDefaultTrustParties();
    const parties = Array.isArray(current?.[partyCollection])
      ? current[partyCollection]
      : fallback;
    const nextParties = parties.map((party) =>
      String(party?._rowId || "") === String(partyId)
        ? { ...(party || {}), ...(typeof key === "object" ? key : { [key]: value }) }
        : party
    );
    if (listKey === "ubos") {
      updateItem("ubos", index, { [partyCollection]: nextParties });
      return;
    }
    upsertShareholderArrangement(index, { [partyCollection]: nextParties });
  }

  function removeHolderPartyAt(listKey, index, partyCollection, partyId) {
    if (listKey === "accountHolderArrangement") {
      removeAccountHolderArrangementParty(partyCollection, partyId);
      return;
    }
    if (listKey === "assistantCurrentNode") {
      const node = getListItemByKey(listKey, index);
      const parties = Array.isArray(node?.[partyCollection]) ? node[partyCollection] : [];
      updateAssistantNodeAtModalPath({ [partyCollection]: parties.filter((p) => String(p?._rowId || "") !== String(partyId)) });
      return;
    }
    const current = getListItemByKey(listKey, index);
    const parties = Array.isArray(current?.[partyCollection])
      ? current[partyCollection]
      : [];
    const nextParties = parties.filter(
      (party) => String(party?._rowId || "") !== String(partyId)
    );
    if (listKey === "ubos") {
      updateItem("ubos", index, { [partyCollection]: nextParties });
      return;
    }
    upsertShareholderArrangement(index, { [partyCollection]: nextParties });
  }

  function addHolderPartyAt(listKey, index, partyCollection, role) {
    if (listKey === "accountHolderArrangement") {
      addAccountHolderArrangementParty(partyCollection, role);
      return;
    }
    if (listKey === "assistantCurrentNode") {
      const node = getListItemByKey(listKey, index);
      const parties = Array.isArray(node?.[partyCollection]) ? node[partyCollection] : [];
      const newParty = partyCollection === "foundationParties" ? makeFoundationParty(role) : makeTrustParty(role);
      updateAssistantNodeAtModalPath({ [partyCollection]: [...parties, newParty] });
      return;
    }
    const current = getListItemByKey(listKey, index);
    const parties = Array.isArray(current?.[partyCollection])
      ? current[partyCollection]
      : [];
    const newParty =
      partyCollection === "foundationParties"
        ? makeFoundationParty(role)
        : makeTrustParty(role);
    if (listKey === "ubos") {
      updateItem("ubos", index, { [partyCollection]: [...parties, newParty] });
      return;
    }
    upsertShareholderArrangement(index, { [partyCollection]: [...parties, newParty] });
  }

  function toggleHolderOptionalPartyAt(listKey, index, partyCollection, role) {
    if (listKey === "accountHolderArrangement") {
      if (partyCollection === "foundationParties") {
        toggleAccountHolderFoundationGuardian();
      } else {
        toggleAccountHolderTrustProtector();
      }
      return;
    }
    if (listKey === "assistantCurrentNode") {
      const node = getListItemByKey(listKey, index);
      const fallback = partyCollection === "foundationParties" ? makeDefaultFoundationParties() : makeDefaultTrustParties();
      const parties = Array.isArray(node?.[partyCollection]) ? node[partyCollection] : fallback;
      const hasRole = parties.some((p) => String(p?.role || "") === role);
      const newParty = partyCollection === "foundationParties" ? makeFoundationParty(role) : makeTrustParty(role);
      updateAssistantNodeAtModalPath({
        [partyCollection]: hasRole ? parties.filter((p) => String(p?.role || "") !== role) : [...parties, newParty],
      });
      return;
    }
    const current = getListItemByKey(listKey, index);
    const fallback =
      partyCollection === "foundationParties"
        ? makeDefaultFoundationParties()
        : makeDefaultTrustParties();
    const parties = Array.isArray(current?.[partyCollection])
      ? current[partyCollection]
      : fallback;
    const hasRole = parties.some((party) => String(party?.role || "") === role);
    const nextParties = hasRole
      ? parties.filter((party) => String(party?.role || "") !== role)
      : [
          ...parties,
          partyCollection === "foundationParties"
            ? makeFoundationParty(role)
            : makeTrustParty(role),
        ];
    if (listKey === "ubos") {
      updateItem("ubos", index, { [partyCollection]: nextParties });
      return;
    }
    upsertShareholderArrangement(index, { [partyCollection]: nextParties });
  }

  function getRelatedEntityLabel(rowId) {
    const key = String(rowId || "").trim();
    if (!key) return "Related entity";
    const match = eligibleControllingEntities.find(
      (item) => String(item?.rowId || "") === key
    );
    return match?.name || "Related entity";
  }

  function closeControllingEntityModal() {
    setControllingEntityModalOpen(false);
    setControllingEntityModalMode("individual");
  }

  function addControllingPersonForEntity(rowId) {
    const current = Array.isArray(ownership.ubos) ? ownership.ubos : [];
    const holderType = ["trust", "foundation"].includes(controllingEntityModalMode)
      ? controllingEntityModalMode
      : "individual";
    setList("ubos", [
      ...current,
      {
        ...emptyUbo(),
        holderType,
        ...(holderType === "trust"
          ? { trustParties: makeDefaultTrustParties() }
          : {}),
        ...(holderType === "foundation"
          ? { foundationParties: makeDefaultFoundationParties() }
          : {}),
        relatedShareholderRowId: String(rowId || ""),
      },
    ]);
    setPendingCarouselTarget({ listKey: "ubos", index: current.length });
    setControllingEntityModalOpen(false);
    setControllingEntityModalMode("individual");
  }

  function handleOwnershipKeypadInput(key) {
    const isCountMode = activeOwnershipTarget.mode === "assistantDirectCount" || activeOwnershipTarget.mode === "assistantCurrentOwnersCount";
    const clampPercentDraft = (nextDraft) => {
      if (isCountMode) return nextDraft;
      const nextNumber = toPercentNumber(nextDraft);
      if (nextNumber <= maxOwnershipAllowed) return nextDraft;
      return maxOwnershipAllowed >= 100
        ? "10000"
        : String(Math.round(maxOwnershipAllowed * 100)).padStart(4, "0");
    };
    if (isCountMode && key === "backspace") {
      setOwnershipDraft((prev) =>
        String(prev || "")
          .replace(/\D/g, "")
          .slice(0, -1)
      );
      return;
    }
    if (key === "clear") {
      setOwnershipDraft("");
      setOwnershipCursorIndex(isCountMode ? null : 0);
      ownershipWholeSeedRef.current = "";
      ownershipWholeGroupActiveRef.current = false;
      return;
    }

    if (key === ".") {
      if (isCountMode) return;
      ownershipWholeSeedRef.current = "";
      ownershipWholeGroupActiveRef.current = false;
      setOwnershipCursorIndex((prev) => {
        if (prev == null) return 2;
        return Math.max(prev, 2);
      });
      return;
    }

    if (
      !isCountMode &&
      ownershipWholeSeedRef.current &&
      ownershipCursorIndex === 1
    ) {
      const seed = ownershipWholeSeedRef.current;
      setOwnershipDraft(clampPercentDraft(`${seed}${key}00`));
      setOwnershipCursorIndex(2);
      ownershipWholeSeedRef.current = "";
      ownershipWholeGroupActiveRef.current = false;
      return;
    }

    if (
      !isCountMode &&
      ownershipCursorIndex === 0 &&
      !sanitizePercent(ownershipDraft)
    ) {
      setOwnershipDraft(clampPercentDraft(`0${key}00`));
      setOwnershipCursorIndex(1);
      ownershipWholeSeedRef.current = key;
      ownershipWholeGroupActiveRef.current = true;
      return;
    }

    setOwnershipDraft((prev) => {
      const cleaned = isCountMode
        ? String(prev || "").replace(/\D/g, "").slice(0, 2)
        : sanitizePercent(prev);
      const cursor = ownershipCursorIndex;
      const maxLength = isCountMode ? 2 : 4;

      if (cursor != null && cursor >= 0 && cursor < maxLength) {
        const padded = cleaned.padEnd(maxLength, "0").split("");
        padded[cursor] = key;
        return isCountMode
          ? padded.join("").slice(0, maxLength)
          : clampPercentDraft(padded.join(""));
      }

      if (cleaned.length >= maxLength) return cleaned;
      return clampPercentDraft(`${cleaned}${key}`);
    });
    ownershipWholeSeedRef.current = "";
    ownershipWholeGroupActiveRef.current = false;
    setOwnershipCursorIndex((prev) => {
      if (prev == null) return null;
      return Math.min(prev + 1, isCountMode ? 2 : 4);
    });
  }

  function scrollToTrustEditorParty(listKey, index, role) {
    const refKey = `${String(listKey || "")}:${String(index)}:${String(role || "")}`;
    const run = () => {
      const node = trustEditorPartyRefs.current?.[refKey];
      if (!node) return;
      const scrollContainer = node.closest(".kycMobileTrustEditorModalBody");
      if (scrollContainer) {
        const nodeRect = node.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const nextTop =
          scrollContainer.scrollTop + (nodeRect.top - containerRect.top) - 8;
        scrollContainer.scrollTo({
          top: Math.max(0, nextTop),
          behavior: "smooth",
        });
        return;
      }
      node.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    };
    window.requestAnimationFrame(run);
    window.setTimeout(run, 120);
    window.setTimeout(run, 260);
  }

  function addTrustProtectorAndScroll(listKey, index) {
    toggleHolderOptionalPartyAt(listKey, index, "trustParties", "Protector");
    scrollToTrustEditorParty(listKey, index, "Protector");
  }

  function applyShareholderCountry(countryCode) {
    if (!activeCountryField) return;
    if (activeCountryField.listKey === "assistantCurrentNode") {
      if (activeCountryField.partyId) {
        const node = assistantCurrentNode;
        const collectionKey = activeCountryField.partyCollection || "trustParties";
        const fallback = collectionKey === "foundationParties" ? makeDefaultFoundationParties() : makeDefaultTrustParties();
        const parties = Array.isArray(node?.[collectionKey]) ? node[collectionKey] : fallback;
        updateAssistantCurrentNode({
          [collectionKey]: parties.map((party) =>
            String(party?._rowId || "") === String(activeCountryField.partyId)
              ? { ...(party || {}), [activeCountryField.fieldKey]: countryCode || "" }
              : party
          ),
        });
      } else {
        updateAssistantCurrentNode({ [activeCountryField.fieldKey]: countryCode || "" });
      }
      setCountrySheetOpen(false);
      setActiveCountryField(null);
      return;
    }
    if (activeCountryField.partyCollection === "signatories") {
      updateItem("signatories", activeCountryField.index, {
        [activeCountryField.fieldKey]: countryCode || "",
      });
      setCountrySheetOpen(false);
      setActiveCountryField(null);
      return;
    }

    if (activeCountryField.listKey === "ubos") {
      if (activeCountryField.partyId) {
        const current = ubos?.[activeCountryField.index] || {};
        const collectionKey = activeCountryField.partyCollection || "trustParties";
        const fallback =
          collectionKey === "foundationParties"
            ? makeDefaultFoundationParties()
            : makeDefaultTrustParties();
        const parties = Array.isArray(current?.[collectionKey])
          ? current[collectionKey]
          : fallback;
        updateItem("ubos", activeCountryField.index, {
          [collectionKey]: parties.map((party) =>
            String(party?._rowId || "") === String(activeCountryField.partyId)
              ? { ...(party || {}), [activeCountryField.fieldKey]: countryCode || "" }
              : party
          ),
        });
      } else {
        updateItem("ubos", activeCountryField.index, {
          [activeCountryField.fieldKey]: countryCode || "",
        });
      }
      setCountrySheetOpen(false);
      setActiveCountryField(null);
      return;
    }

    if (activeCountryField.listKey === "accountHolderArrangement") {
      if (activeCountryField.partyId) {
        updateAccountHolderArrangementParty(
          activeCountryField.partyCollection || "trustParties",
          activeCountryField.partyId,
          activeCountryField.fieldKey,
          countryCode || ""
        );
      } else {
        updateAccountHolderArrangement({
          [activeCountryField.fieldKey]: countryCode || "",
        });
      }
      setCountrySheetOpen(false);
      setActiveCountryField(null);
      return;
    }

    if (activeCountryField.partyId) {
      const current = visibleShareholders?.[activeCountryField.index] || {};
      const collectionKey = activeCountryField.partyCollection || "trustParties";
      const parties = Array.isArray(current?.[collectionKey])
        ? current[collectionKey]
        : [];
      const nextParties = parties.map((party) =>
        String(party?._rowId || "") === String(activeCountryField.partyId)
          ? { ...(party || {}), [activeCountryField.fieldKey]: countryCode || "" }
          : party
      );
      const holderType = normalizeHolderType(current?.holderType);
      if (["trust", "foundation"].includes(holderType)) {
        upsertShareholderArrangement(activeCountryField.index, {
          [collectionKey]: nextParties,
        });
      } else {
        updateItem("shareholders", activeCountryField.index, {
          [collectionKey]: nextParties,
        });
      }
    } else {
      updateItem("shareholders", activeCountryField.index, {
        [activeCountryField.fieldKey]: countryCode || "",
      });
    }
    setCountrySheetOpen(false);
    setActiveCountryField(null);
  }

  function updateShareholderTrustParty(index, partyId, key, value) {
    updateShareholderParty(index, "trustParties", partyId, key, value);
  }

  function updateShareholderParty(index, partyCollection, partyId, key, value) {
    if (isTrustOrFoundationAccountHolder) {
      updateAccountHolderArrangementParty(partyCollection, partyId, key, value);
      return;
    }
    const current = visibleShareholders?.[index] || {};
    const fallback =
      partyCollection === "foundationParties"
        ? makeDefaultFoundationParties()
        : makeDefaultTrustParties();
    const parties = Array.isArray(current?.[partyCollection])
      ? current[partyCollection]
      : fallback;
    const nextParties = parties.map((party) =>
      String(party?._rowId || "") === String(partyId)
        ? { ...(party || {}), ...(typeof key === "object" ? key : { [key]: value }) }
        : party
    );
    if (
      ["trust", "foundation"].includes(normalizeHolderType(current?.holderType))
    ) {
      upsertShareholderArrangement(index, {
        [partyCollection]: nextParties,
      });
      return;
    }
    updateItem("shareholders", index, {
      [partyCollection]: nextParties,
    });
  }

  function addShareholderTrustBeneficiary(index) {
    if (isTrustOrFoundationAccountHolder) {
      addAccountHolderArrangementParty("trustParties", "Beneficiary");
      return;
    }
    const current = visibleShareholders?.[index] || {};
    const trustParties = Array.isArray(current?.trustParties)
      ? current.trustParties
      : makeDefaultTrustParties();
    upsertShareholderArrangement(index, {
      trustParties: [...trustParties, makeTrustParty("Beneficiary")],
    });
  }

  function toggleShareholderTrustProtector(index) {
    if (isTrustOrFoundationAccountHolder) {
      toggleAccountHolderTrustProtector();
      return;
    }
    const current = visibleShareholders?.[index] || {};
    const trustParties = Array.isArray(current?.trustParties)
      ? current.trustParties
      : makeDefaultTrustParties();
    const hasProtector = trustParties.some(
      (party) => String(party?.role || "") === "Protector"
    );
    upsertShareholderArrangement(index, {
      trustParties: hasProtector
        ? trustParties.filter(
            (party) => String(party?.role || "") !== "Protector"
          )
        : [...trustParties, makeTrustParty("Protector")],
    });
  }

  function removeShareholderTrustParty(index, partyId) {
    removeShareholderParty(index, "trustParties", partyId);
  }

  function removeShareholderParty(index, partyCollection, partyId) {
    if (isTrustOrFoundationAccountHolder) {
      removeAccountHolderArrangementParty(partyCollection, partyId);
      return;
    }
    const current = visibleShareholders?.[index] || {};
    const parties = Array.isArray(current?.[partyCollection])
      ? current[partyCollection]
      : [];
    upsertShareholderArrangement(index, {
      [partyCollection]: parties.filter(
        (party) => String(party?._rowId || "") !== String(partyId)
      ),
    });
  }

  function addShareholderFoundationBeneficiary(index) {
    if (isTrustOrFoundationAccountHolder) {
      addAccountHolderArrangementParty("foundationParties", "Beneficiary");
      return;
    }
    const current = visibleShareholders?.[index] || {};
    const foundationParties = Array.isArray(current?.foundationParties)
      ? current.foundationParties
      : makeDefaultFoundationParties();
    upsertShareholderArrangement(index, {
      foundationParties: [...foundationParties, makeFoundationParty("Beneficiary")],
    });
  }

  function toggleShareholderFoundationGuardian(index) {
    if (isTrustOrFoundationAccountHolder) {
      toggleAccountHolderFoundationGuardian();
      return;
    }
    const current = visibleShareholders?.[index] || {};
    const foundationParties = Array.isArray(current?.foundationParties)
      ? current.foundationParties
      : makeDefaultFoundationParties();
    const hasGuardian = foundationParties.some(
      (party) => String(party?.role || "") === "Guardian"
    );
    upsertShareholderArrangement(index, {
      foundationParties: hasGuardian
        ? foundationParties.filter(
            (party) => String(party?.role || "") !== "Guardian"
          )
        : [...foundationParties, makeFoundationParty("Guardian")],
    });
  }

  function setList(listKey, next) {
    if (listKey === "shareholders") {
      const current = Array.isArray(ownership.shareholders)
        ? ownership.shareholders
        : [];
      if (didShareholderTreeFieldsChange(current, next)) {
        refreshAssistantSnapshot(next, ubos);
      }
    } else if (listKey === "ubos") {
      const current = Array.isArray(ownership.ubos) ? ownership.ubos : [];
      if (didUboTreeFieldsChange(current, next)) {
        refreshAssistantSnapshot(shareholders, next);
      }
    }
    onSetAt?.(["ownership", listKey], next);
  }

  function setLegalArrangements(nextArrangements) {
    onSetAt?.(["ownership", "legalArrangements"], {
      arrangements: nextArrangements || [],
    });
  }

  function upsertLegalArrangement(matchFn, nextArrangement) {
    const current = Array.isArray(legalArrangements) ? legalArrangements : [];
    const next = [...current];
    const idx = next.findIndex((arrangement) => matchFn(arrangement));
    if (idx >= 0) next[idx] = nextArrangement;
    else next.push(nextArrangement);
    setLegalArrangements(next);
  }

  function upsertShareholderArrangement(shareholderIndex, patch) {
    const baseShareholder = visibleShareholders?.[shareholderIndex] || {};
    const rowId = String(baseShareholder?._rowId || "").trim();
    const holderType = normalizeHolderType(baseShareholder?.holderType);
    if (!rowId || !["trust", "foundation"].includes(holderType)) return;
    const existingArrangement = shareholderArrangementMap[rowId];
    const nextMobileItem = {
      ...(baseShareholder || {}),
      ...(patch || {}),
    };
    const nextArrangement = {
      ...(existingArrangement ||
        makeLinkedArrangement({
          type: holderType === "foundation" ? "Foundation" : "Trust",
          source: "shareholder",
          sourceRefRowId: rowId,
          relatedShareholderName: String(nextMobileItem?.name || ""),
          autoCreated: true,
          ...nameFields(nextMobileItem),
          name: String(nextMobileItem?.name || ""),
          parties:
            holderType === "foundation"
              ? nextMobileItem?.foundationParties
              : nextMobileItem?.trustParties,
        })),
      arrangementType: holderType === "foundation" ? "Foundation" : "Trust",
      source: "shareholder",
      sourceRefRowId: rowId,
      relatedShareholderName: String(nextMobileItem?.name || ""),
      autoCreated: true,
      ...nameFields(nextMobileItem),
      name: String(nextMobileItem?.name || ""),
      parties:
        holderType === "foundation"
          ? Array.isArray(nextMobileItem?.foundationParties)
            ? nextMobileItem.foundationParties.map((party) => ({ ...(party || {}) }))
            : makeDefaultFoundationParties()
          : Array.isArray(nextMobileItem?.trustParties)
          ? nextMobileItem.trustParties.map((party) => ({ ...(party || {}) }))
          : makeDefaultTrustParties(),
    };

    upsertLegalArrangement(
      (arrangement) =>
        String(arrangement?.source || "") === "shareholder" &&
        String(arrangement?.sourceRefRowId || "") === rowId,
      nextArrangement
    );
  }

  function upsertAccountHolderArrangement(nextItem) {
    const nextArrangement = mobileHolderToArrangement(
      nextItem,
      accountHolderArrangement ||
        makeAccountHolderArrangement(
          isFoundationAccountHolder ? "Foundation" : "Trust"
        )
    );
    upsertLegalArrangement(
      (arrangement) =>
        String(arrangement?.source || "") === "account_holder" &&
        normalizeArrangementType(arrangement?.arrangementType) ===
          normalizeArrangementType(nextArrangement?.arrangementType),
      nextArrangement
    );
  }

  function updateAccountHolderArrangement(patch) {
    const currentItem =
      accountHolderArrangementItem ||
      arrangementToMobileHolder(
        makeAccountHolderArrangement(
          isFoundationAccountHolder ? "Foundation" : "Trust"
        )
      );
    upsertAccountHolderArrangement({ ...(currentItem || {}), ...(patch || {}) });
  }

  function updateAccountHolderArrangementParty(
    partyCollection,
    partyId,
    key,
    value
  ) {
    const currentItem =
      accountHolderArrangementItem ||
      arrangementToMobileHolder(
        makeAccountHolderArrangement(
          isFoundationAccountHolder ? "Foundation" : "Trust"
        )
      );
    const fallback =
      partyCollection === "foundationParties"
        ? makeDefaultFoundationParties()
        : makeDefaultTrustParties();
    const parties = Array.isArray(currentItem?.[partyCollection])
      ? currentItem[partyCollection]
      : fallback;
    upsertAccountHolderArrangement({
      ...(currentItem || {}),
      [partyCollection]: parties.map((party) =>
        String(party?._rowId || "") === String(partyId)
          ? { ...(party || {}), ...(typeof key === "object" ? key : { [key]: value }) }
          : party
      ),
    });
  }

  function removeAccountHolderArrangementParty(partyCollection, partyId) {
    const currentItem =
      accountHolderArrangementItem ||
      arrangementToMobileHolder(
        makeAccountHolderArrangement(
          isFoundationAccountHolder ? "Foundation" : "Trust"
        )
      );
    const parties = Array.isArray(currentItem?.[partyCollection])
      ? currentItem[partyCollection]
      : [];
    upsertAccountHolderArrangement({
      ...(currentItem || {}),
      [partyCollection]: parties.filter(
        (party) => String(party?._rowId || "") !== String(partyId)
      ),
    });
  }

  function addAccountHolderArrangementParty(partyCollection, role) {
    const currentItem =
      accountHolderArrangementItem ||
      arrangementToMobileHolder(
        makeAccountHolderArrangement(
          isFoundationAccountHolder ? "Foundation" : "Trust"
        )
      );
    const parties = Array.isArray(currentItem?.[partyCollection])
      ? currentItem[partyCollection]
      : [];
    const newParty =
      partyCollection === "foundationParties"
        ? makeFoundationParty(role)
        : makeTrustParty(role);
    upsertAccountHolderArrangement({
      ...(currentItem || {}),
      [partyCollection]: [...parties, newParty],
    });
  }

  function toggleAccountHolderTrustProtector() {
    const currentItem =
      accountHolderArrangementItem ||
      arrangementToMobileHolder(makeAccountHolderArrangement("Trust"));
    const trustParties = Array.isArray(currentItem?.trustParties)
      ? currentItem.trustParties
      : makeDefaultTrustParties();
    const hasProtector = trustParties.some(
      (party) => String(party?.role || "") === "Protector"
    );
    upsertAccountHolderArrangement({
      ...(currentItem || {}),
      trustParties: hasProtector
        ? trustParties.filter(
            (party) => String(party?.role || "") !== "Protector"
          )
        : [...trustParties, makeTrustParty("Protector")],
    });
  }

  function toggleAccountHolderFoundationGuardian() {
    const currentItem =
      accountHolderArrangementItem ||
      arrangementToMobileHolder(makeAccountHolderArrangement("Foundation"));
    const foundationParties = Array.isArray(currentItem?.foundationParties)
      ? currentItem.foundationParties
      : makeDefaultFoundationParties();
    const hasGuardian = foundationParties.some(
      (party) => String(party?.role || "") === "Guardian"
    );
    upsertAccountHolderArrangement({
      ...(currentItem || {}),
      foundationParties: hasGuardian
        ? foundationParties.filter(
            (party) => String(party?.role || "") !== "Guardian"
          )
        : [...foundationParties, makeFoundationParty("Guardian")],
    });
  }

  function updateItem(listKey, index, patch) {
    const current = Array.isArray(ownership[listKey]) ? ownership[listKey] : [];
    const next = current.map((item, i) =>
      i === index ? { ...(item || {}), ...(patch || {}) } : item
    );
    // A signatory linked to a shareholder / controlling person is the same person: a change to the name,
    // nationality or domicile on either row reaches the other one.
    const edited = editLinkedPerson(ownership, listKey, index, patch || {});
    ["shareholders", "ubos", "signatories"].forEach((key) => {
      if (key !== listKey && JSON.stringify(edited[key]) !== JSON.stringify(ownership[key])) setList(key, edited[key]);
    });
    setList(listKey, next);
  }

  function addItem(listKey, factory) {
    const current = Array.isArray(ownership[listKey]) ? ownership[listKey] : [];
    setList(listKey, [...current, factory()]);
    setPendingCarouselTarget({ listKey, index: current.length });
  }

  function getItemDeleteKey(listKey, item, index) {
    return `${listKey}:${String(item?._rowId || index)}`;
  }

  function animateRemoveItem(listKey, item, index) {
    const deleteKey = getItemDeleteKey(listKey, item, index);

    if (deleteItemTimersRef.current[deleteKey]) return;

    setDeletingItemKeys((current) => ({
      ...current,
      [deleteKey]: true,
    }));

    deleteItemTimersRef.current[deleteKey] = window.setTimeout(() => {
      removeItem(listKey, index);
      setDeletingItemKeys((current) => {
        const next = { ...current };
        delete next[deleteKey];
        return next;
      });
      delete deleteItemTimersRef.current[deleteKey];
    }, 220);
  }

  function removeItem(listKey, index) {
    const current = Array.isArray(ownership[listKey]) ? ownership[listKey] : [];
    if (listKey === "shareholders") {
      const rowId = String(current?.[index]?._rowId || "").trim();
      if (rowId) {
        setLegalArrangements(
          legalArrangements.filter(
            (arrangement) =>
              !(
                String(arrangement?.source || "") === "shareholder" &&
                String(arrangement?.sourceRefRowId || "") === rowId
              )
          )
        );
      }
    }
    setList(
      listKey,
      current.filter((_, i) => i !== index)
    );
  }

  const sections = [
    {
      key: "shareholders",
      title: isTrustOrFoundationAccountHolder
        ? "Legal arrangement"
        : "Direct shareholders",
      hint:
        isTrustOrFoundationAccountHolder
          ? "Provide the legal arrangement details below."
          : isAssistantGeneratedReview
          ? "Direct shareholders are the persons or entities that hold shares directly in the account holder."
          : "List each direct shareholder of the entity and capture the ownership position clearly.",
      items: visibleShareholders,
      addLabel: isTrustOrFoundationAccountHolder
        ? isTrustAccountHolder
          ? "Add trust"
          : "Add foundation"
        : "Add shareholder",
      factory: emptyShareholder,
      sectionClassName:
        "kycMobileRepeaterCard kycMobileRepeaterCard--ownershipHero",
      itemClassName: "kycMobileRepeaterItem kycMobileRepeaterItem--shareholder",
      renderHead: () => (
        <div
          className={`kycMobileRepeaterHead kycMobileRepeaterHead--ownership ${
            isTrustOrFoundationAccountHolder
              ? "kycMobileRepeaterHead--legalArrangement"
              : ""
          }`}
        >
          <div
            className={`kycMobileSectionIconWrap kycMobileSectionIconWrap--ownership ${
              isTrustOrFoundationAccountHolder
                ? "kycMobileSectionIconWrap--legalArrangement"
                : ""
            }`}
          >
            <Lottie
              animationData={
                isTrustOrFoundationAccountHolder
                  ? trustBadgeLottieJson
                  : sharesLottieJson
              }
              loop
              autoplay
              className="kycMobileSectionLottie kycMobileSectionLottie--ownership"
            />
          </div>
          <div>
            <div className="kycMobileRepeaterTitle">
              {isTrustOrFoundationAccountHolder
                ? "Legal arrangement"
                : "Direct shareholders"}
            </div>
            <div className="kycMobileRepeaterHint">
              {isTrustOrFoundationAccountHolder
                ? "Complete the trust or foundation details below."
                : isAssistantGeneratedReview
                ? "Direct shareholders are the persons or entities that hold shares directly in the account holder."
                : "Add one card per direct shareholder and indicate the holder type and percentage owned."}
            </div>
            {isTrustAccountHolder && !entityTypeAnsweredEarlier ? (
              <>
                <div className="kycMobileRepeaterHint" style={{ marginTop: 10 }}>
                  Does {entityName} refer to the trust or to the trustee name?
                </div>
                <div className="kycMobileOwnershipQuestionActions">
                  <button
                    type="button"
                    className={`kycMobileOwnershipQuestionBtn ${
                      accountHolderTrustNameKind === "trust" ? "is-selected" : ""
                    }`}
                    onClick={() =>
                      handleAccountHolderTrustNameKindSelection("trust")
                    }
                    disabled={readOnly}
                  >
                    This is trust name
                  </button>
                  <button
                    type="button"
                    className={`kycMobileOwnershipQuestionBtn ${
                      accountHolderTrustNameKind === "trustee" ? "is-selected" : ""
                    }`}
                    onClick={() =>
                      handleAccountHolderTrustNameKindSelection("trustee")
                    }
                    disabled={readOnly}
                  >
                    This is trustee name
                  </button>
                </div>
                {continueAttempted && needsTrustNameKindSelection ? (
                  <div className="kycMobileInlineError">
                    <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                    <span>
                      Select whether the current client name is the trust or the
                      trustee
                    </span>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      ),
      renderItemTop: (item, index) => {
        const holderType = normalizeHolderType(item?.holderType);
        const rowTitle = `Shareholder ${index + 1}`;

        return (
          <div className="kycMobileRepeaterItemTop kycMobileRepeaterItemTop--shareholder">
            <div className="kycMobileRepeaterIndexWrap">
              {!isTrustOrFoundationAccountHolder ? (
                <div className="kycMobileShareholderHeaderRow">
                  {["individual", "entity", "trust", "foundation"].includes(
                    holderType
                  ) ? (
                    <span className={`kycMobileShareholderTitleIcon${holderType === "trust" || holderType === "foundation" ? " kycMobileShareholderTitleIcon--arrangement" : ""}`}>
                      <img
                        src={
                          holderType === "entity"
                            ? iconBusinessTrade
                            : holderType === "trust" ||
                              holderType === "foundation"
                            ? iconCertificate
                            : iconAvatar
                        }
                        alt=""
                        className="kycMobileShareholderTitleIconImage"
                      />
                    </span>
                  ) : null}
                  <div className="kycMobileShareholderHeaderText">
                    <div
                      className={`kycMobileRepeaterIndex${holderType === "trust" || holderType === "foundation" ? " kycMobileRepeaterIndex--arrangement" : ""}`}
                    >
                      {rowTitle}
                    </div>
                    {holderType === "individual" ? (
                      <div className="kycMobileArrangementSubLabel kycMobileArrangementSubLabel--individual">
                        Individual
                      </div>
                    ) : holderType === "entity" ? (
                      <div className="kycMobileArrangementSubLabel kycMobileArrangementSubLabel--entity">
                        Company
                      </div>
                    ) : holderType === "trust" ? (
                      <div className="kycMobileArrangementSubLabel">
                        Trust · Legal arrangement
                      </div>
                    ) : holderType === "foundation" ? (
                      <div className="kycMobileArrangementSubLabel">
                        Foundation · Legal arrangement
                      </div>
                    ) : (
                      <div className="kycMobileShareholderMetaRow">
                        <button
                          type="button"
                          className="kycMobileShareholderMetaChip"
                          onClick={() => openShareholderTypeSheet(index)}
                          disabled={readOnly || isTrustOrFoundationAccountHolder}
                        >
                          {formatHolderTypeLabel(holderType)}
                        </button>
                        {holderType === "trust" &&
                        !isTrustOrFoundationAccountHolder &&
                        !isPicOrPassiveHolding ? (
                          <span className="kycMobileShareholderMetaChip kycMobileShareholderMetaChip--ownership">
                            <img
                              src={iconPercent}
                              alt=""
                              className="kycMobileShareholderMetaChipIcon"
                            />
                            <span>{formatOptionalPercentDisplay(item?.ownershipPercent)}</span>
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              {isTrustOrFoundationAccountHolder ? (
                <div className="kycMobileShareholderMetaRow">
                  <button
                    type="button"
                    className="kycMobileShareholderMetaChip"
                    onClick={() => openShareholderTypeSheet(index)}
                    disabled={readOnly || isTrustOrFoundationAccountHolder}
                  >
                    {formatHolderTypeLabel(holderType)}
                  </button>
                  {holderType === "trust" &&
                  !isTrustOrFoundationAccountHolder &&
                  !isPicOrPassiveHolding ? (
                    <span className="kycMobileShareholderMetaChip kycMobileShareholderMetaChip--ownership">
                      <img
                        src={iconPercent}
                        alt=""
                        className="kycMobileShareholderMetaChipIcon"
                      />
                      <span>{formatOptionalPercentDisplay(item?.ownershipPercent)}</span>
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
    {/* test         */}
            {!readOnly ? (
              <div className="kycMobileRepeaterActions">
                {["trust", "foundation"].includes(holderType) ? (
                  <button
                    type="button"
                    className="kycMobileGhostIconButton"
                    onClick={() =>
                      openTrustEditModal(
                        index,
                        isTrustOrFoundationAccountHolder
                          ? "accountHolderArrangement"
                          : "shareholders"
                      )
                    }
                    aria-label={
                      holderType === "foundation"
                        ? "Edit foundation details"
                        : "Edit trust details"
                    }
                    title={
                      holderType === "foundation"
                        ? "Edit foundation details"
                        : "Edit trust details"
                    }
                  >
                    <i className="bi bi-pencil" aria-hidden="true" />
                  </button>
                ) : null}
                {!isTrustOrFoundationAccountHolder && !isAssistantGeneratedReview ? (
                  <button
                    type="button"
                    className="kycMobileGhostIconButton"
                    onClick={() => animateRemoveItem("shareholders", item, index)}
                    aria-label="Remove shareholder"
                    title="Remove shareholder"
                    disabled={
                      !!deletingItemKeys[
                        getItemDeleteKey("shareholders", item, index)
                      ]
                    }
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      },
      render: (item, index) => (
        <>
          <div className="kycMobileSectionStack">
            {!isTrustOrFoundationAccountHolder &&
            !["individual", "entity", "trust", "foundation"].includes(
              normalizeHolderType(item?.holderType)
            ) ? (
              <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                  <img
                    src={iconPercent}
                    alt=""
                    className="kycMobilePickerIcon"
                  />
                </div>
                <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                  <div className="kycMobilePickerLabel">Ownership</div>
                  <button
                    type="button"
                    className="kycMobileCountryMiniButton"
                    onClick={() => openOwnershipModal(index)}
                    disabled={readOnly || isPicOrPassiveHolding}
                  >
                    <span className="kycMobileCountryMiniButtonValue">
                      <span>
                        {isPicOrPassiveHolding
                          ? "N/A"
                          : formatOptionalPercentDisplay(item?.ownershipPercent)}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            ) : null}

            {normalizeHolderType(item?.holderType) === "individual" ? (
              <>
                <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
                  <div className="kycMobilePickerLabel">Full name</div>
                  {separateNames && !readOnly ? (
                    <MobileIndividualNamePreview key={item?._rowId || index} displayName={item?.name || ""} value={item} onChange={(patch) => isTrustOrFoundationAccountHolder ? updateAccountHolderArrangement(patch) : updateItem("shareholders", index, patch)} />
                  ) : (
                  <input
                    {...MOBILE_TEXT_ENTRY_PROPS}
                    className="form-control"
                    value={item?.name || ""}
                    onChange={(e) =>
                      isTrustOrFoundationAccountHolder
                        ? updateAccountHolderArrangement({
                            name: toTitleCase(e.target.value),
                          })
                        : updateItem("shareholders", index, {
                            name: toTitleCase(e.target.value),
                          })
                    }
                    disabled={readOnly}
                  />
                  )}
                </div>

                {!isTrustOrFoundationAccountHolder && !isPicOrPassiveHolding ? (
                  <div className={`kycMobilePickerCard kycMobilePickerCard--shareholderField${percentMissingOnResult(item) ? " kycMobilePickerCard--missing" : ""}`}>
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img
                        src={iconPercent}
                        alt=""
                        className="kycMobilePickerIcon"
                      />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Ownership</div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onClick={() => openOwnershipModal(index)}
                        disabled={readOnly || isPicOrPassiveHolding}
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          <span>
                            {percentMissingOnResult(item)
                              ? <span className="kycMobilePercentMissing">% missing</span>
                              : formatOptionalPercentDisplay(item?.ownershipPercent)}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                  <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                    <img
                      src={iconPassport}
                      alt=""
                      className="kycMobilePickerIcon"
                    />
                  </div>
                  <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                    <div className="kycMobilePickerLabel">Nationality</div>
                    <button
                      type="button"
                      className="kycMobileCountryMiniButton"
                      onClick={() =>
                        openShareholderCountrySheet(
                          index,
                          "nationality",
                          "Nationality"
                        )
                      }
                      disabled={readOnly}
                    >
                      <span className="kycMobileCountryMiniButtonValue">
                        {item?.nationality ? (
                          <CircleFlag
                            className="kycMobilePickerFlag"
                            countryCode={String(item?.nationality || "").toLowerCase()}
                            height="18"
                          />
                        ) : null}
                        <span>{formatCountryFull(item?.nationality)}</span>
                      </span>
                    </button>
                  </div>
                </div>

                <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                  <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                    <img
                      src={
                        normalizeHolderType(item?.holderType) === "entity"
                          ? iconEarthLocation
                          : iconAddress
                      }
                      alt=""
                      className="kycMobilePickerIcon"
                    />
                  </div>
                  <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                    <div className="kycMobilePickerLabel">Domicile</div>
                    <button
                      type="button"
                      className="kycMobileCountryMiniButton"
                      onClick={() =>
                        openShareholderCountrySheet(
                          index,
                          "countryOfResidence",
                          "Domicile"
                        )
                      }
                      disabled={readOnly}
                    >
                      <span className="kycMobileCountryMiniButtonValue">
                        {item?.countryOfResidence ? (
                          <CircleFlag
                            className="kycMobilePickerFlag"
                            countryCode={String(
                              item?.countryOfResidence || ""
                            ).toLowerCase()}
                            height="18"
                          />
                        ) : null}
                        <span>{formatCountryFull(item?.countryOfResidence)}</span>
                      </span>
                    </button>
                  </div>
                </div>
              </>
            ) : normalizeHolderType(item?.holderType) === "trust" ? (
              <>
                {!isTrustAccountHolder || !needsTrustNameKindSelection ? (
                  <>{renderCompactTrustSummary(item)}</>
                ) : null}
              </>
            ) : normalizeHolderType(item?.holderType) === "foundation" ? (
              <>
                <>{renderCompactFoundationSummary(item)}</>
              </>
            ) : (
              <>
                {normalizeHolderType(item?.holderType) === "entity" ? (
                  <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
                    <div className="kycMobilePickerLabel">Entity name</div>
                    <input
                      {...MOBILE_TEXT_ENTRY_PROPS}
                      className="form-control"
                      value={item?.name || ""}
                      onChange={(e) =>
                        updateItem("shareholders", index, {
                          name: e.target.value,
                        })
                      }
                      disabled={readOnly}
                    />
                  </div>
                ) : (
                  <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <button
                        type="button"
                        className="kycMobilePickerRailIconButton"
                        onClick={() => openShareholderTypeSheet(index)}
                        disabled={readOnly}
                        aria-label="Change shareholder type"
                      >
                        <span className="kycMobilePickerRailIcon">
                          <i className="bi bi-bank" aria-hidden="true" />
                        </span>
                      </button>
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Foundation name</div>
                      <input
                        {...MOBILE_TEXT_ENTRY_PROPS}
                        className="form-control"
                        value={item?.name || ""}
                        onChange={(e) => {
                          updateItem("shareholders", index, {
                            name: e.target.value,
                          });
                          upsertShareholderArrangement(index, {
                            ...(visibleShareholders?.[index] || {}),
                            name: e.target.value,
                          });
                        }}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                )}

                {normalizeHolderType(item?.holderType) === "entity" &&
                !isTrustOrFoundationAccountHolder &&
                !isPicOrPassiveHolding ? (
                  <div className={`kycMobilePickerCard kycMobilePickerCard--shareholderField${percentMissingOnResult(item) ? " kycMobilePickerCard--missing" : ""}`}>
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img
                        src={iconPercent}
                        alt=""
                        className="kycMobilePickerIcon"
                      />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Ownership</div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onClick={() => openOwnershipModal(index)}
                        disabled={readOnly || isPicOrPassiveHolding}
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          <span>
                            {percentMissingOnResult(item)
                              ? <span className="kycMobilePercentMissing">% missing</span>
                              : formatOptionalPercentDisplay(item?.ownershipPercent)}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                  <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                    <img
                      src={iconAddress}
                      alt=""
                      className="kycMobilePickerIcon"
                    />
                  </div>
                  <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                    <div className="kycMobilePickerLabel">
                      {normalizeHolderType(item?.holderType) === "entity"
                        ? "Country of incorporation"
                        : "Domicile"}
                    </div>
                    <button
                      type="button"
                      className="kycMobileCountryMiniButton"
                      onClick={() =>
                        openShareholderCountrySheet(
                          index,
                          "countryOfResidence",
                          normalizeHolderType(item?.holderType) === "entity"
                            ? "Country of incorporation"
                            : "Domicile"
                        )
                      }
                      disabled={readOnly}
                    >
                      <span className="kycMobileCountryMiniButtonValue">
                        {item?.countryOfResidence ? (
                          <CircleFlag
                            className="kycMobilePickerFlag"
                            countryCode={String(
                              item?.countryOfResidence || ""
                            ).toLowerCase()}
                            height="18"
                          />
                        ) : null}
                        <span>{formatCountryFull(item?.countryOfResidence)}</span>
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      ),
    },
    {
      key: "ubos",
      title: "Controlling persons (indirect ownership)",
      hint: isAssistantGeneratedReview
        ? "Controlling persons are the individuals or legal arrangements who ultimately own or control the account holder."
        : "Add individual, trust, or foundation controlling persons when required.",
      items: visibleUbos,
      addLabel: "Add controlling person",
      factory: emptyUbo,
      sectionClassName:
        "kycMobileRepeaterCard kycMobileRepeaterCard--ownershipHero",
      itemClassName: "kycMobileRepeaterItem kycMobileRepeaterItem--shareholder",
      renderHead: () => (
        <div className="kycMobileRepeaterHead kycMobileRepeaterHead--ownership">
          <div className="kycMobileSectionIconWrap kycMobileSectionIconWrap--ownership">
            <Lottie
              animationData={orgtreeLottieJson}
              loop
              autoplay
              className="kycMobileSectionLottie kycMobileSectionLottie--ownership"
            />
          </div>
          <div>
            <div className="kycMobileRepeaterTitle">
              Controlling persons{" "}
              <span className="kycMobileRepeaterTitleHint">
                (indirect ownership)
              </span>
            </div>
            <div className="kycMobileRepeaterHint">
              {isAssistantGeneratedReview
                ? "Controlling persons are the individuals or legal arrangements who ultimately own or control the account holder."
                : "Add individual, trust, or foundation controlling persons when required."}
            </div>
          </div>
        </div>
      ),
      renderItemTop: (item, index) => {
        const holderType = normalizeHolderType(item?.holderType);
        return (
          <div className="kycMobileRepeaterItemTop kycMobileRepeaterItemTop--shareholder">
            <div className="kycMobileRepeaterHeaderBlock">
              <div className="kycMobileCardTopBadge">
                {getRelatedEntityLabel(item?.relatedShareholderRowId)}
              </div>

              <div className="kycMobileRepeaterHeaderRow">
                <div className="kycMobileRepeaterIndexWrap">
                  {holderType === "individual" ? (
                    <div className="kycMobileShareholderHeaderRow">
                      <span className="kycMobileShareholderTitleIcon">
                        <img
                          src={iconAvatar}
                          alt=""
                          className="kycMobileShareholderTitleIconImage"
                        />
                      </span>
                      <div className="kycMobileShareholderHeaderText">
                        <div className="kycMobileRepeaterIndex">
                          Beneficial owner {index + 1}
                        </div>
                        <div className="kycMobileArrangementSubLabel kycMobileArrangementSubLabel--individual">
                          Individual
                        </div>
                      </div>
                    </div>
                  ) : holderType === "trust" || holderType === "foundation" ? (
                    <div className="kycMobileShareholderHeaderRow">
                      <span className="kycMobileShareholderTitleIcon kycMobileShareholderTitleIcon--arrangement">
                        <img
                          src={iconCertificate}
                          alt=""
                          className="kycMobileShareholderTitleIconImage"
                        />
                      </span>
                      <div className="kycMobileShareholderHeaderText">
                        <div className="kycMobileRepeaterIndex kycMobileRepeaterIndex--arrangement">
                          Beneficial owner {index + 1}
                        </div>
                        <div className="kycMobileArrangementSubLabel">
                          {holderType === "foundation" ? "Foundation" : "Trust"} · Legal arrangement
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="kycMobileRepeaterIndex">
                        Beneficial owner {index + 1}
                      </div>
                      <div className="kycMobileShareholderMetaRow">
                        <button
                          type="button"
                          className="kycMobileShareholderMetaChip"
                          onClick={() => openShareholderTypeSheet(index, "ubos")}
                          disabled={readOnly}
                        >
                          {formatHolderTypeLabel(holderType)}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                {!readOnly ? (
                  <div className="kycMobileRepeaterActions">
                    {holderType !== "individual" ? (
                      <button
                        type="button"
                        className="kycMobileGhostIconButton"
                        onClick={() => openTrustEditModal(index, "ubos")}
                        aria-label={
                          holderType === "foundation"
                            ? "Edit foundation details"
                            : "Edit trust details"
                        }
                        title={
                          holderType === "foundation"
                            ? "Edit foundation details"
                            : "Edit trust details"
                        }
                      >
                        <i className="bi bi-pencil" aria-hidden="true" />
                      </button>
                    ) : null}
                    {!isAssistantGeneratedReview ? (
                      <button
                        type="button"
                        className="kycMobileGhostIconButton"
                        onClick={() => animateRemoveItem("ubos", item, index)}
                        aria-label="Remove controlling person"
                        title="Remove controlling person"
                        disabled={
                          !!deletingItemKeys[getItemDeleteKey("ubos", item, index)]
                        }
                      >
                        <i className="bi bi-trash3" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        );
      },
      render: (item, index) => (
        <>
          {normalizeHolderType(item?.holderType) === "individual" ? (
            <>
              <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
                <div className="kycMobilePickerLabel">Full name</div>
                {separateNames && !readOnly ? (
                  <MobileIndividualNamePreview key={item?._rowId || index} displayName={item?.name || ""} value={item} onChange={(patch) => updateItem("ubos", index, patch)} />
                ) : (
                <input
                  {...MOBILE_TEXT_ENTRY_PROPS}
                  className="form-control"
                  value={item?.name || ""}
                  onChange={(e) =>
                    updateItem("ubos", index, { name: toTitleCase(e.target.value) })
                  }
                  disabled={readOnly}
                />
                )}
              </div>

              <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                  <img src={iconPassport} alt="" className="kycMobilePickerIcon" />
                </div>
                <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                  <div className="kycMobilePickerLabel">Nationality</div>
                  <button
                    type="button"
                    className="kycMobileCountryMiniButton"
                    onClick={() =>
                      openShareholderCountrySheet(
                        index,
                        "nationality",
                        "Nationality",
                        "",
                        "",
                        "ubos"
                      )
                    }
                    disabled={readOnly}
                  >
                    <span className="kycMobileCountryMiniButtonValue">
                      {item?.nationality ? (
                        <CircleFlag
                          className="kycMobilePickerFlag"
                          countryCode={String(item?.nationality || "").toLowerCase()}
                          height="18"
                        />
                      ) : null}
                      <span>{formatCountryFull(item?.nationality)}</span>
                    </span>
                  </button>
                </div>
              </div>

              <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                  <img src={iconAddress} alt="" className="kycMobilePickerIcon" />
                </div>
                <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                  <div className="kycMobilePickerLabel">Domicile</div>
                  <button
                    type="button"
                    className="kycMobileCountryMiniButton"
                    onClick={() =>
                      openShareholderCountrySheet(
                        index,
                        "countryOfResidence",
                        "Domicile",
                        "",
                        "",
                        "ubos"
                      )
                    }
                    disabled={readOnly}
                  >
                    <span className="kycMobileCountryMiniButtonValue">
                      {item?.countryOfResidence ? (
                        <CircleFlag
                          className="kycMobilePickerFlag"
                          countryCode={String(
                            item?.countryOfResidence || ""
                          ).toLowerCase()}
                          height="18"
                        />
                      ) : null}
                      <span>{formatCountryFull(item?.countryOfResidence)}</span>
                    </span>
                  </button>
                </div>
              </div>
            </>
          ) : normalizeHolderType(item?.holderType) === "entity" ? (
            <>
              <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
                <div className="kycMobilePickerLabel">Company name</div>
                <input
                  {...MOBILE_TEXT_ENTRY_PROPS}
                  className="form-control"
                  value={item?.name || ""}
                  onChange={(e) =>
                    updateItem("ubos", index, { name: capitalizeFirst(e.target.value) })
                  }
                  disabled={readOnly}
                />
              </div>
              <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                  <img src={iconEarthLocation} alt="" className="kycMobilePickerIcon" />
                </div>
                <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                  <div className="kycMobilePickerLabel">Country of incorporation</div>
                  <button
                    type="button"
                    className="kycMobileCountryMiniButton"
                    onClick={() =>
                      openShareholderCountrySheet(
                        index,
                        "countryOfResidence",
                        "Country of incorporation",
                        "",
                        "",
                        "ubos"
                      )
                    }
                    disabled={readOnly}
                  >
                    <span className="kycMobileCountryMiniButtonValue">
                      {item?.countryOfResidence ? (
                        <CircleFlag
                          className="kycMobilePickerFlag"
                          countryCode={String(item?.countryOfResidence || "").toLowerCase()}
                          height="18"
                        />
                      ) : null}
                      <span>{formatCountryFull(item?.countryOfResidence)}</span>
                    </span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {normalizeHolderType(item?.holderType) === "trust"
                ? renderCompactTrustSummary(item, {
                    hideOwnershipPercent: !assistantNodeParentUsesPercent(assistantRoots, item?.treeNodeId, assistantPreliminaryMode),
                    extraContent: (
                      <div
                        className={`kycMobileCompactBasisControl ${
                          readOnly ? "kycMobileCompactBasisControl--readonly" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="kycMobileCompactBasisControlButton"
                          onClick={() => openBasisOfControlModal(index)}
                          disabled={readOnly}
                        >
                          <img
                            src={iconBriefcase}
                            alt=""
                            className="kycMobileCompactBasisControlIcon"
                          />
                          <span className="kycMobileCompactBasisControlLabel">
                            Basis of control
                          </span>
                          <span className="kycMobileCompactBasisControlValue">
                            {item?.basisOfControl || (readOnly ? "-" : "Select")}
                          </span>
                        </button>
                      </div>
                    ),
                  })
                : renderCompactFoundationSummary(item, {
                    hideOwnershipPercent: !assistantNodeParentUsesPercent(assistantRoots, item?.treeNodeId, assistantPreliminaryMode),
                    extraContent: (
                      <div
                        className={`kycMobileCompactBasisControl ${
                          readOnly ? "kycMobileCompactBasisControl--readonly" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="kycMobileCompactBasisControlButton"
                          onClick={() => openBasisOfControlModal(index)}
                          disabled={readOnly}
                        >
                          <img
                            src={iconBriefcase}
                            alt=""
                            className="kycMobileCompactBasisControlIcon"
                          />
                          <span className="kycMobileCompactBasisControlLabel">
                            Basis of control
                          </span>
                          <span className="kycMobileCompactBasisControlValue">
                            {item?.basisOfControl || (readOnly ? "-" : "Select")}
                          </span>
                        </button>
                      </div>
                    ),
                  })}
            </>
          )}

          {normalizeHolderType(item?.holderType) === "individual" ? (
            <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
              <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                <img
                  src={iconBriefcase}
                  alt=""
                  className="kycMobilePickerIcon"
                />
              </div>
              <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                <div className="kycMobilePickerLabel">Basis of control</div>
                <button
                  type="button"
                  className="kycMobileCountryMiniButton"
                  onClick={() => openBasisOfControlModal(index)}
                  disabled={readOnly}
                >
                  <span className="kycMobileCountryMiniButtonValue">
                    <span>{item?.basisOfControl || "Select basis of control"}</span>
                  </span>
                </button>
              </div>
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: "signatories",
      title: "Signatory details",
      hint: "Add people authorised to sign on behalf of the entity.",
      items: signatories,
      addLabel: "Add signatory",
      factory: emptySignatory,
      sectionClassName:
        "kycMobileRepeaterCard kycMobileRepeaterCard--softBeige",
      itemClassName: "kycMobileRepeaterItem kycMobileRepeaterItem--shareholder",
      renderHead: () => (
        <div className="kycMobileRepeaterHead kycMobileRepeaterHead--ownership">
          <div className="kycMobileSectionIconWrap kycMobileSectionIconWrap--ownership">
            <Lottie
              animationData={signatoryLottieJson}
              loop
              autoplay
              className="kycMobileSectionLottie kycMobileSectionLottie--ownership"
            />
          </div>
          <div>
            <div className="kycMobileRepeaterTitle">Signatory details</div>
            <div className="kycMobileRepeaterHint">
              Add people authorised to sign on behalf of the entity.
            </div>
          </div>
        </div>
      ),
      renderItemTop: (item, index) => (
        <div className="kycMobileRepeaterItemTop kycMobileRepeaterItemTop--shareholder">
          <div className="kycMobileRepeaterIndexWrap">
            <div className="kycMobileShareholderHeaderRow">
              <span className="kycMobileShareholderTitleIcon">
                <img
                  src={iconAvatar}
                  alt=""
                  className="kycMobileShareholderTitleIconImage"
                />
              </span>
              <div className="kycMobileShareholderHeaderText">
                <div className="kycMobileRepeaterIndex kycMobileRepeaterIndex--iconCentered">
                  Signatory {index + 1}
                </div>
              </div>
            </div>
          </div>
          {!readOnly ? (
            <div className="kycMobileRepeaterActions">
              <button
                type="button"
                className="kycMobileGhostIconButton"
                onClick={() => animateRemoveItem("signatories", item, index)}
                aria-label="Remove signatory"
                title="Remove signatory"
                disabled={
                  !!deletingItemKeys[getItemDeleteKey("signatories", item, index)]
                }
              >
                <i className="bi bi-trash3" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
      ),
      render: (item, index) => (
        <>
          <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
            <div className="kycMobilePickerLabel">Full name</div>
            {separateNames && !readOnly ? (
              <MobileIndividualNamePreview key={item?._rowId || index} displayName={item?.name || ""} value={item} onChange={(patch) => updateItem("signatories", index, patch)} />
            ) : (
            <input
              {...MOBILE_TEXT_ENTRY_PROPS}
              className="form-control"
              value={item?.name || ""}
              onChange={(e) =>
                updateItem("signatories", index, { name: toTitleCase(e.target.value) })
              }
              onFocus={() => {
                if (!readOnly) setSignatoryNameFocusIndex(index);
              }}
              disabled={readOnly}
            />
            )}
          </div>

          {/* Empty signatory: known individuals (shareholders, UBOs) to prefill it in one tap. */}
          {!readOnly && !String(item?.name || "").trim() && signatoryQuickAdd.length > 0 ? (
            <div className="kycMobileSuggestChipsWrap">
              <div className="kycMobileSuggestChipsHeader">
                <div className="kycMobileSuggestChipsHeaderAction">
                  <i className="bi bi-hand-index-thumb-fill" aria-hidden="true" />
                  <span>Tap a name to prefill details from known individuals</span>
                </div>
              </div>
              <div className="kycMobileSuggestChips">
                {signatoryQuickAdd.map((person) => (
                  <button
                    key={signatoryPersonKey(person)}
                    type="button"
                    className="kycMobileSuggestChip"
                    onClick={() => updateItem("signatories", index, knownSignatoryDetails(person))}
                  >
                    {person.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
            <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
              <img
                src={iconPassport}
                alt=""
                className="kycMobilePickerIcon"
              />
            </div>
            <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
              <div className="kycMobilePickerLabel">Nationality</div>
              <button
                type="button"
                className="kycMobileCountryMiniButton"
                onClick={() =>
                  openShareholderCountrySheet(
                    index,
                    "nationality",
                    "Nationality",
                    "",
                    "signatories"
                  )
                }
                disabled={readOnly}
              >
                <span className="kycMobileCountryMiniButtonValue">
                  {item?.nationality ? (
                    <CircleFlag
                      className="kycMobilePickerFlag"
                      countryCode={String(item?.nationality || "").toLowerCase()}
                      height="18"
                    />
                  ) : null}
                  <span>{formatCountryFull(item?.nationality)}</span>
                </span>
              </button>
            </div>
          </div>

          <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
            <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
              <img
                src={iconAddress}
                alt=""
                className="kycMobilePickerIcon"
              />
            </div>
            <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
              <div className="kycMobilePickerLabel">Domicile</div>
              <button
                type="button"
                className="kycMobileCountryMiniButton"
                onClick={() =>
                  openShareholderCountrySheet(
                    index,
                    "countryOfResidence",
                    "Domicile",
                    "",
                    "signatories"
                  )
                }
                disabled={readOnly}
              >
                <span className="kycMobileCountryMiniButtonValue">
                  {item?.countryOfResidence ? (
                    <CircleFlag
                      className="kycMobilePickerFlag"
                      countryCode={String(
                        item?.countryOfResidence || ""
                      ).toLowerCase()}
                      height="18"
                    />
                  ) : null}
                  <span>{formatCountryFull(item?.countryOfResidence)}</span>
                </span>
              </button>
            </div>
          </div>

          <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
            <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
              <img
                src={iconBriefcase}
                alt=""
                className="kycMobilePickerIcon"
              />
            </div>
            <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
              <div className="kycMobilePickerLabel">Capacity (optional)</div>
              <button
                type="button"
                className="kycMobileCountryMiniButton"
                onClick={() => openSignatoryCapacityModal(index)}
                disabled={readOnly}
              >
                <span className="kycMobileCountryMiniButtonValue">
                  <span>{item?.capacity || "Select capacity"}</span>
                </span>
              </button>
            </div>
          </div>
        </>
      ),
    },
  ];
  const visibleSections = sections.filter((section) => {
    if (section.key === "shareholders") return !isQuotedCompany;
    if (section.key === "ubos") return shouldShowControllingPersons;
    return true;
  });
  const ownershipSections = visibleSections.filter((s) => s.key !== "signatories");
  const signatorySections = visibleSections.filter((s) => s.key === "signatories");

  const otherShareholdersTotal = (shareholders || []).reduce((sum, sh, idx) => {
    if (idx === activeOwnershipIndex) return sum;
    return sum + toPercentNumber(sh?.ownershipPercent);
  }, 0);
  const remainingOwnership = Math.max(
    0,
    Math.round((100 - otherShareholdersTotal) * 100) / 100
  );
  const assistantDirectShareholderMaxApplies =
    activeOwnershipTarget.mode === "assistantCurrentOwnership" &&
    assistantCurrentDirectShareholderIndex >= 0;
  const assistantIndirectShareholderMaxApplies =
    activeOwnershipTarget.mode === "assistantCurrentOwnership" &&
    assistantCurrentNodeIndexInParent >= 0 &&
    !assistantCurrentParentIsPic;
  const maxOwnershipAllowed = assistantDirectShareholderMaxApplies
    ? assistantRemainingDirectOwnership
    : assistantIndirectShareholderMaxApplies
    ? assistantRemainingIndirectOwnership
    : remainingOwnership;
  const remainingOwnershipValue = maxOwnershipAllowed.toFixed(2);
  const firstDefaultValue =
    remainingOwnershipValue === "50.00" || remainingOwnershipValue === "25.00"
      ? "10.00"
      : "25.00";
  const secondDefaultValue =
    remainingOwnershipValue === "50.00"
      ? "25.00"
      : remainingOwnershipValue === "25.00"
      ? "15.00"
      : "50.00";
  const ownershipQuickActions = [
    {
      value: firstDefaultValue,
      label: formatQuickPercentLabel(firstDefaultValue),
      draft: firstDefaultValue === "100.00" ? "10000" : sanitizePercent(firstDefaultValue),
      cursorIndex:
        firstDefaultValue === "50.00"
          ? 2
          : firstDefaultValue === "25.00"
          ? 2
          : firstDefaultValue === "100.00"
          ? null
          : null,
    },
    {
      value: secondDefaultValue,
      label: formatQuickPercentLabel(secondDefaultValue),
      draft:
        secondDefaultValue === "100.00"
          ? "10000"
          : sanitizePercent(secondDefaultValue),
      cursorIndex:
        secondDefaultValue === "50.00"
          ? 2
          : secondDefaultValue === "25.00"
          ? 2
          : secondDefaultValue === "100.00"
          ? null
          : null,
    },
    {
      value: remainingOwnershipValue,
      label: formatQuickPercentLabel(remainingOwnershipValue),
      draft:
        maxOwnershipAllowed >= 100
          ? "10000"
          : String(Math.round(maxOwnershipAllowed * 100)).padStart(4, "0"),
      cursorIndex:
        remainingOwnershipValue === "50.00"
          ? 2
          : remainingOwnershipValue === "25.00"
          ? 2
          : remainingOwnershipValue === "100.00"
          ? null
          : null,
    },
  ];
  const ownershipKeypad = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    ".",
    "0",
    "clear",
  ];
  const directCountKeypad = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "clear",
    "0",
    "backspace",
  ];
  const ownershipDisplayMeta =
    activeOwnershipTarget.mode === "assistantDirectCount"
      ? getCountDisplayMeta(ownershipDraft, ownershipCursorIndex)
      : getPercentDisplayMeta(
          ownershipDraft,
          ownershipCursorIndex,
          ownershipWholeGroupActiveRef.current
        );

  function renderDirectTrustEditor(item, index, listKey = "shareholders") {
    const targetListKey = listKey;
    const sortedTrustParties = getTrustPartiesSorted(item);
    const hasProtector = sortedTrustParties.some(
      (party) => String(party?.role || "") === "Protector"
    );
    const isLockedAccountHolderTrustName =
      targetListKey === "accountHolderArrangement" &&
      accountHolderTrustNameKind === "trust";
    const isLockedAccountHolderTrusteeName =
      targetListKey === "accountHolderArrangement" &&
      accountHolderTrustNameKind === "trustee";
    const isLockedAssistantNodeName = targetListKey === "assistantCurrentNode";

    return (
      <>
        <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
          <div className="kycMobilePickerLabel">Trust name</div>
          <input
            {...MOBILE_TEXT_ENTRY_PROPS}
            className="form-control"
            value={item?.name || ""}
            onChange={(e) =>
              targetListKey === "accountHolderArrangement"
                ? updateHolderAt(targetListKey, index, {
                    name: e.target.value,
                  })
                : updateHolderAt(targetListKey, index, {
                    name: e.target.value,
                  })
            }
            disabled={readOnly || isLockedAccountHolderTrustName || isLockedAssistantNodeName}
          />
        </div>

        {sortedTrustParties.map((party) => {
          const role = String(party?.role || "");
          const trusteeKind = String(party?.trusteeKind || "")
            .trim()
            .toLowerCase();
          const beneficiaryIndex =
            role === "Beneficiary"
              ? Math.max(
                  1,
                  sortedTrustParties
                    .slice(
                      0,
                      Math.max(
                        0,
                        sortedTrustParties.findIndex(
                          (candidate) =>
                            String(candidate?._rowId || "") ===
                            String(party?._rowId || "")
                        ) + 1
                      )
                    )
                    .filter(
                      (candidate) =>
                        String(candidate?.role || "") === "Beneficiary"
                    ).length
                )
              : 0;
          const roleLabel =
            role === "Beneficiary" ? `Beneficiary ${beneficiaryIndex}` : role;
          const canDelete = role === "Beneficiary" || role === "Protector";
          const trusteeNameValue = String(party?.name || "").trim();
          const shouldOpenTrusteeKindFromNameField =
            role === "Trustee" && !trusteeNameValue;
          const shouldShowTrusteeTypePlaceholder =
            role === "Trustee" &&
            !["entity", "individual"].includes(trusteeKind) &&
            !trusteeNameValue;
          const isLockedTrusteeName =
            role === "Trustee" && isLockedAccountHolderTrusteeName;
          const roleIcon =
            role === "Trustee"
              ? trusteeKind === "entity"
                ? "bi bi-building"
                : "bi bi-person-badge"
              : role === "Beneficiary"
              ? "bi bi-people"
              : role === "Protector"
              ? "bi bi-shield-check"
              : "bi bi-person";

          return (
            <React.Fragment key={party?._rowId || role}>
              <div
                className={`kycMobileSectionStack kycMobileTrustEditorPartyBlock ${
                  role === "Settlor" ? "kycMobileTrustEditorPartyBlock--first" : ""
                }`}
                ref={(node) => {
                  const refKey = `${targetListKey}:${index}:${role}`;
                  if (node) trustEditorPartyRefs.current[refKey] = node;
                  else delete trustEditorPartyRefs.current[refKey];
                }}
              >
                <div
                  className={`kycMobileRepeaterItemTop kycMobileRepeaterItemTop--shareholder kycMobileTrustEditorPartyTop ${
                    role === "Settlor" || role === "Trustee" || role === "Protector"
                      ? "kycMobileTrustEditorPartyTop--primary"
                      : ""
                  }`}
                >
                  <div className="kycMobileRepeaterIndexWrap">
                    <div className="kycMobileRepeaterIndex">{roleLabel}</div>
                  </div>
                  {!readOnly && canDelete ? (
                    <button
                      type="button"
                      className="kycMobileGhostIconButton"
                      onClick={() =>
                        removeHolderPartyAt(
                          targetListKey,
                          index,
                          "trustParties",
                          party?._rowId
                        )
                      }
                      aria-label={`Remove ${role}`}
                      title={`Remove ${role}`}
                    >
                      <i className="bi bi-trash3" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>

                <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                  <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                    <span
                      className="kycMobilePickerRailIcon"
                      role={role === "Trustee" && !readOnly ? "button" : undefined}
                      tabIndex={role === "Trustee" && !readOnly ? 0 : undefined}
                      onClick={
                        role === "Trustee" && !readOnly
                          ? () =>
                              openTrusteeKindModal(
                                index,
                                party?._rowId,
                                targetListKey
                              )
                          : undefined
                      }
                      onKeyDown={
                        role === "Trustee" && !readOnly
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openTrusteeKindModal(
                                  index,
                                  party?._rowId,
                                  targetListKey
                                );
                              }
                            }
                          : undefined
                      }
                    >
                      {role === "Settlor" ? (
                        <img
                          src={iconSettlor}
                          alt=""
                          className="kycMobilePickerIcon kycMobileTrustPartyNameIcon"
                        />
                      ) : role === "Trustee" ? (
                        <img
                          src={iconHandshake}
                          alt=""
                          className="kycMobilePickerIcon kycMobileTrustPartyNameIcon"
                        />
                      ) : role === "Beneficiary" ? (
                        <img
                          src={iconBeneficiary}
                          alt=""
                          className="kycMobilePickerIcon kycMobileTrustPartyNameIcon"
                        />
                      ) : (
                        <i className={roleIcon} aria-hidden="true" />
                      )}
                    </span>
                  </div>
                  <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                    <div className="kycMobilePickerLabel">
                      {role === "Trustee"
                        ? trusteeKind === "entity"
                          ? "Name of corporate trustee"
                          : "Name of trustee"
                        : "Name"}
                    </div>
                    {separateNames && !readOnly && !isLockedTrusteeName && (role !== "Trustee" || trusteeKind === "individual") ? (
                      <MobileIndividualNamePreview key={party?._rowId} displayName={party?.name || ""} value={party} onChange={(patch) => updateHolderPartyAt(targetListKey, index, "trustParties", party?._rowId, patch)} />
                    ) : (
                    <input
                      {...MOBILE_TEXT_ENTRY_PROPS}
                      className="form-control"
                      value={party?.name || ""}
                      placeholder={
                        shouldShowTrusteeTypePlaceholder
                          ? "Select individual or entity"
                          : ""
                      }
                      ref={(node) => {
                        const refKey = `${index}:${String(party?._rowId || "")}`;
                        if (node) trusteeNameInputRefs.current[refKey] = node;
                        else delete trusteeNameInputRefs.current[refKey];
                      }}
                      onChange={(e) =>
                        updateHolderPartyAt(
                          targetListKey,
                          index,
                          "trustParties",
                          party?._rowId,
                          "name",
                          e.target.value
                        )
                      }
                      onFocus={(e) => {
                        if (!shouldOpenTrusteeKindFromNameField) return;
                        const refKey = `${index}:${String(party?._rowId || "")}`;
                        if (suppressTrusteeNameModalRef.current === refKey) {
                          suppressTrusteeNameModalRef.current = "";
                          return;
                        }
                        e.target.blur();
                        openTrusteeKindModalFromNameField(
                          index,
                          party?._rowId,
                          targetListKey
                        );
                      }}
                      onClick={(e) => {
                        if (!shouldOpenTrusteeKindFromNameField) return;
                        e.preventDefault();
                        openTrusteeKindModalFromNameField(
                          index,
                          party?._rowId,
                          targetListKey
                        );
                      }}
                      disabled={readOnly || isLockedTrusteeName}
                    />
                    )}
                  </div>
                </div>

                {role === "Trustee" && trusteeKind === "entity" ? (
                  <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img
                        src={iconEarthLocation}
                        alt=""
                        className="kycMobilePickerIcon"
                      />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">
                        Country of incorporation
                      </div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onClick={() =>
                            openShareholderCountrySheet(
                              index,
                              "domicile",
                              "Country of incorporation",
                              party?._rowId,
                              "",
                              targetListKey
                            )
                          }
                        disabled={readOnly}
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          {party?.domicile ? (
                            <CircleFlag
                              className="kycMobilePickerFlag"
                              countryCode={String(party?.domicile || "").toLowerCase()}
                              height="18"
                            />
                          ) : null}
                          <span>{formatCountryShort(party?.domicile)}</span>
                        </span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="kycMobileInlineGrid">
                    <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                      <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                        <img
                          src={iconPassport}
                          alt=""
                          className="kycMobilePickerIcon"
                        />
                      </div>
                      <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                        <div className="kycMobilePickerLabel">Nationality</div>
                        <button
                          type="button"
                          className="kycMobileCountryMiniButton"
                          onClick={() =>
                            openShareholderCountrySheet(
                              index,
                              "nationality",
                              "Nationality",
                              party?._rowId,
                              "",
                              targetListKey
                            )
                          }
                          disabled={readOnly}
                        >
                          <span className="kycMobileCountryMiniButtonValue">
                            {party?.nationality ? (
                              <CircleFlag
                                className="kycMobilePickerFlag"
                                countryCode={String(
                                  party?.nationality || ""
                                ).toLowerCase()}
                                height="18"
                              />
                            ) : null}
                            <span>{formatCountryShort(party?.nationality)}</span>
                          </span>
                        </button>
                      </div>
                    </div>

                    <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                      <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                        <img
                          src={iconAddress}
                          alt=""
                          className="kycMobilePickerIcon"
                        />
                      </div>
                      <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                        <div className="kycMobilePickerLabel">Domicile</div>
                        <button
                          type="button"
                          className="kycMobileCountryMiniButton"
                          onClick={() =>
                            openShareholderCountrySheet(
                              index,
                              "domicile",
                              "Domicile",
                              party?._rowId,
                              "",
                              targetListKey
                            )
                          }
                          disabled={readOnly}
                        >
                          <span className="kycMobileCountryMiniButtonValue">
                            {party?.domicile ? (
                              <CircleFlag
                                className="kycMobilePickerFlag"
                                countryCode={String(party?.domicile || "").toLowerCase()}
                                height="18"
                              />
                            ) : null}
                            <span>{formatCountryShort(party?.domicile)}</span>
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {!readOnly ? (
          <div className="kycMobileTrustEditorActionsRow">
            <button
              type="button"
              className={`kycMobileSoftActionButton kycMobileTrustEditorActionButton ${
                hasProtector
                  ? "kycMobileTrustEditorActionButton--full"
                  : "kycMobileTrustEditorActionButton--half"
              }`}
              onClick={() =>
                addHolderPartyAt(targetListKey, index, "trustParties", "Beneficiary")
              }
            >
              <i className="bi bi-plus-lg me-2" aria-hidden="true" />
              Add beneficiary
            </button>

            {!hasProtector ? (
              <button
                type="button"
                className="kycMobileSoftActionButton kycMobileTrustEditorActionButton kycMobileTrustEditorActionButton--half"
                onClick={() => addTrustProtectorAndScroll(targetListKey, index)}
              >
                <i className="bi bi-shield-plus me-2" aria-hidden="true" />
                Add protector
              </button>
            ) : null}
          </div>
        ) : null}
      </>
    );
  }

  function renderDirectFoundationEditor(item, index, listKey = "shareholders") {
    const targetListKey = listKey;
    const isLockedAccountHolderFoundationName =
      targetListKey === "accountHolderArrangement" ||
      targetListKey === "assistantCurrentNode";
    const sortedFoundationParties = getFoundationPartiesSorted(item);
    const hasGuardian = sortedFoundationParties.some(
      (party) => String(party?.role || "") === "Guardian"
    );
    const councilMembers = sortedFoundationParties.filter(
      (party) => String(party?.role || "") === "Council member"
    );

    return (
      <>
        <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
          <div className="kycMobilePickerLabel">Foundation name</div>
          <input
            {...MOBILE_TEXT_ENTRY_PROPS}
            className="form-control"
            value={item?.name || ""}
            onChange={(e) =>
              updateHolderAt(targetListKey, index, { name: e.target.value })
            }
            disabled={readOnly || isLockedAccountHolderFoundationName}
          />
        </div>

        {sortedFoundationParties.map((party) => {
          const role = String(party?.role || "");
          const councilKind = String(party?.councilKind || "")
            .trim()
            .toLowerCase();
          const beneficiaryIndex =
            role === "Beneficiary"
              ? Math.max(
                  1,
                  sortedFoundationParties
                    .slice(
                      0,
                      Math.max(
                        0,
                        sortedFoundationParties.findIndex(
                          (candidate) =>
                            String(candidate?._rowId || "") ===
                            String(party?._rowId || "")
                        ) + 1
                      )
                    )
                    .filter(
                      (candidate) =>
                        String(candidate?.role || "") === "Beneficiary"
                    ).length
                )
              : 0;
          const roleLabel =
            role === "Beneficiary"
              ? `Beneficiary ${beneficiaryIndex}`
              : role === "Council member"
              ? councilMembers.length > 1
                ? `Council member ${
                    councilMembers.findIndex(
                      (candidate) =>
                        String(candidate?._rowId || "") ===
                        String(party?._rowId || "")
                    ) + 1
                  }`
                : "Council member"
              : role;
          const canDelete =
            role === "Council member" ||
            role === "Beneficiary" ||
            role === "Guardian";
          const councilNameValue = String(party?.name || "").trim();
          const shouldOpenCouncilKindFromNameField =
            role === "Council member" && !councilNameValue;
          const shouldShowCouncilTypePlaceholder =
            role === "Council member" &&
            !["entity", "individual"].includes(councilKind) &&
            !councilNameValue;

          return (
            <React.Fragment key={party?._rowId || role}>
              <div
                className={`kycMobileSectionStack kycMobileTrustEditorPartyBlock ${
                  role === "Founder" ? "kycMobileTrustEditorPartyBlock--first" : ""
                }`}
              >
                <div
                  className={`kycMobileRepeaterItemTop kycMobileRepeaterItemTop--shareholder kycMobileTrustEditorPartyTop ${
                    role === "Founder" ||
                    role === "Council member" ||
                    role === "Guardian"
                      ? "kycMobileTrustEditorPartyTop--primary"
                      : ""
                  }`}
                >
                  <div className="kycMobileRepeaterIndexWrap">
                    <div className="kycMobileRepeaterIndex">{roleLabel}</div>
                  </div>
                  {!readOnly && canDelete ? (
                    <button
                      type="button"
                      className="kycMobileGhostIconButton"
                      onClick={() =>
                        removeHolderPartyAt(
                          targetListKey,
                          index,
                          "foundationParties",
                          party?._rowId
                        )
                      }
                      aria-label={`Remove ${role}`}
                      title={`Remove ${role}`}
                    >
                      <i className="bi bi-trash3" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>

                <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                  <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                    {role === "Founder" ? (
                      <img
                        src={iconSettlor}
                        alt=""
                        className="kycMobilePickerIcon kycMobileTrustPartyNameIcon"
                      />
                    ) : role === "Council member" ? (
                      <span
                        className="kycMobilePickerRailIcon"
                        role={!readOnly ? "button" : undefined}
                        tabIndex={!readOnly ? 0 : undefined}
                        onClick={
                          !readOnly
                            ? () =>
                                openTrusteeKindModal(
                                  index,
                                  party?._rowId,
                                  targetListKey,
                                  "foundationParties",
                                  "councilKind",
                                  "Select council member type"
                                )
                            : undefined
                        }
                        onKeyDown={
                          !readOnly
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openTrusteeKindModal(
                                    index,
                                    party?._rowId,
                                    targetListKey,
                                    "foundationParties",
                                    "councilKind",
                                    "Select council member type"
                                  );
                                }
                              }
                            : undefined
                        }
                      >
                        <img
                          src={iconHandshake}
                          alt=""
                          className="kycMobilePickerIcon kycMobileTrustPartyNameIcon"
                        />
                      </span>
                    ) : role === "Beneficiary" ? (
                      <img
                        src={iconBeneficiary}
                        alt=""
                        className="kycMobilePickerIcon kycMobileTrustPartyNameIcon"
                      />
                    ) : (
                      <span className="kycMobilePickerRailIcon">
                        <i className="bi bi-shield-check" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                  <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                    <div className="kycMobilePickerLabel">Name</div>
                    {separateNames && !readOnly && (role !== "Council member" || councilKind === "individual") ? (
                      <MobileIndividualNamePreview key={party?._rowId} displayName={party?.name || ""} value={party} onChange={(patch) => updateHolderPartyAt(targetListKey, index, "foundationParties", party?._rowId, patch)} />
                    ) : (
                    <input
                      {...MOBILE_TEXT_ENTRY_PROPS}
                      className="form-control"
                      value={party?.name || ""}
                      placeholder={
                        shouldShowCouncilTypePlaceholder
                          ? "Select individual or entity"
                          : ""
                      }
                      ref={(node) => {
                        const refKey = `${index}:${String(party?._rowId || "")}`;
                        if (node) trusteeNameInputRefs.current[refKey] = node;
                        else delete trusteeNameInputRefs.current[refKey];
                      }}
                      onChange={(e) =>
                        updateHolderPartyAt(
                          targetListKey,
                          index,
                          "foundationParties",
                          party?._rowId,
                          "name",
                          e.target.value
                        )
                      }
                      onFocus={(e) => {
                        if (!shouldOpenCouncilKindFromNameField) return;
                        const refKey = `${index}:${String(party?._rowId || "")}`;
                        if (suppressTrusteeNameModalRef.current === refKey) {
                          suppressTrusteeNameModalRef.current = "";
                          return;
                        }
                        e.target.blur();
                        openTrusteeKindModalFromNameField(
                          index,
                          party?._rowId,
                          targetListKey,
                          "foundationParties",
                          "councilKind",
                          "Select council member type"
                        );
                      }}
                      onClick={(e) => {
                        if (!shouldOpenCouncilKindFromNameField) return;
                        e.preventDefault();
                        openTrusteeKindModalFromNameField(
                          index,
                          party?._rowId,
                          targetListKey,
                          "foundationParties",
                          "councilKind",
                          "Select council member type"
                        );
                      }}
                      disabled={readOnly}
                    />
                    )}
                  </div>
                </div>

                {role === "Council member" && councilKind === "entity" ? (
                  <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img
                        src={iconEarthLocation}
                        alt=""
                        className="kycMobilePickerIcon"
                      />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">
                        Country of incorporation
                      </div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onClick={() =>
                          openShareholderCountrySheet(
                            index,
                            "domicile",
                            "Country of incorporation",
                            party?._rowId,
                            "foundationParties",
                            targetListKey
                          )
                        }
                        disabled={readOnly}
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          {party?.domicile ? (
                            <CircleFlag
                              className="kycMobilePickerFlag"
                              countryCode={String(party?.domicile || "").toLowerCase()}
                              height="18"
                            />
                          ) : null}
                          <span>{formatCountryShort(party?.domicile)}</span>
                        </span>
                      </button>
                    </div>
                  </div>
                ) : (
                <div className="kycMobileInlineGrid">
                  <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img
                        src={iconPassport}
                        alt=""
                        className="kycMobilePickerIcon"
                      />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Nationality</div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onClick={() =>
                          openShareholderCountrySheet(
                            index,
                            "nationality",
                            "Nationality",
                            party?._rowId,
                            "foundationParties",
                            targetListKey
                          )
                        }
                        disabled={readOnly}
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          {party?.nationality ? (
                            <CircleFlag
                              className="kycMobilePickerFlag"
                              countryCode={String(
                                party?.nationality || ""
                              ).toLowerCase()}
                              height="18"
                            />
                          ) : null}
                          <span>{formatCountryShort(party?.nationality)}</span>
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img
                        src={iconAddress}
                        alt=""
                        className="kycMobilePickerIcon"
                      />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Domicile</div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onClick={() =>
                          openShareholderCountrySheet(
                            index,
                            "domicile",
                            "Domicile",
                            party?._rowId,
                            "foundationParties",
                            targetListKey
                          )
                        }
                        disabled={readOnly}
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          {party?.domicile ? (
                            <CircleFlag
                              className="kycMobilePickerFlag"
                              countryCode={String(
                                party?.domicile || ""
                              ).toLowerCase()}
                              height="18"
                            />
                          ) : null}
                          <span>{formatCountryShort(party?.domicile)}</span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {!readOnly ? (
          <>
            <div className="kycMobileTrustEditorActionsRow">
              {!hasGuardian ? (
                <button
                  type="button"
                  className="kycMobileSoftActionButton kycMobileTrustEditorActionButton kycMobileTrustEditorActionButton--half"
                  onClick={() =>
                    toggleHolderOptionalPartyAt(
                      targetListKey,
                      index,
                      "foundationParties",
                      "Guardian"
                    )
                  }
                >
                  <i className="bi bi-shield-plus me-2" aria-hidden="true" />
                  Add guardian
                </button>
              ) : null}

              <button
                type="button"
                className="kycMobileSoftActionButton kycMobileTrustEditorActionButton kycMobileTrustEditorActionButton--half"
                onClick={() =>
                  addHolderPartyAt(
                    targetListKey,
                    index,
                    "foundationParties",
                    "Council member"
                  )
                }
              >
                <i className="bi bi-plus-lg me-2" aria-hidden="true" />
                Add council member
              </button>

              <button
                type="button"
                className="kycMobileSoftActionButton kycMobileTrustEditorActionButton kycMobileTrustEditorActionButton--full"
                onClick={() =>
                  addHolderPartyAt(
                    targetListKey,
                    index,
                    "foundationParties",
                    "Beneficiary"
                  )
                }
              >
                <i className="bi bi-plus-lg me-2" aria-hidden="true" />
                Add beneficiary
              </button>
            </div>
          </>
        ) : null}
      </>
    );
  }

  // onOpen: the whole summary opens the editor (tapping an empty row is the natural way to fill it in).
  function summaryOpenProps(onOpen) {
    if (!onOpen) return {};
    return {
      role: "button",
      tabIndex: 0,
      "aria-label": "Edit details",
      onClick: (event) => { if (!event.target.closest("button")) onOpen(); },
      onKeyDown: (event) => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onOpen(); }
      },
    };
  }

  function renderCompactTrustSummary(item, options = {}) {
    const extraContent = options?.extraContent ?? null;
    const hideOwnershipPercent = options?.hideOwnershipPercent === true;
    const sortedTrustParties = getTrustPartiesSorted(item);
    const settlor = sortedTrustParties.find(
      (party) => String(party?.role || "") === "Settlor"
    );
    const trustee = sortedTrustParties.find(
      (party) => String(party?.role || "") === "Trustee"
    );
    const protector = sortedTrustParties.find(
      (party) => String(party?.role || "") === "Protector"
    );
    const beneficiaries = sortedTrustParties.filter(
      (party) => String(party?.role || "") === "Beneficiary"
    );
    const trusteeKind = String(trustee?.trusteeKind || "")
      .trim()
      .toLowerCase();
    const showTrusteeNationality = trusteeKind !== "entity";

    const renderCountryMarker = (countryCode, iconSrc, label) =>
      countryCode ? (
        <span
          className="kycMobileTrustSummaryMarkerWrap"
          title={`${label}: ${formatCountryFull(countryCode)}`}
        >
          <img src={iconSrc} alt="" className="kycMobileTrustSummaryIcon" />
          <span className="kycMobileTrustSummaryMarker">
            <CircleFlag
              className="kycMobileTrustSummaryFlag"
              countryCode={String(countryCode || "").toLowerCase()}
              height="14"
            />
            <span className="kycMobileTrustSummaryCode">
              {formatCountryShort(countryCode)}
            </span>
          </span>
        </span>
      ) : null;

    const renderPartyMetaMarkers = (party, opts = {}) => (
      <span className="kycMobileTrustSummaryMeta">
        {opts.showNationality !== false
          ? renderCountryMarker(
              party?.nationality,
              iconPassport,
              "Nationality"
            )
          : null}
        {renderCountryMarker(
          party?.domicile,
          iconAddress,
          opts.domicileLabel || "Domicile"
        )}
      </span>
    );

    return (
      <div className={`kycMobileTrustSummaryCard${options?.onOpen ? " kycMobileTrustSummaryCard--clickable" : ""}`} {...summaryOpenProps(options?.onOpen)}>
        <div className="kycMobileTrustSummaryTopRow">
          <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileFieldCard--trustName">
            <div className="kycMobilePickerLabel">Trust name</div>
            <div className="kycMobileTrustSummaryNameValue">
              {String(item?.name || "").trim() || "\u00A0"}
            </div>
          </div>

          {!hideOwnershipPercent &&
          !isTrustOrFoundationAccountHolder &&
          !isPicOrPassiveHolding ? (
            <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileFieldCard--trustPercent">
              <div className="kycMobileTrustSummaryPercentValue">
                <img
                  src={iconPercent}
                  alt=""
                  className="kycMobileShareholderMetaChipIcon"
                />
                <span>{formatOptionalPercentDisplay(item?.ownershipPercent)}</span>
              </div>
            </div>
          ) : null}
        </div>

        {extraContent}

        <div className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role">
          <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
            <span className="kycMobileTrustSummaryRailLabel">Settlor</span>
          </div>
          <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
            <div className="kycMobileTrustSummaryNameValue">
              {String(settlor?.name || "").trim() || "\u00A0"}
            </div>
            {renderPartyMetaMarkers(settlor)}
          </div>
        </div>

        <div className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role">
          <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
            <span className="kycMobileTrustSummaryRailLabel">Trustee</span>
          </div>
          <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
            <div className="kycMobileTrustSummaryNameValue">
              {String(trustee?.name || "").trim() || "\u00A0"}
            </div>
            {renderPartyMetaMarkers(trustee, {
              showNationality: showTrusteeNationality,
              domicileLabel:
                trusteeKind === "entity"
                  ? "Country of incorporation"
                  : "Domicile",
            })}
          </div>
        </div>

        {protector ? (
          <div className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role">
            <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
              <span className="kycMobileTrustSummaryRailLabel">Protect</span>
            </div>
            <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
              <div className="kycMobileTrustSummaryNameValue">
                {String(protector?.name || "").trim() || "\u00A0"}
              </div>
              {renderPartyMetaMarkers(protector)}
            </div>
          </div>
        ) : null}

        {beneficiaries.length
          ? beneficiaries.map((party, beneficiaryIndex) => (
              <div
                key={party?._rowId || `beneficiary_${beneficiaryIndex}`}
                className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role"
              >
                <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                  <span className="kycMobileTrustSummaryRailLabel">
                    {`Benef ${beneficiaryIndex + 1}`}
                  </span>
                </div>
                <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                  <div className="kycMobileTrustSummaryNameValue">
                    {String(party?.name || "").trim() || "\u00A0"}
                  </div>
                  {renderPartyMetaMarkers(party)}
                </div>
              </div>
            ))
          : null}
      </div>
    );
  }

  function renderCompactFoundationSummary(item, options = {}) {
    const extraContent = options?.extraContent ?? null;
    const hideOwnershipPercent = options?.hideOwnershipPercent === true;
    const sortedFoundationParties = getFoundationPartiesSorted(item);
    const founder = sortedFoundationParties.find(
      (party) => String(party?.role || "") === "Founder"
    );
    const councils = sortedFoundationParties.filter(
      (party) => String(party?.role || "") === "Council member"
    );
    const guardian = sortedFoundationParties.find(
      (party) => String(party?.role || "") === "Guardian"
    );
    const beneficiaries = sortedFoundationParties.filter(
      (party) => String(party?.role || "") === "Beneficiary"
    );
    const renderCountryMarker = (countryCode, iconSrc, label) =>
      countryCode ? (
        <span
          className="kycMobileTrustSummaryMarkerWrap"
          title={`${label}: ${formatCountryFull(countryCode)}`}
        >
          <img src={iconSrc} alt="" className="kycMobileTrustSummaryIcon" />
          <span className="kycMobileTrustSummaryMarker">
            <CircleFlag
              className="kycMobileTrustSummaryFlag"
              countryCode={String(countryCode || "").toLowerCase()}
              height="14"
            />
            <span className="kycMobileTrustSummaryCode">
              {formatCountryShort(countryCode)}
            </span>
          </span>
        </span>
      ) : null;

    const renderPartyMetaMarkers = (party, opts = {}) => (
      <span className="kycMobileTrustSummaryMeta">
        {opts.showNationality !== false
          ? renderCountryMarker(party?.nationality, iconPassport, "Nationality")
          : null}
        {renderCountryMarker(
          party?.domicile,
          iconAddress,
          opts.domicileLabel || "Domicile"
        )}
      </span>
    );

    return (
      <div className={`kycMobileTrustSummaryCard${options?.onOpen ? " kycMobileTrustSummaryCard--clickable" : ""}`} {...summaryOpenProps(options?.onOpen)}>
        <div className="kycMobileTrustSummaryTopRow">
          <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileFieldCard--trustName">
            <div className="kycMobilePickerLabel">Foundation name</div>
            <div className="kycMobileTrustSummaryNameValue">
              {String(item?.name || "").trim() || "\u00A0"}
            </div>
          </div>

          {!hideOwnershipPercent &&
          !isTrustOrFoundationAccountHolder &&
          !isPicOrPassiveHolding ? (
            <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileFieldCard--trustPercent">
              <div className="kycMobileTrustSummaryPercentValue">
                <img
                  src={iconPercent}
                  alt=""
                  className="kycMobileShareholderMetaChipIcon"
                />
                <span>{formatOptionalPercentDisplay(item?.ownershipPercent)}</span>
              </div>
            </div>
          ) : null}
        </div>

        {extraContent}

        <div className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role">
          <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
            <span className="kycMobileTrustSummaryRailLabel">Founder</span>
          </div>
          <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
            <div className="kycMobileTrustSummaryNameValue">
              {String(founder?.name || "").trim() || "\u00A0"}
            </div>
            {renderPartyMetaMarkers(founder)}
          </div>
        </div>

        {councils.length
          ? councils.map((party, councilIndex) => {
              const councilKind = String(party?.councilKind || "")
                .trim()
                .toLowerCase();
              return (
                <div
                  key={party?._rowId || `council_${councilIndex}`}
                  className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role"
                >
                  <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                    <span className="kycMobileTrustSummaryRailLabel">
                      {`Council ${councilIndex + 1}`}
                    </span>
                  </div>
                  <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                    <div className="kycMobileTrustSummaryNameValue">
                      {String(party?.name || "").trim() || "\u00A0"}
                    </div>
                    {renderPartyMetaMarkers(party, {
                      showNationality: councilKind !== "entity",
                      domicileLabel:
                        councilKind === "entity"
                          ? "Country of incorporation"
                          : "Domicile",
                    })}
                  </div>
                </div>
              );
            })
          : null}

        {guardian ? (
          <div className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role">
            <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
              <span className="kycMobileTrustSummaryRailLabel">Guardian</span>
            </div>
            <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
              <div className="kycMobileTrustSummaryNameValue">
                {String(guardian?.name || "").trim() || "\u00A0"}
              </div>
              {renderPartyMetaMarkers(guardian)}
            </div>
          </div>
        ) : null}

        {beneficiaries.length
          ? beneficiaries.map((party, beneficiaryIndex) => (
              <div
                key={party?._rowId || `foundation_beneficiary_${beneficiaryIndex}`}
                className="kycMobileTrustSummaryNameCard kycMobileTrustSummaryNameCard--role"
              >
                <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                  <span className="kycMobileTrustSummaryRailLabel">
                    {`Benef ${beneficiaryIndex + 1}`}
                  </span>
                </div>
                <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                  <div className="kycMobileTrustSummaryNameValue">
                    {String(party?.name || "").trim() || "\u00A0"}
                  </div>
                  {renderPartyMetaMarkers(party)}
                </div>
              </div>
            ))
          : null}
      </div>
    );
  }

  function renderSectionItem(section, item, index) {
    const hideTrustShell =
      section.key === "shareholders" &&
      isTrustAccountHolder &&
      needsTrustNameKindSelection &&
      normalizeHolderType(item?.holderType) === "trust";
    const deleteKey = getItemDeleteKey(section.key, item, index);
    const isDeleting = !!deletingItemKeys[deleteKey];

    if (hideTrustShell) {
      return (
        <React.Fragment key={item?._rowId || `${section.key}_${index}`}>
          {section.render(item || {}, index)}
        </React.Fragment>
      );
    }

    return (
      <motion.div
        key={item?._rowId || `${section.key}_${index}`}
        className={section.itemClassName || "kycMobileRepeaterItem"}
        layout
        initial={false}
        animate={
          isDeleting
            ? {
                opacity: 0,
                y: -10,
                scale: 0.97,
                height: 0,
                marginTop: 0,
                marginBottom: 0,
                paddingTop: 0,
                paddingBottom: 0,
              }
            : {
                opacity: 1,
                y: 0,
                scale: 1,
                height: "auto",
              }
        }
        transition={{
          duration: 0.22,
          ease: "easeOut",
          layout: { duration: 0.28, ease: "easeInOut" },
        }}
        style={{
          overflow: isDeleting ? "hidden" : undefined,
          pointerEvents: isDeleting ? "none" : undefined,
          transformOrigin: "50% 0%",
        }}
      >
        {typeof section.renderItemTop === "function" ? (
          section.renderItemTop(item || {}, index)
        ) : (
          <div className="kycMobileRepeaterItemTop">
            <div className="kycMobileRepeaterIndex">
              {section.title.slice(0, -1)} {index + 1}
            </div>
            {!readOnly ? (
              <button
                type="button"
                className="kycMobileGhostButton"
                onClick={() => animateRemoveItem(section.key, item, index)}
                disabled={isDeleting}
              >
                Remove
              </button>
            ) : null}
          </div>
        )}
        <div className="kycMobileSectionStack">{section.render(item || {}, index)}</div>
      </motion.div>
    );
  }

  function renderAssistantTranscript() {
    const bubbles = [];

    // Chat prepared from the company documents with a company among the shareholders: why its owners are asked.
    const documentCompanies = Array.isArray(mobileAssistantDraft?.documentsCompanyShareholders)
      ? mobileAssistantDraft.documentsCompanyShareholders.filter(Boolean) : [];
    if (documentCompanies.length) {
      bubbles.push({ speaker: "assistant", text: companyShareholdersNotice(documentCompanies, entityName, assistantDirectCount) });
    }

    // The type was answered in the upload-first step (shown above as "Client entity type"): not repeated here.
    if (assistantPreliminaryMode && assistantPreliminaryMode !== "holding" && !entityTypeAnsweredEarlier) {
      const prelimLabel =
        assistantPreliminaryMode === "holdingActive"
          ? "Holding — Active"
          : assistantPreliminaryMode === "holdingPassive"
          ? "Holding — Passive"
          : MOBILE_ASSISTANT_PRELIM_OPTIONS.find(
              (opt) => opt.value === assistantPreliminaryMode
            )?.label || assistantPreliminaryMode;
      bubbles.push({
        speaker: "assistant",
        text: "Preliminary information",
      });
      bubbles.push({
        speaker: "user",
        text: prelimLabel,
      });
    }

    // Prepared from the company documents: the number is in the first message, not asked.
    if (assistantDirectCount && !documentCompanies.length) {
      bubbles.push({
        speaker: "assistant",
        text: `How many direct shareholders does ${entityName} have?`,
      });
      bubbles.push({
        speaker: "user",
        text: assistantDirectCount,
      });
    }

    function walk(nodes, parentEntityName, isDirect, pathPrefix = [], parentUsesPercent = assistantPercentShouldApply(assistantPreliminaryMode)) {
      const nodeList = Array.isArray(nodes) ? nodes : [];
      const total = nodeList.length;
      nodeList.forEach((node, index) => {
        const label = isDirect ? "Direct shareholder" : "Shareholder";
        const title = total > 1
          ? `${parentEntityName} | ${label} ${index + 1} of ${total}`
          : `${parentEntityName} | ${label}`;
        const nodePath = [...pathPrefix, index];
        const summary = formatAssistantNodeSummary(node, parentUsesPercent);
        const isEditingCurrentPath =
          Array.isArray(assistantCurrentPath) &&
          assistantCurrentPath.join(".") === nodePath.join(".");
        if (summary && !isEditingCurrentPath) {
          bubbles.push({ speaker: "assistant", text: title });
          bubbles.push({ speaker: "user", text: summary });
        }
        if (normalizeHolderType(node?.type) === "entity" && !isEditingCurrentPath) {
          const entityCat = String(node?.entityCategory || "");
          if (isTerminalEntityCategory(entityCat)) {
            const entityLabel = capitalizeFirst(String(node?.name || title).trim());
            bubbles.push({
              speaker: "assistant",
              text: `Shareholders are not required because ${entityLabel} is a quoted company.`,
            });
          } else if (String(node?.ownersCount || "").trim()) {
            bubbles.push({
              speaker: "assistant",
              text: `How many shareholders does ${capitalizeFirst(String(node?.name || title).trim())} have?`,
            });
            bubbles.push({
              speaker: "user",
              text: `${node.ownersCount} owner${
                Number.parseInt(String(node.ownersCount), 10) > 1 ? "s" : ""
              }`,
            });
          }
        }
        if (Array.isArray(node?.owners) && node.owners.length) {
          const childParentName = capitalizeFirst(String(node?.name || title).trim());
          walk(node.owners, childParentName, false, nodePath,
            !entityNodeSkipsPercent(String(node?.entityCategory || ""), String(node?.holdingKind || "")));
        }
      });
    }

    walk(assistantRoots, entityName, true, []);

    return (
      <div className="kycMobileAssistantTranscript">
        {bubbles.map((bubble, index) => (
          <div
            key={`assistant_bubble_${index}`}
            className={`kycMobileAssistantBubble kycMobileAssistantBubble--${bubble.speaker}`}
          >
            {ownershipLabel(bubble.text)}
          </div>
        ))}
      </div>
    );
  }

  function renderAssistantCurrentStep() {
    if (
      assistantPreliminaryMode === "trust" ||
      assistantPreliminaryMode === "foundation"
    ) {
      const assistantArrangementType =
        assistantPreliminaryMode === "foundation" ? "Foundation" : "Trust";
      const assistantArrangementItem =
        accountHolderArrangementItem ||
        arrangementToMobileHolder(
          makeAccountHolderArrangement(assistantArrangementType)
        );

      return (
        <div className="kycMobileAssistantPromptCard">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span className="kycMobileShareholderTitleIcon kycMobileShareholderTitleIcon--arrangement" style={{ flexShrink: 0, marginTop: 2 }}>
              <img src={iconCertificate} alt="" className="kycMobileShareholderTitleIconImage" />
            </span>
            <div>
              <div className="kycMobileRepeaterTitle" style={{ margin: 0 }}>Legal arrangement</div>
              {assistantPreliminaryMode === "trust" && !entityTypeAnsweredEarlier ? (
                <div className="kycMobileRepeaterHint" style={{ margin: "4px 0 0" }}>
                  Does {entityName} refer to the trust or to the trustee name?
                </div>
              ) : null}
            </div>
          </div>

          {assistantPreliminaryMode === "trust" && entityTypeAnsweredEarlier ? (
            <div className="kycMobileOwnershipQuestionActions">
              <button type="button" className="kycMobileOwnershipQuestionBtn is-selected" disabled={readOnly}
                onClick={() => handleAccountHolderTrustNameKindSelection(accountHolderTrustNameKind || answeredEntityType.trustKind || "trust")}>
                Complete the trust details
              </button>
            </div>
          ) : assistantPreliminaryMode === "trust" ? (
            <>
              <div className="kycMobileOwnershipQuestionActions">
                <button
                  type="button"
                  className={`kycMobileOwnershipQuestionBtn ${
                    accountHolderTrustNameKind === "trust" ? "is-selected" : ""
                  }`}
                  onClick={() =>
                    handleAccountHolderTrustNameKindSelection("trust")
                  }
                  disabled={readOnly}
                >
                  This is trust name
                </button>
                <button
                  type="button"
                  className={`kycMobileOwnershipQuestionBtn ${
                    accountHolderTrustNameKind === "trustee" ? "is-selected" : ""
                  }`}
                  onClick={() =>
                    handleAccountHolderTrustNameKindSelection("trustee")
                  }
                  disabled={readOnly}
                >
                  This is trustee name
                </button>
              </div>
            </>
          ) : null}

          {assistantArrangementSummaryVisible ? (
            assistantPreliminaryMode === "foundation" ? (
              renderCompactFoundationSummary(assistantArrangementItem, {
                hideOwnershipPercent: true,
                onOpen: readOnly ? null : () => openTrustEditModal(0, "accountHolderArrangement"),
              })
            ) : (
              renderCompactTrustSummary(assistantArrangementItem, {
                hideOwnershipPercent: true,
                onOpen: readOnly ? null : () => openTrustEditModal(0, "accountHolderArrangement"),
              })
            )
          ) : null}

          {assistantPreliminaryMode === "foundation" ||
          assistantArrangementSummaryVisible ? (
            <button
              type="button"
              className="kycMobileAddButton"
              onClick={() => openTrustEditModal(0, "accountHolderArrangement")}
            >
              {assistantPreliminaryMode === "foundation"
                ? assistantArrangementSummaryVisible
                  ? "Edit foundation details"
                  : "Enter foundation details"
                : "Edit trust details"}
            </button>
          ) : null}

          {assistantArrangementReady ? (
            <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
              <div style={{ fontSize: 13, color: "#1e6e42", fontWeight: 600 }}>Details complete</div>
              <div style={{ fontSize: 11, color: "#6a8f78", marginTop: 3 }}>Click Continue below to proceed</div>
            </div>
          ) : null}
        </div>
      );
    }

    if (!assistantPreliminaryMode) {
      // Answered earlier: restored by the effect below, never asked again here.
      if (entityTypeAnsweredEarlier) return null;
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">1. Preliminary information</div>
          <div className="kycMobileRepeaterHint">
            Choose the option that best describes the account holder.
          </div>
          <div className="kycMobileAssistantOptionGrid">
            {MOBILE_ASSISTANT_PRELIM_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`kycMobileAssistantChoice${assistantPrelimPending === opt.value ? " is-selected" : ""}`}
                onClick={() => applyAssistantPreliminaryMode(opt.value)}
              >
                <strong className="kycMobileAssistantChoiceTitle">
                  <span className="kycMobileAssistantChoiceLabelStack">
                    <span className="kycMobileAssistantChoiceTitleText">
                      {ownershipLabel(opt.label.replace(" (non-quoted)", ""))}
                    </span>
                    {opt.label.includes("(non-quoted)") && (
                      <span className="kycMobileAssistantChoiceTitleHint">(non-quoted)</span>
                    )}
                  </span>
                  {opt.value === "noneOfAbove" && (
                    <span className="kycMobileAssistantChoiceBadge">
                      Most common
                    </span>
                  )}
                </strong>
                <span>{opt.hint}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="kycMobileAddButton"
            style={{ marginTop: 16 }}
            onClick={() => applyAssistantPreliminaryMode(assistantPrelimPending)}
          >
            Continue
          </button>
        </div>
      );
    }

    if (assistantPreliminaryMode === "holding") {
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">1. Preliminary information — Holding type</div>
          <div className="kycMobileRepeaterHint">
            Specify whether the holding company is active or passive.
          </div>
          <div className="kycMobileAssistantOptionGrid">
            {MOBILE_ASSISTANT_HOLDING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="kycMobileAssistantChoice"
                onClick={() => applyAssistantPreliminaryMode(opt.value === "active" ? "holdingActive" : "holdingPassive")}
              >
                <strong>{opt.label}</strong>
                <span>{opt.hint}</span>
              </button>
            ))}
          </div>
          {entityTypeAnsweredEarlier ? null : <button
            type="button"
            style={{ marginTop: 12, background: "none", border: "none", color: "#8a9bb0", fontSize: 13, cursor: "pointer", padding: "4px 0" }}
            onClick={() => applyAssistantPreliminaryMode("")}
          >
            ← Back
          </button>}
        </div>
      );
    }

    if (assistantPreliminaryMode === "quoted") {
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">Quoted company</div>
          <div className="kycMobileRepeaterHint">
            As a publicly listed company, shareholder and controlling person details are not required.
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginTop: 24, marginBottom: 8 }}>
            <div style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "#7DAB92",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 16px rgba(30, 158, 87, 0.28)",
            }}>
              <i className="bi bi-check2" style={{ color: "#fff", fontSize: 24, lineHeight: 1 }} aria-hidden="true" />
            </div>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#1e6e42", marginTop: 2, userSelect: "none" }}>Section complete</div>
            <div style={{ fontSize: 12, color: "#8a9bb0", textAlign: "center", maxWidth: 220, userSelect: "none" }}>
              No further action required for this step.
            </div>
          </div>
        </div>
      );
    }

    if (!assistantDirectCount || assistantRoots.length === 0) {
      return (
        <div className="kycMobileAssistantPromptCard">
          {/* "1. Preliminary information" is skipped when the type was answered in the upload-first step (org setting: entity documents at the start): no number then. */}
          <div className="kycMobileRepeaterTitle">{entityTypeAnsweredEarlier ? "Direct shareholders" : "2. Direct shareholders"}</div>
          <div className="kycMobileRepeaterHint">
            {`How many direct shareholders does ${entityName} have?`}
          </div>
          <button
            type="button"
            className="kycMobileCountryMiniButton"
            onClick={openAssistantDirectCountModal}
            disabled={readOnly}
          >
            <span className="kycMobileCountryMiniButtonValue">
              <span>{assistantDirectCount || "Select number"}</span>
            </span>
          </button>
        </div>
      );
    }

    if (!assistantCurrentNode) {
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">Ownership Assistant Completed</div>
          <div className="kycMobileRepeaterHint">
            All ownership details have been captured. We now have enough
            information to generate the ownership structure.
          </div>
          <button
            type="button"
            className="kycMobileAddButton kycMobileAddButton--assistantContinue kycMobileAddButton--assistantProceed"
            onClick={() => applyAssistantOutcome()}
          >
            Proceed to Ownership Summary
          </button>
        </div>
      );
    }

    const depth = Math.max(0, assistantCurrentPath.length - 1);
    const assistantParentNode =
      depth > 0
        ? getAssistantNodeAtPath(
            assistantRoots,
            assistantCurrentPath.slice(0, assistantCurrentPath.length - 1)
          )
        : null;
    const assistantParentEntityName = String(
      assistantParentNode?.name || `Entity ${assistantCurrentPath[assistantCurrentPath.length - 2] + 1}`
    ).trim().replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
    const assistantSiblingCount =
      depth === 0
        ? assistantDirectCountNumber || assistantRoots.length || 1
        : Array.isArray(assistantParentNode?.owners)
        ? assistantParentNode.owners.length || 1
        : 1;
    const assistantCurrentIndex = assistantCurrentPath[assistantCurrentPath.length - 1] + 1;
    const ownerLabel = depth === 0 ? "Direct shareholder" : "Shareholder";
    const ownerEntityName = depth === 0 ? entityName : assistantParentEntityName;
    const titlePrefixDisplay =
      assistantSiblingCount > 1
        ? `${ownerEntityName} | ${ownerLabel} ${assistantCurrentIndex} of ${assistantSiblingCount}`
        : `${ownerEntityName} | ${ownerLabel}`;

    if (!String(assistantCurrentNode?.type || "").trim()) {
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">{titlePrefixDisplay}</div>
          <div className="kycMobileRepeaterHint">
            Select type of shareholder :
          </div>
          <div className="kycMobileAssistantOptionGrid">
            {[
              { value: "individual", label: "Individual", icon: iconAvatar },
              { value: "entity", label: "Entity", icon: iconBusinessTrade },
              {
                value: "legalArrangement",
                label: "Legal arrangement",
                hint: "Trust or foundation",
                icon: iconCertificate,
              },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="kycMobileAssistantChoice kycMobileAssistantChoice--shareholderType"
                onClick={() => applyAssistantNodeType(opt.value)}
              >
                <img
                  src={opt.icon}
                  alt=""
                  aria-hidden="true"
                  className="kycMobileAssistantChoicePlainIcon"
                />
                <span className="kycMobileAssistantChoiceCopy">
                  <strong>{opt.label}</strong>
                  {opt.hint ? <span>{opt.hint}</span> : null}
                </span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (assistantCurrentNodeType === "legalArrangement") {
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">{titlePrefixDisplay}</div>
          <div className="kycMobileRepeaterHint">
            Select type of legal arrangement.
          </div>
          <div className="kycMobileAssistantOptionGrid">
            {[
              { value: "trust", label: "Trust" },
              { value: "foundation", label: "Foundation" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="kycMobileAssistantChoice"
                onClick={() => applyAssistantNodeType(opt.value)}
              >
                <strong>{opt.label}</strong>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (
      assistantCurrentNodeType === "entity" &&
      !String(assistantCurrentNode?.entityCategory || "").trim()
    ) {
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">{titlePrefixDisplay}</div>
          <div className="kycMobileRepeaterHint">
            {/* Named already (company documents): the question names it. */}
            {String(assistantCurrentNode?.name || "").trim()
              ? <>What type of company is <strong>{String(assistantCurrentNode.name).trim()}</strong>?</>
              : "What type of entity is this?"}
          </div>
          <div className="kycMobileAssistantOptionGrid">
            {MOBILE_ASSISTANT_ENTITY_CATEGORY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="kycMobileAssistantChoice"
                onClick={() => applyAssistantEntityCategory(opt.value)}
              >
                <strong className="kycMobileAssistantChoiceTitle">
                  <span className="kycMobileAssistantChoiceLabelStack">
                    <span className="kycMobileAssistantChoiceTitleText">
                      {ownershipLabel(opt.label.replace(" (non-quoted)", ""))}
                    </span>
                    {opt.label.includes("(non-quoted)") && (
                      <span className="kycMobileAssistantChoiceTitleHint">(non-quoted)</span>
                    )}
                  </span>
                </strong>
                <span>{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (
      assistantCurrentNodeType === "entity" &&
      String(assistantCurrentNode?.entityCategory || "") === "holding" &&
      !String(assistantCurrentNode?.holdingKind || "").trim()
    ) {
      return (
        <div className="kycMobileAssistantPromptCard">
          <div className="kycMobileRepeaterTitle">{titlePrefixDisplay}</div>
          <div className="kycMobileRepeaterHint">
            Is this an active or passive holding?
          </div>
          <div className="kycMobileAssistantOptionGrid">
            {MOBILE_ASSISTANT_HOLDING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="kycMobileAssistantChoice"
                onClick={() => applyAssistantHoldingKind(opt.value)}
              >
                <strong>{opt.label}</strong>
                <span>{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    const needsName = !String(assistantCurrentNode?.name || "").trim();
    const needsNationality =
      assistantCurrentNodeType === "individual" &&
      !String(assistantCurrentNode?.nationality || "").trim();
    const needsResidence =
      (assistantCurrentNodeType === "individual" ||
        assistantCurrentNodeType === "entity") &&
      !String(assistantCurrentNode?.countryOfResidence || "").trim();
    const isSingleDirectShareholder =
      assistantCurrentPath.length === 1 && assistantDirectCountNumber === 1;
    const isAutoLockedLastDirectShareholderPct =
      Array.isArray(assistantCurrentPath) &&
      assistantCurrentPath.length === 1 &&
      assistantDirectCountNumber > 1 &&
      assistantCurrentDirectShareholderIndex === assistantDirectCountNumber - 1;
    const isSingleIndirectShareholder =
      assistantCurrentPath.length > 1 &&
      assistantCurrentParentOwnersCount === 1 &&
      !assistantCurrentParentIsPic;
    const isAutoLockedLastIndirectShareholderPct =
      assistantCurrentPath.length > 1 &&
      assistantCurrentParentOwnersCount > 1 &&
      assistantCurrentNodeIndexInParent === assistantCurrentParentOwnersCount - 1 &&
      !assistantCurrentParentIsPic;
    const isSingleShareholder = isSingleDirectShareholder || isSingleIndirectShareholder;
    const isAutoLockedLastShareholderPct =
      isAutoLockedLastDirectShareholderPct || isAutoLockedLastIndirectShareholderPct;
    // For direct shareholders (depth 1): based on account holder mode
    // For indirect shareholders (depth > 1): based on parent entity category
    const currentNodeShouldShowPercent =
      assistantCurrentPath.length <= 1
        ? assistantPercentShouldApply(assistantPreliminaryMode)
        : !assistantCurrentParentIsPic;
    const needsPct =
      !isSingleShareholder &&
      currentNodeShouldShowPercent &&
      !String(assistantCurrentNode?.ownershipPercent || "").trim();
    const needsOwnersCount =
      assistantCurrentNodeType === "entity" &&
      !isTerminalEntityCategory(String(assistantCurrentNode?.entityCategory || "")) &&
      (!Number.parseInt(String(assistantCurrentNode?.ownersCount || ""), 10) ||
        Number.parseInt(String(assistantCurrentNode?.ownersCount || ""), 10) < 1);

    return (
      <div className="kycMobileAssistantPromptCard">
        <div className="kycMobileRepeaterTitle">{titlePrefixDisplay}</div>
        <div className="kycMobileRepeaterHint">
          Complete this shareholder's details
        </div>

        <div className="kycMobileSectionStack">
          <div
            className={`kycMobileFieldCard kycMobileFieldCard--shareholderPlain${
              assistantCurrentNodeType === "individual" || assistantCurrentNodeType === "entity"
                ? " kycMobileAssistantOwnerFieldWithIcon"
                : ""
            }`}
          >
            {assistantCurrentNodeType === "individual" ? (
              <img
                src={iconAvatar}
                alt=""
                aria-hidden="true"
                className="kycMobileAssistantOwnerFieldIcon"
              />
            ) : assistantCurrentNodeType === "entity" ? (
              <img
                src={iconBusinessTrade}
                alt=""
                aria-hidden="true"
                className="kycMobileAssistantOwnerFieldIcon"
              />
            ) : null}
            <div className="kycMobileAssistantOwnerFieldContent">
              <div className="kycMobilePickerLabel">
                {assistantCurrentNodeType === "entity"
                  ? "Entity name"
                  : assistantCurrentNodeType === "trust"
                  ? "Trust name"
                  : assistantCurrentNodeType === "foundation"
                  ? "Foundation name"
                  : "Full name"}
              </div>
              {separateNames && assistantCurrentNodeType === "individual" && !readOnly ? (
                <MobileIndividualNamePreview key={assistantCurrentNode?._rowId || assistantCurrentPath.join(".")} displayName={assistantCurrentNode?.name || ""} value={assistantCurrentNode} onChange={updateAssistantCurrentNode} />
              ) : (
              <input
                {...MOBILE_TEXT_ENTRY_PROPS}
                className="form-control"
                value={assistantCurrentNode?.name || ""}
                onChange={(e) => updateAssistantCurrentNode({
                  name: assistantCurrentNodeType === "individual"
                    ? toTitleCase(e.target.value)
                    : e.target.value,
                })}
                placeholder="Enter name"
              />
              )}
            </div>
          </div>

          {assistantCurrentNodeType === "individual" ? (
            <>
              <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileAssistantOwnerFieldWithIcon">
                <img
                  src={iconPassport}
                  alt=""
                  aria-hidden="true"
                  className="kycMobileAssistantOwnerFieldIcon"
                />
                <div className="kycMobileAssistantOwnerFieldContent">
                  <div className="kycMobilePickerLabel">Nationality</div>
                  <button
                    type="button"
                    className="kycMobileCountryMiniButton"
                    onClick={() =>
                      openAssistantCurrentCountrySheet("nationality", "Nationality")
                    }
                  >
                    <span className="kycMobileCountryMiniButtonValue">
                      {assistantCurrentNode?.nationality ? (
                        <CircleFlag
                          className="kycMobilePickerFlag"
                          countryCode={String(
                            assistantCurrentNode?.nationality || ""
                          ).toLowerCase()}
                          height="18"
                        />
                      ) : null}
                      <span>{formatCountryFull(assistantCurrentNode?.nationality)}</span>
                    </span>
                  </button>
                </div>
              </div>
              <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileAssistantOwnerFieldWithIcon">
                <img
                  src={iconEarthLocation}
                  alt=""
                  aria-hidden="true"
                  className="kycMobileAssistantOwnerFieldIcon"
                />
                <div className="kycMobileAssistantOwnerFieldContent">
                  <div className="kycMobilePickerLabel">Domicile</div>
                  <button
                    type="button"
                    className="kycMobileCountryMiniButton"
                    onClick={() =>
                      openAssistantCurrentCountrySheet("countryOfResidence", "Domicile")
                    }
                  >
                    <span className="kycMobileCountryMiniButtonValue">
                      {assistantCurrentNode?.countryOfResidence ? (
                        <CircleFlag
                          className="kycMobilePickerFlag"
                          countryCode={String(
                            assistantCurrentNode?.countryOfResidence || ""
                          ).toLowerCase()}
                          height="18"
                        />
                      ) : null}
                      <span>
                        {formatCountryFull(assistantCurrentNode?.countryOfResidence)}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </>
          ) : null}

          {assistantCurrentNodeType === "entity" ? (
            <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileAssistantOwnerFieldWithIcon">
              <img
                src={iconEarthLocation}
                alt=""
                aria-hidden="true"
                className="kycMobileAssistantOwnerFieldIcon"
              />
              <div className="kycMobileAssistantOwnerFieldContent">
                <div className="kycMobilePickerLabel">
                  Country of incorporation
                </div>
                <button
                  type="button"
                  className="kycMobileCountryMiniButton"
                  onClick={() =>
                    openAssistantCurrentCountrySheet(
                      "countryOfResidence",
                      "Country of incorporation"
                    )
                  }
                >
                  <span className="kycMobileCountryMiniButtonValue">
                    {assistantCurrentNode?.countryOfResidence ? (
                      <CircleFlag
                        className="kycMobilePickerFlag"
                        countryCode={String(
                          assistantCurrentNode?.countryOfResidence || ""
                        ).toLowerCase()}
                        height="18"
                      />
                    ) : null}
                    <span>
                      {formatCountryFull(assistantCurrentNode?.countryOfResidence)}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          ) : null}

          {currentNodeShouldShowPercent ? (
            <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
              <div className="kycMobilePickerLabel">Ownership %</div>
              {isSingleShareholder ? (
                <div className="kycMobileCountryMiniButton">
                  <span className="kycMobileCountryMiniButtonValue">
                    <span>100 %</span>
                  </span>
                </div>
              ) : isAutoLockedLastShareholderPct ? (
                <div className="kycMobileCountryMiniButton">
                  <span className="kycMobileCountryMiniButtonValue">
                    <span>
                      {assistantCurrentNode?.ownershipPercent
                        ? formatPercentDisplay(assistantCurrentNode?.ownershipPercent)
                        : "Select percentage"}
                    </span>
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="kycMobileCountryMiniButton"
                  onClick={openAssistantCurrentOwnershipModal}
                >
                  <span className="kycMobileCountryMiniButtonValue">
                    <span>
                      {assistantCurrentNode?.ownershipPercent
                        ? formatPercentDisplay(assistantCurrentNode?.ownershipPercent)
                        : "Select percentage"}
                    </span>
                  </span>
                </button>
              )}
            </div>
          ) : null}

          {assistantCurrentNodeType === "entity" &&
          !isTerminalEntityCategory(String(assistantCurrentNode?.entityCategory || "")) ? (
            <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain kycMobileAssistantOwnerFieldWithIcon">
              <img
                src={iconAllocation}
                alt=""
                aria-hidden="true"
                className="kycMobileAssistantOwnerFieldIcon"
              />
              <div className="kycMobileAssistantOwnerFieldContent">
                <div className="kycMobilePickerLabel">
                  {`How many shareholders does ${String(
                    assistantCurrentNode?.name || "this entity"
                  ).trim()} have?`}
                </div>
                <button
                  type="button"
                  className="kycMobileCountryMiniButton"
                  onClick={openAssistantCurrentOwnersCountModal}
                  disabled={readOnly}
                >
                  <span className="kycMobileCountryMiniButtonValue">
                    <span>{assistantCurrentNode?.ownersCount || "Select number"}</span>
                  </span>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {(assistantCurrentNodeType === "trust" || assistantCurrentNodeType === "foundation") && !needsName ? (
          <button
            type="button"
            className="kycMobileAddButton kycMobileAddButton--assistantContinue"
            onClick={() => openTrustEditModal(0, "assistantCurrentNode")}
          >
            {`Set up ${assistantCurrentNodeType === "foundation" ? "foundation" : "trust"} parties`}
          </button>
        ) : null}

        <button
          type="button"
          className={`kycMobileAddButton kycMobileAddButton--assistantContinue ${
            needsName ||
            needsNationality ||
            needsResidence ||
            needsPct ||
            (assistantCurrentNodeType === "entity" && needsOwnersCount) ||
            ((assistantCurrentNodeType === "trust" || assistantCurrentNodeType === "foundation"))
              ? "kycMobileAddButton--assistantContinueBlocked"
              : ""
          }`}
          style={{ display: (assistantCurrentNodeType === "trust" || assistantCurrentNodeType === "foundation") ? "none" : undefined }}
          onClick={
            assistantCurrentNodeType === "entity" &&
            !isTerminalEntityCategory(String(assistantCurrentNode?.entityCategory || ""))
              ? confirmAssistantCurrentEntityOwnersCount
              : advanceAssistantCurrentNode
          }
          disabled={
            needsName ||
            needsNationality ||
            needsResidence ||
            needsPct ||
            (assistantCurrentNodeType === "entity" && needsOwnersCount)
          }
        >
          Continue
        </button>
      </div>
    );
  }

  const assistantSelectionView = (
    <div className="kycMobileSectionStack">
      <div className="kycMobileAssistantEntryCard">
        <div className="kycMobileRepeaterHead">
          <div>
            <div className="kycMobileAssistantEntryTopline">
              <div className="kycMobileRepeaterTitle kycMobileAssistantEntryTitleAuto kycMobileAssistantTitleTone">
                Ownership{" "}
                <span className="kycMobileAssistantEntryTitleKeepTogether">
                  auto assistant
                </span>
              </div>
              <span className="kycMobileAssistantEntryBadge">Recommended</span>
            </div>
            <div className="kycMobileRepeaterHint kycMobileAssistantEntryLead">
              We will build the ownership chain step by step, then generate the
              ownership summary for review.
            </div>
          </div>
        </div>

        {renderAssistantTranscript()}
        {renderAssistantCurrentStep()}
      </div>

      {/* Manual entry card — hidden, keep for later reactivation if needed
      <div className="kycMobileAssistantEntryCard">
        <div className="kycMobileRepeaterTitle kycMobileAssistantEntryTitleManual kycMobileAssistantTitleTone">
          Manual entry
        </div>
        <div className="kycMobileAssistantEntryCopy">
          <div className="kycMobileRepeaterHint">
            Enter manually the details about the direct shareholders, UBOs, and
            controlling persons. This option is best for users already familiar
            with compliance concepts such as controlling persons and UBOs
            identification.
          </div>
        </div>
        <div className="kycMobileAssistantEntryGrid">
          <button
            type="button"
            className="kycMobileAssistantEntryButton kycMobileAssistantEntryButton--simple"
            onClick={startManualEntryMode}
          >
            <strong>Switch to manual entry</strong>
          </button>
        </div>
      </div>
      */}
    </div>
  );

  const assistantBuilderView = (
    <div className="kycMobileSectionStack">
      <div className="kycMobileAssistantEntryCard">
        <div className="kycMobileRepeaterHead kycMobileAssistantBuilderHead">
          <div className="kycMobileAssistantBuilderTitleWrap">
            <div className="kycMobileRepeaterTitle kycMobileAssistantTitleTone">Ownership auto assistant</div>
          </div>
          <button
            type="button"
            className="kycMobileGhostIconButton"
            onClick={() => {
              setMobileEntryMode("");
              resetMobileAssistantDraft("");
            }}
            aria-label="Close assistant"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {renderAssistantTranscript()}
        {renderAssistantCurrentStep()}
      </div>
    </div>
  );

  const manualOwnershipView = (
    <>
      <div className="kycMobileSectionStack">
        {shouldShowLegacyManualPreliminary ? (
          <div className="kycMobileRepeaterCard kycMobileRepeaterCard--softBeige">
            <div className="kycMobileRepeaterHead">
              <div>
                <div className="kycMobileRepeaterTitle">Preliminary Information</div>
                <div className="kycMobileRepeaterHint">
                  Tick only if applicable. If none apply, leave all boxes unchecked.
                </div>
              </div>
            </div>

            <div className="kycMobileSectionStack">
              <label className="kycMobilePrelimOption">
                <input
                  type="checkbox"
                  checked={isPicOrPassiveHolding}
                  disabled={readOnly}
                  onChange={(e) =>
                    setPreliminaryFlag("pctNotMeaningful", e.target.checked)
                  }
                />
                <span>
                  <strong>{entityName}</strong> is a personal investment company
                  (PIC) or a passive holding vehicle primarily used to hold
                  investments and assets
                </span>
              </label>

              <label className="kycMobilePrelimOption">
                <input
                  type="checkbox"
                  checked={isQuotedCompany}
                  disabled={readOnly}
                  onChange={(e) =>
                    setPreliminaryFlag("quotedCompany", e.target.checked)
                  }
                />
                <span>
                  <strong>{entityName}</strong> is a company quoted on the stock
                  exchange
                </span>
              </label>

              <label className="kycMobilePrelimOption">
                <input
                  type="checkbox"
                  checked={isTrustAccountHolder}
                  disabled={readOnly}
                  onChange={(e) =>
                    setPreliminaryFlag("accountHolderTrust", e.target.checked)
                  }
                />
                <span>
                  <strong>{entityName}</strong> is a trust
                </span>
              </label>

              <label className="kycMobilePrelimOption">
                <input
                  type="checkbox"
                  checked={isFoundationAccountHolder}
                  disabled={readOnly}
                  onChange={(e) =>
                    setPreliminaryFlag("accountHolderFoundation", e.target.checked)
                  }
                />
                <span>
                  <strong>{entityName}</strong> is a foundation
                </span>
              </label>

              <label className="kycMobilePrelimOption">
                <input
                  type="checkbox"
                  checked={isNoneOfAbove}
                  disabled={readOnly}
                  onChange={(e) => setPreliminaryNoneOfAbove(e.target.checked)}
                />
                <span>
                  <strong>None of the above</strong>
                </span>
              </label>
            </div>

            {continueAttempted && !isPreliminaryValidated ? (
              <div className="kycMobileInlineError kycMobileInlineError--centered">
                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                <span>Select one preliminary option first</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {isPreliminaryValidated && isQuotedCompany ? (
          <div className="kycMobileFieldCard kycMobileFieldCard--soft">
            <div className="kycMobileRepeaterTitle">Quoted company</div>
            <div className="kycMobileRepeaterHint">
              As a publicly listed company, shareholder and controlling person details are not required.
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginTop: 24, marginBottom: 8 }}>
              <div style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: "#7DAB92",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px rgba(79, 133, 106, 0.22)",
              }}>
                <i className="bi bi-check2" style={{ color: "#fff", fontSize: 24, lineHeight: 1 }} aria-hidden="true" />
              </div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1e6e42", marginTop: 2, userSelect: "none" }}>Section complete</div>
              <div style={{ fontSize: 12, color: "#8a9bb0", textAlign: "center", maxWidth: 220, userSelect: "none" }}>
                No further action required for this step.
              </div>
            </div>
          </div>
        ) : null}

        {hasAssistantSnapshot ? (
          <div className="kycMobileRepeaterCard">
            <div className="kycMobileRepeaterHead">
              <div>
                <div className="kycMobileRepeaterTitle">Ownership structure diagram</div>
                <div className="kycMobileRepeaterHint">
                  Snapshot generated by the mobile assistant.
                </div>
              </div>
            </div>
            <div className="kycMobileAssistantTreeWrap">
              <OwnershipTreeUaeReadOnly
                snapshot={liveOwnershipSnapshot || ownership.assistantTree}
                invalidPercentageEdges={invalidPercentageEdges}
                edgeLabelBg="#edeff1"
              />
            </div>
            {liveOwnershipPercentWarning ? (
              <div className="kycMobileAssistantTreeWarning">
                <img src={warningIcon} alt="" aria-hidden="true" />
                <span>{liveOwnershipPercentWarning}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {isPreliminaryValidated && view !== "signatories"
          ? ownershipSections.map((section) => (
              <div
                key={section.key}
                className={section.sectionClassName || "kycMobileRepeaterCard"}
              >
                {typeof section.renderHead === "function" ? (
                  section.renderHead()
                ) : (
                  <div className="kycMobileRepeaterHead">
                    <div>
                      <div className="kycMobileRepeaterTitle">{section.title}</div>
                      <div className="kycMobileRepeaterHint">{section.hint}</div>
                    </div>
                  </div>
                )}

                {(section.key === "shareholders" || section.key === "ubos") &&
                (section.items || []).length > 0 &&
                !(
                  isTrustAccountHolder &&
                  needsTrustNameKindSelection &&
                  section.key === "shareholders" &&
                  (section.items || []).some(
                    (item) => normalizeHolderType(item?.holderType) === "trust"
                  )
                ) ? (
                  <div
                    className="kycMobileOwnershipCarouselWrap"
                    ref={section.key === "shareholders" ? shareholderSwiperRef : null}
                  >
                    <Swiper
                      key={`${section.key}-${carouselRenderNonce[section.key] || 0}`}
                      modules={[EffectCoverflow, Pagination]}
                      onSwiper={(swiper) => {
                        if (section.key === "shareholders") {
                          shareholderSwiperRef.current = swiper;
                        } else if (section.key === "ubos") {
                          uboSwiperRef.current = swiper;
                        }
                        window.requestAnimationFrame(() => {
                          tryFocusPendingCarouselTarget();
                        });
                      }}
                      onSlideChange={() => {
                        if (!pendingCarouselTarget) return;
                        window.requestAnimationFrame(() => {
                          tryFocusPendingCarouselTarget();
                        });
                      }}
                      initialSlide={carouselInitialIndex[section.key] || 0}
                      observer
                      observeParents
                      watchSlidesProgress
                      effect="coverflow"
                      grabCursor
                      centeredSlides
                      slidesPerView="auto"
                      pagination={{ clickable: true }}
                      coverflowEffect={{
                        rotate: 20,
                        stretch: 50,
                        depth: 350,
                        modifier: 1,
                        slideShadows: true,
                      }}
                      className="kycMobileOwnershipSwiper"
                    >
                      {(section.items || []).map((item, index) => (
                        <SwiperSlide
                          key={item?._rowId || `${section.key}_${index}`}
                          className="kycMobileOwnershipSwiperSlide"
                        >
                          {renderSectionItem(section, item, index)}
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                ) : (
                  (section.items || []).map((item, index) =>
                    renderSectionItem(section, item, index)
                  )
                )}

                {!readOnly &&
                !isAssistantGeneratedReview &&
                !(
                  section.key === "shareholders" &&
                  (isQuotedCompany || isTrustOrFoundationAccountHolder)
                ) ? (
                  <button
                    type="button"
                    className="kycMobileAddButton"
                    onClick={() =>
                      section.key === "ubos"
                        ? openAddTypeSheet("ubos")
                        : openAddTypeSheet("shareholders")
                    }
                    disabled={
                      section.key === "ubos"
                        ? !eligibleControllingEntities.length
                        : false
                    }
                  >
                    +{" "}
                    {section.key === "ubos"
                      ? "Add controlling person"
                      : "Add direct shareholder"}
                  </button>
                ) : null}

              </div>
            ))
          : null}

        {isPreliminaryValidated && view === "signatories" ? (
          <>
            {signatorySections.map((section) => (
              <div
                key={section.key}
                className={section.sectionClassName || "kycMobileRepeaterCard"}
              >
                {typeof section.renderHead === "function" ? (
                  section.renderHead()
                ) : (
                  <div className="kycMobileRepeaterHead">
                    <div>
                      <div className="kycMobileRepeaterTitle">{section.title}</div>
                      <div className="kycMobileRepeaterHint">{section.hint}</div>
                    </div>
                  </div>
                )}

                {(section.items || []).length > 0 ? (
                  <div className="kycMobileOwnershipCarouselWrap">
                    <Swiper
                      key={`${section.key}-${carouselRenderNonce[section.key] || 0}`}
                      modules={[EffectCoverflow, Pagination]}
                      onSwiper={(swiper) => {
                        signatorySwiperRef.current = swiper;
                        window.requestAnimationFrame(() => {
                          tryFocusPendingCarouselTarget();
                        });
                      }}
                      onSlideChange={() => {
                        if (!pendingCarouselTarget) return;
                        window.requestAnimationFrame(() => {
                          tryFocusPendingCarouselTarget();
                        });
                      }}
                      initialSlide={carouselInitialIndex[section.key] || 0}
                      observer
                      observeParents
                      watchSlidesProgress
                      effect="coverflow"
                      grabCursor
                      centeredSlides
                      slidesPerView="auto"
                      pagination={{ clickable: true }}
                      coverflowEffect={{
                        rotate: 20,
                        stretch: 50,
                        depth: 350,
                        modifier: 1,
                        slideShadows: true,
                      }}
                      className="kycMobileOwnershipSwiper"
                    >
                      {(section.items || []).map((item, index) => (
                        <SwiperSlide
                          key={item?._rowId || `${section.key}_${index}`}
                          className="kycMobileOwnershipSwiperSlide"
                        >
                          {renderSectionItem(section, item, index)}
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                ) : (
                  (section.items || []).map((item, index) =>
                    renderSectionItem(section, item, index)
                  )
                )}

                {!readOnly && !isAssistantGeneratedReview ? (
                  <button
                    type="button"
                    className="kycMobileAddButton kycMobileAddButton--signatories"
                    onClick={() => addItem(section.key, section.factory)}
                  >
                    + {section.addLabel}
                  </button>
                ) : null}
              </div>
            ))}
          </>
        ) : null}
      </div>
    </>
  );

  const signatoryStepContent = (() => {
    const sigSection = signatorySections[0];
    if (!sigSection) return null;
    return (
      <div className="kycMobileSectionStack">
        <div className={sigSection.sectionClassName || "kycMobileRepeaterCard"}>
          {typeof sigSection.renderHead === "function" ? (
            sigSection.renderHead()
          ) : (
            <div className="kycMobileRepeaterHead">
              <div>
                <div className="kycMobileRepeaterTitle">{sigSection.title}</div>
                <div className="kycMobileRepeaterHint">{sigSection.hint}</div>
              </div>
            </div>
          )}

          {(sigSection.items || []).length > 0 ? (
            <div className="kycMobileOwnershipCarouselWrap">
              <Swiper
                key={`signatories-${carouselRenderNonce.signatories || 0}`}
                modules={[EffectCoverflow, Pagination]}
                onSwiper={(swiper) => {
                  signatorySwiperRef.current = swiper;
                  window.requestAnimationFrame(() => tryFocusPendingCarouselTarget());
                }}
                onSlideChange={() => {
                  if (!pendingCarouselTarget) return;
                  window.requestAnimationFrame(() => tryFocusPendingCarouselTarget());
                }}
                initialSlide={carouselInitialIndex.signatories || 0}
                observer
                observeParents
                watchSlidesProgress
                effect="coverflow"
                grabCursor
                centeredSlides
                slidesPerView="auto"
                pagination={{ clickable: true }}
                coverflowEffect={{ rotate: 20, stretch: 50, depth: 350, modifier: 1, slideShadows: true }}
                className="kycMobileOwnershipSwiper"
              >
                {(sigSection.items || []).map((item, index) => (
                  <SwiperSlide
                    key={item?._rowId || `signatories_${index}`}
                    className="kycMobileOwnershipSwiperSlide"
                  >
                    {renderSectionItem(sigSection, item, index)}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : null}

          {!readOnly && !hasEmptySignatory && signatoryQuickAdd.length > 0 ? (
            <div className="kycMobileSignatoryQuickAdd">
              <div className="kycMobileSignatoryQuickAddLabel">Add as signatory</div>
              <div className="kycMobileSuggestChips">
                {signatoryQuickAdd.map((person) => (
                  <button
                    key={signatoryPersonKey(person)}
                    type="button"
                    className="kycMobileSuggestChip kycMobileSignatoryQuickAddChip"
                    aria-label={`Add ${person.name} as signatory`}
                    onClick={() => addItem("signatories", () => ({ ...emptySignatory(), ...knownSignatoryDetails(person) }))}
                  >
                    <i className="bi bi-plus-lg" aria-hidden="true" />
                    {person.name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {!readOnly ? (
            <button
              type="button"
              className="kycMobileAddButton kycMobileAddButton--signatories"
              onClick={() => addItem("signatories", emptySignatory)}
            >
              + Add another signatory
            </button>
          ) : null}
        </div>

        {/* Signatory name suggestion overlay — portal + AnimatePresence for blur-zoom */}
        {createPortal(
          <AnimatePresence>
            {signatoryNameFocusIndex !== null && !readOnly ? (
              <>
                <motion.div
                  className="kycMobileSuggestBackdrop"
                  onPointerDown={() => setSignatoryNameFocusIndex(null)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                />
                <motion.div
                  className="kycMobileSuggestPanel kycMobileRepeaterItem--shareholder"
                  initial={{ opacity: 0, scale: 0.94, filter: "blur(8px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.94, filter: "blur(8px)" }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  style={{ transformOrigin: "50% 0%" }}
                >
              {/* Header — mirrors renderItemTop */}
              <div className="kycMobileRepeaterItemTop kycMobileRepeaterItemTop--shareholder">
                <div className="kycMobileRepeaterIndexWrap">
                  <div className="kycMobileShareholderHeaderRow">
                    <span className="kycMobileShareholderTitleIcon">
                      <img
                        src={iconAvatar}
                        alt=""
                        className="kycMobileShareholderTitleIconImage"
                      />
                    </span>
                    <div className="kycMobileShareholderHeaderText">
                      <div className="kycMobileRepeaterIndex kycMobileRepeaterIndex--iconCentered">
                        Signatory {signatoryNameFocusIndex + 1}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="kycMobileRepeaterActions">
                  <button
                    type="button"
                    className="kycMobileGhostIconButton"
                    disabled
                    aria-hidden="true"
                    tabIndex={-1}
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                </div>
              </div>

              {/* Fields — wrapped in kycMobileSectionStack to match card gap (12px) */}
              <div className="kycMobileSectionStack">
                {/* Full name */}
                <div className="kycMobileFieldCard kycMobileFieldCard--shareholderPlain">
                  <div className="kycMobilePickerLabel">Full name</div>
                  {separateNames && !readOnly ? (
                    <MobileIndividualNamePreview key={signatories[signatoryNameFocusIndex]?._rowId || signatoryNameFocusIndex} displayName={signatories[signatoryNameFocusIndex]?.name || ""} value={signatories[signatoryNameFocusIndex]} onChange={(patch) => updateItem("signatories", signatoryNameFocusIndex, patch)} />
                  ) : (
                  <input
                    {...MOBILE_TEXT_ENTRY_PROPS}
                    className="form-control"
                    autoFocus
                    placeholder="Full name"
                    value={signatories[signatoryNameFocusIndex]?.name || ""}
                    onChange={(e) =>
                      updateItem("signatories", signatoryNameFocusIndex, {
                        name: toTitleCase(e.target.value),
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setSignatoryNameFocusIndex(null);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setSignatoryNameFocusIndex(null), 160);
                    }}
                  />
                  )}
                </div>

                {/* Name suggestion chips — replaces country fields while typing */}
                {signatoryNameMatches.length > 0 ? (
                  <div className="kycMobileSuggestChipsWrap">
                    <div className="kycMobileSuggestChipsHeader">
                      <div className="kycMobileSuggestChipsHeaderAction">
                        <i className="bi bi-hand-index-thumb-fill" aria-hidden="true" />
                        <span>Tap a name to prefill details from known individuals</span>
                      </div>
                    </div>
                    <div className="kycMobileSuggestChips">
                      {signatoryNameMatches.map((person) => (
                        <button
                          key={person.name}
                          type="button"
                          className="kycMobileSuggestChip"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            updateItem("signatories", signatoryNameFocusIndex, {
                              ...nameFields(person),
                              name: person.name,
                              nationality: person.nationality,
                              countryOfResidence: person.countryOfResidence,
                            });
                            setSignatoryNameFocusIndex(null);
                          }}
                        >
                          {person.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Nationality */}
                    <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img src={iconPassport} alt="" className="kycMobilePickerIcon" />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Nationality</div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() =>
                          openShareholderCountrySheet(
                            signatoryNameFocusIndex,
                            "nationality",
                            "Nationality",
                            "",
                            "signatories"
                          )
                        }
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          {signatories[signatoryNameFocusIndex]?.nationality ? (
                            <CircleFlag
                              className="kycMobilePickerFlag"
                              countryCode={String(
                                signatories[signatoryNameFocusIndex]?.nationality || ""
                              ).toLowerCase()}
                              height="18"
                            />
                          ) : null}
                          <span>
                            {formatCountryFull(
                              signatories[signatoryNameFocusIndex]?.nationality
                            )}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Domicile */}
                  <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img src={iconAddress} alt="" className="kycMobilePickerIcon" />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Domicile</div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() =>
                          openShareholderCountrySheet(
                            signatoryNameFocusIndex,
                            "countryOfResidence",
                            "Domicile",
                            "",
                            "signatories"
                          )
                        }
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          {signatories[signatoryNameFocusIndex]?.countryOfResidence ? (
                            <CircleFlag
                              className="kycMobilePickerFlag"
                              countryCode={String(
                                signatories[signatoryNameFocusIndex]?.countryOfResidence || ""
                              ).toLowerCase()}
                              height="18"
                            />
                          ) : null}
                          <span>
                            {formatCountryFull(
                              signatories[signatoryNameFocusIndex]?.countryOfResidence
                            )}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Capacity (optional) */}
                  <div className="kycMobilePickerCard kycMobilePickerCard--shareholderField">
                    <div className="kycMobilePickerRail kycMobilePickerRail--shareholder">
                      <img src={iconBriefcase} alt="" className="kycMobilePickerIcon" />
                    </div>
                    <div className="kycMobilePickerContent kycMobilePickerContent--shareholder">
                      <div className="kycMobilePickerLabel">Capacity (optional)</div>
                      <button
                        type="button"
                        className="kycMobileCountryMiniButton"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => openSignatoryCapacityModal(signatoryNameFocusIndex)}
                      >
                        <span className="kycMobileCountryMiniButtonValue">
                          <span>
                            {signatories[signatoryNameFocusIndex]?.capacity || "Select capacity"}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </>
              )}
              </div>{/* end kycMobileSectionStack */}
                </motion.div>
              </>
            ) : null}
          </AnimatePresence>
        , document.body)}
      </div>
    );
  })();

  return (
    <>
      {view !== "signatories" && entityTypeAnsweredEarlier && entityTypeComplete(answeredEntityType) ? (
        <div className="kycMobileEntityTypeSummary">
          <span className="kycMobileEntityTypeSummaryLabel">Client entity type</span>
          <KycEntityTypeChoice value={answeredEntityType} onChange={changeAnsweredEntityType} mobile disabled={readOnly} />
        </div>
      ) : null}
      {view === "signatories"
        ? signatoryStepContent
        : !mobileEntryMode
        ? assistantSelectionView
        : mobileEntryMode === "assistant" && !shouldShowAssistantResult
        ? assistantBuilderView
        : (
            <div className="kycMobileSectionStack">
              {mobileEntryMode === "manual" && !readOnly ? (
                <button
                  type="button"
                  className="kycMobileGhostButton"
                  onClick={() => setMobileEntryMode("")}
                >
                  ← Back to entry mode selection
                </button>
              ) : null}
              {mobileEntryMode === "assistant" ? (
                <div className="kycMobileAssistantReviewBar">
                  <div className="kycMobileAssistantReviewCopy">
                    <div className="kycMobileRepeaterTitle kycMobileAssistantReviewTitle">
                      {assistantResultGenerated
                        ? "Please verify the ownership details generated below"
                        : "You are now editing the generated ownership details"}
                    </div>
                    <div className="kycMobileAssistantReviewBody">
                      <div className="kycMobileRepeaterHint kycMobileAssistantReviewHint">
                        {assistantResultGenerated
                          ? "You can keep them as they are, edit them below, or restart the assistant to generate a new ownership structure."
                          : "You have edited the assistant-generated ownership structure. You can continue adjusting it manually below, or rebuild it from the beginning with the assistant."}
                      </div>
                      <div className="kycMobileAssistantReviewActions">
                        <button
                          type="button"
                          className="kycMobileGhostButton kycMobileGhostButton--assistantReset"
                          onClick={restartAssistantFromScratch}
                        >
                          {assistantResultGenerated
                            ? "Restart assistant"
                            : "Start assistant"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {manualOwnershipView}
            </div>
          )}

      {typeof document !== "undefined"
        ? createPortal(
            <AnimatePresence>
              {ownershipModalOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeOwnershipModal}
                  />

                  <motion.div
                    className="kycMobileOwnershipModal"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 18 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileOwnershipModalTop">
                      <div className="kycMobileOwnershipModalTopBar">
                        <div className="kycMobileOwnershipModalTitle">
                          {activeOwnershipTarget.label || "OWNERSHIP %"}
                        </div>
                        <button
                          type="button"
                          className="kycMobileOwnershipModalClose"
                          onClick={closeOwnershipModal}
                          aria-label="Close"
                        >
                          <i className="bi bi-x-lg" aria-hidden="true" />
                        </button>
                      </div>

                      <div className="kycMobileOwnershipModalValue">
                        {activeOwnershipTarget.mode === "assistantDirectCount" || activeOwnershipTarget.mode === "assistantCurrentOwnersCount" ? (
                          <span>{ownershipDraft || "0"}</span>
                        ) : (() => {
                          let numericDigitIndex = -1;
                          const groupedActiveIndexes =
                            ownershipDisplayMeta.activeIndexes || [];
                          const hasGroupedWholeBubble =
                            groupedActiveIndexes.length === 2 &&
                            groupedActiveIndexes[0] === 0 &&
                            groupedActiveIndexes[1] === 1;

                          return ownershipDisplayMeta.fullText
                            .split("")
                            .map((char, idx) => {
                          if (
                            ownershipDisplayMeta.hideLeadingWholeZero &&
                            idx === 0
                          ) {
                            return null;
                          }
                          if (hasGroupedWholeBubble && idx === 1) {
                            return null;
                          }
                          const isNumericChar = idx < ownershipDisplayMeta.numericLength;
                          if (
                            isNumericChar &&
                            char !== "." &&
                            char !== "%"
                          ) {
                            numericDigitIndex += 1;
                          }
                          const isActive =
                            isNumericChar &&
                            char !== "." &&
                            char !== "%" &&
                            ownershipDisplayMeta.activeIndexes?.includes(idx);
                          const displayChar =
                            hasGroupedWholeBubble && idx === 0
                              ? ownershipDisplayMeta.fullText.slice(0, 2)
                              : char;
                          const isDarkDigit =
                            isNumericChar &&
                            char !== "%" &&
                            (ownershipDisplayMeta.activeDigitIndex === -1
                              ? true
                              : numericDigitIndex <=
                                ownershipDisplayMeta.activeDigitIndex);

                          return (
                            <span
                              key={`${char}-${idx}`}
                              className={`kycMobileOwnershipDigit ${
                                isActive ? "is-active" : ""
                              } ${
                                hasGroupedWholeBubble && idx === 0
                                  ? "kycMobileOwnershipDigit--double"
                                  : ""
                              } ${isDarkDigit ? "is-complete" : ""} ${
                                char === "%" ? "kycMobileOwnershipPercentMark" : ""
                              } ${
                                char === "." ? "kycMobileOwnershipDecimalMark" : ""
                              }`}
                            >
                              {displayChar === " " ? "\u00A0" : displayChar}
                            </span>
                          );
                        });
                        })()}
                      </div>
                    </div>

                    <div className="kycMobileOwnershipBottom">
                      {activeOwnershipTarget.mode === "assistantDirectCount" || activeOwnershipTarget.mode === "assistantCurrentOwnersCount" ? null : (
                        <div className="kycMobileOwnershipQuickActions">
                          {ownershipQuickActions.map((val) => (
                          <button
                            key={val.label}
                            type="button"
                            className="kycMobileOwnershipQuickAction"
                            onClick={() => {
                              setOwnershipDraft(
                                assistantDirectShareholderMaxApplies || assistantIndirectShareholderMaxApplies
                                  ? (() => {
                                      const clamped = Math.min(
                                        toPercentNumber(val.draft),
                                        maxOwnershipAllowed
                                      );
                                      return clamped >= 100
                                        ? "10000"
                                        : String(Math.round(clamped * 100)).padStart(4, "0");
                                    })()
                                  : val.draft
                              );
                              setOwnershipCursorIndex(val.cursorIndex);
                              ownershipWholeSeedRef.current = "";
                              ownershipWholeGroupActiveRef.current = false;
                            }}
                          >
                            {val.label}
                          </button>
                          ))}
                        </div>
                      )}

                      <div className="kycMobileOwnershipKeypad">
                        {(activeOwnershipTarget.mode === "assistantDirectCount" || activeOwnershipTarget.mode === "assistantCurrentOwnersCount"
                          ? directCountKeypad
                          : ownershipKeypad
                        ).map((key) => (
                          <button
                            key={key}
                            type="button"
                            className="kycMobileOwnershipKey"
                            onClick={() => handleOwnershipKeypadInput(key)}
                          >
                            {key === "clear" ? (
                              <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                            ) : key === "backspace" ? (
                              <i className="bi bi-backspace" aria-hidden="true" />
                            ) : (
                              key
                            )}
                          </button>
                        ))}
                      </div>

                      <button
                        type="button"
                        className="kycMobileOwnershipValidateButton"
                        onClick={applyOwnershipDraft}
                        disabled={
                          (activeOwnershipTarget.mode === "assistantDirectCount" ||
                            activeOwnershipTarget.mode === "assistantCurrentOwnersCount") &&
                          !(Number.parseInt(String(ownershipDraft || "").replace(/\D/g, ""), 10) >= 1)
                        }
                      >
                        Validate
                      </button>
                    </div>
                  </motion.div>
                </>
              ) : null}

              {trustEditModal.open &&
              trustEditModal.index >= 0 &&
              ((trustEditModal.listKey === "accountHolderArrangement" &&
                accountHolderArrangementItem &&
                ["trust", "foundation"].includes(
                  normalizeHolderType(accountHolderArrangementItem?.holderType)
                )) ||
                (trustEditModal.listKey !== "accountHolderArrangement" &&
                  getListItemByKey(trustEditModal.listKey, trustEditModal.index) &&
                  ["trust", "foundation"].includes(
                    normalizeHolderType(
                      getListItemByKey(trustEditModal.listKey, trustEditModal.index)
                        ?.holderType
                    )
                  ))) ? (
                <>
                  {(() => {
                    const trustEditItem = getListItemByKey(
                      trustEditModal.listKey,
                      trustEditModal.index
                    );
                    const trustEditHolderType = normalizeHolderType(
                      trustEditItem?.holderType
                    );
                    const foundationSaveDisabled =
                      trustEditHolderType === "foundation"
                        ? !isFoundationHolderComplete(trustEditItem)
                        : trustEditHolderType === "trust"
                        ? !isTrustHolderComplete(trustEditItem)
                        : false;

                    return (
                      <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeTrustEditModal}
                  />

                  <motion.div
                    className={`kycMobileTrustEditorModal${trusteeKindModal.open ? " kycMobileTrustEditorModal--obscured" : ""}`}
                    initial={{ opacity: 0, y: 18, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 18, scale: 0.985 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileTrustEditorModalHeader">
                      <div className="kycMobileTrustEditorModalHeaderMain">
                        <div className="kycMobileSectionIconWrap kycMobileSectionIconWrap--ownership kycMobileSectionIconWrap--legalArrangement kycMobileTrustEditorModalHeaderIcon">
                          <Lottie
                            animationData={trustBadgeLottieJson}
                            loop
                            autoplay
                            className="kycMobileSectionLottie kycMobileSectionLottie--ownership kycMobileTrustEditorModalHeaderLottie"
                          />
                        </div>
                        <div className="kycMobileTrustEditorModalHeaderText">
                          <div className="kycMobileTrustEditorModalTitle">
                            {trustEditHolderType === "foundation"
                              ? "Foundation details"
                              : "Trust details"}
                          </div>
                          <div className="kycMobileTrustEditorModalHint">
                            {trustEditHolderType === "foundation"
                              ? "Update the foundation parties below."
                              : "Update the trust parties below."}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="kycMobileTrustEditorModalClose"
                        onClick={closeTrustEditModal}
                        aria-label="Close"
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="kycMobileTrustEditorModalBody">
                      {trustEditHolderType === "foundation"
                        ? renderDirectFoundationEditor(
                            trustEditItem,
                            trustEditModal.index,
                            trustEditModal.listKey
                          )
                        : renderDirectTrustEditor(
                            trustEditItem,
                            trustEditModal.index,
                            trustEditModal.listKey
                          )}
                    </div>

                    <div className="kycMobileTrustEditorModalFooter">
                      <button
                        type="button"
                        className={`kycMobileTrustEditorModalSave ${
                          foundationSaveDisabled
                            ? "kycMobileTrustEditorModalSave--blocked"
                            : ""
                        }`}
                        onClick={() => closeTrustEditModal(true)}
                        disabled={foundationSaveDisabled}
                      >
                        Save
                      </button>
                    </div>
                  </motion.div>
                      </>
                    );
                  })()}
                </>
              ) : null}

              {controllingEntityModalOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeControllingEntityModal}
                  />

                  <motion.div
                    ref={basisOfControlModalRef}
                    className="kycMobileTypeModal"
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.18 }}
                    tabIndex={-1}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        confirmBasisOfControlSelection();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        closeBasisOfControlModal();
                      }
                    }}
                  >
                    <div className="kycMobileTypeModalHeader">
                      <div className="kycMobileTypeModalTitle">
                        {controllingEntityModalMode === "trust"
                          ? "Select related entity for legal arrangement"
                          : "Select related entity"}
                      </div>
                      <button
                        type="button"
                        className="kycMobileTypeModalClose"
                        onClick={closeControllingEntityModal}
                        aria-label="Close"
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="kycMobileTypeModalStack">
                      {eligibleControllingEntities.map((entity) => (
                        <button
                          key={entity.rowId}
                          type="button"
                          className="kycMobileTypeModalOption"
                          onClick={() => addControllingPersonForEntity(entity.rowId)}
                        >
                          <span>{entity.name}</span>
                          <i className="bi bi-chevron-right" aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {typeSheetOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeTypeSheet}
                  />

                  <motion.div
                    className="kycMobileTypeModal"
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileTypeModalHeader">
                      <div className="kycMobileTypeModalTitle">Select type</div>
                      <button
                        type="button"
                        className="kycMobileTypeModalClose"
                        onClick={closeTypeSheet}
                        aria-label="Close"
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="kycMobileTypeModalStack">
                      {[
                        {
                          value: "individual",
                          label: "Individual",
                          icon: iconAvatar,
                        },
                        ...(activeTypeListKey === "ubos"
                          ? []
                          : [
                              {
                                value: "entity",
                                label: "Entity",
                                icon: iconBusinessTrade,
                              },
                            ]),
                        {
                          value: "trust",
                          label: "Trust",
                          icon: iconCertificate,
                        },
                        {
                          value: "foundation",
                          label: "Foundation",
                          icon: iconCertificate,
                        },
                      ].map((opt) => {
                        const currentType =
                          activeTypeIndex == null
                            ? ""
                            : normalizeHolderType(
                                activeTypeListKey === "ubos"
                                  ? ubos?.[activeTypeIndex]?.holderType
                                  : shareholders?.[activeTypeIndex]?.holderType
                              );
                        const isSelected =
                          activeTypeIndex != null && currentType === opt.value;

                        return (
                          <button
                            key={opt.value}
                            type="button"
                          className={`kycMobileTypeModalOption ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => applyShareholderType(opt.value)}
                          >
                            <span className="kycMobileTypeModalOptionMain">
                              <span className="kycMobileTypeModalOptionIcon">
                                <img src={opt.icon} alt="" />
                              </span>
                              <span>{opt.label}</span>
                            </span>
                            {isSelected ? (
                              <i className="bi bi-check2" aria-hidden="true" />
                            ) : (
                              <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {trusteeKindModal.open ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeTrusteeKindModal}
                  />

                  <motion.div
                    className="kycMobileTypeModal"
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileTypeModalHeader">
                      <div className="kycMobileTypeModalTitle">
                        {trusteeKindModal.title || "Select party type"}
                      </div>
                      <button
                        type="button"
                        className="kycMobileTypeModalClose"
                        onClick={closeTrusteeKindModal}
                        aria-label="Close"
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="kycMobileTypeModalStack">
                      {[
                        { value: "entity", label: "Entity" },
                        { value: "individual", label: "Individual" },
                      ].map((opt) => {
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className="kycMobileTypeModalOption"
                            onClick={() => applyTrusteeKind(opt.value)}
                          >
                            <span>{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {signatoryCapacityModal.open ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeSignatoryCapacityModal}
                  />

                  <motion.div
                    className="kycMobileTypeModal"
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileTypeModalHeader">
                      <div className="kycMobileTypeModalTitle">Select capacity</div>
                      <button
                        type="button"
                        className="kycMobileTypeModalClose"
                        onClick={closeSignatoryCapacityModal}
                        aria-label="Close"
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="kycMobileTypeModalStack">
                      {SIGNATORY_CAPACITY_OPTIONS.map((opt) => {
                        const currentCapacity = String(
                          signatories?.[signatoryCapacityModal.index]?.capacity || ""
                        ).trim();
                        const isSelected = currentCapacity === opt.value;

                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`kycMobileTypeModalOption ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => applySignatoryCapacity(opt.value)}
                          >
                            <span>{opt.label}</span>
                            {isSelected ? (
                              <i className="bi bi-check2" aria-hidden="true" />
                            ) : (
                              <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {basisOfControlModal.open ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeBasisOfControlModal}
                  />

                  <motion.div
                    className="kycMobileTypeModal"
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileTypeModalHeader">
                      <div className="kycMobileTypeModalTitle">
                        Select basis of control
                      </div>
                      <button
                        type="button"
                        className="kycMobileTypeModalClose"
                        onClick={closeBasisOfControlModal}
                        aria-label="Close"
                      >
                        <i className="bi bi-x-lg" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="kycMobileTypeModalStack">
                      {BASIS_OF_CONTROL_OPTIONS.map((opt) => {
                        const currentValue = String(
                          ubos?.[basisOfControlModal.index]?.basisOfControl || ""
                        ).trim();
                        const effectiveValue =
                          currentValue || "Indirect ownership";
                        const isSelected = effectiveValue === opt.value;

                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`kycMobileTypeModalOption ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => applyBasisOfControl(opt.value)}
                          >
                            <span>{opt.label}</span>
                            {isSelected ? (
                              <i className="bi bi-check2" aria-hidden="true" />
                            ) : (
                              <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}

              {countrySheetOpen ? (
                <>
                  <motion.div
                    className="kycMobileSheetBackdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeCountrySheet}
                  />

                  <motion.div
                    className="kycMobileCountrySheet"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="kycMobileCountrySheetHeader">
                      <button
                        type="button"
                        className="kycMobileDateSheetBtn kycMobileDateSheetBtn--ghost"
                        onClick={closeCountrySheet}
                      >
                        Cancel
                      </button>
                      <div className="kycMobileDateSheetTitle">
                        {activeCountryField?.title || "Country"}
                      </div>
                      <div className="kycMobileCountrySheetSpacer" />
                    </div>

                    <div className="kycMobileCountrySearchWrap">
                      <i className="bi bi-search" aria-hidden="true" />
                      <input
                        {...MOBILE_TEXT_ENTRY_PROPS}
                        ref={countrySearchRef}
                        autoFocus
                        className="kycMobileCountrySearchInput"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            filteredCountryOptions.length === 1
                          ) {
                            e.preventDefault();
                            applyShareholderCountry(filteredCountryOptions[0].value);
                          }
                        }}
                        placeholder="Search country"
                      />
                    </div>

                    <div className="kycMobileCountryList">
                      {filteredCountryOptions.map((opt) => {
                        const selectedValue = getActiveCountrySelectedValue();
                        const isSelected = opt.value === selectedValue;

                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`kycMobileCountryOption ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => applyShareholderCountry(opt.value)}
                          >
                            <span className="kycMobileCountryOptionLeft">
                              <CircleFlag
                                className="kycMobilePickerFlag"
                                countryCode={String(opt.value || "").toLowerCase()}
                                height="18"
                              />
                              <span>{opt.label}</span>
                            </span>
                            {isSelected ? (
                              <i className="bi bi-check2" aria-hidden="true" />
                            ) : (
                              <i
                                className="bi bi-chevron-right"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>,
            document.body
          )
        : null}
    </>
  );
}
